import { createSelector } from 'reselect';
import { has, round } from 'lodash';
import orderBy from 'lodash.orderby';

import { StoreState } from '../index';
import {
  AngularCoordinates,
  AssortmentSweepWarnings,
  AtributeElement,
  AttributeColor,
  AttributeProduct,
  Attributes,
  AttributesColors,
  AttributesProducts,
  BincapRecord,
  ClipboardFixtureFacings,
  ExcludedProductWarnings,
  FacingGropOrigin,
  FacingGroup,
  FacingGroupCipboard,
  FdDwoBincapWarnings,
  Filters,
  ForecastName,
  Job,
  LeadInInfo,
  LocatedFixture,
  Nesting,
  NormalizeProducts,
  PogAssortment,
  ProductMissingBlockingDataWarnings,
  SkuMetrics,
  SolverOutputState,
  WarningData,
  WarningsData
} from './job.types';
import {
  AssortmentItemOptimized,
  ContextMenuData,
  ContextMenuSegmentData,
  DemandOptions,
  LabelType,
} from '../../types/models';
import { ApiAssortmentProtos, ApiSolverOutputInputParameters } from '../../types/api';
import {
  colorsDGM,
  colorsWOS,
  criticalAlertTypes,
  DAFULT_VENDOR_PEGBOARD,
  EPSILON,
  excludedProductAlertTypes,
  EXTRA_ESL_HEIGHT_OUTSIDE_FACING,
  Margin,
  UNSUPPORTED_BLOCKING_FILTERS,
  WEEKS_OF_SUPPLY
} from '../../utils/constants';
import { escapeRegExp, generateFixtureId, generateUniqueId } from '../../helpers/utils';
import { reviewAndEditSelector, selectedFacingEntitiesSelector } from '../viewAndEdit/viewAndEdit.selectors';
import {
  adjacentHeightToShelfSlopeAngle,
  checkIsIdenticalFixtures,
  correctionPositionPegholes,
  correctionWorkbenchItems,
  filterByUniqueSku,
  generateIdsInRange,
  getFacingGroupByAltOrientation,
  getFacingHeight,
  getHeavyProductAlerts,
  getLinkedProductsAlert,
  getRegExpForClipboardSearching,
  getUserAlert,
  getWeightRestrictionAlert,
  getWhiteSpaceAlert,
  groupAlertsByFixtureType,
  groupByObstructedRows,
  intToHSL,
  isFixturesOverlap,
  isPeggableFixture,
  processAlertsToDisplay,
  processMovingFacingsToWorkbench,
  transformPegholes,
  yToOriginShelfSlopeOffset,
  yToShelfSlopeOffset,
} from '../../components/ViewAndEdit/OptimizedAisleView/utils';
import { range } from '../../helpers/data';
import {
  BASKET_PARAMS,
  BASKET_TEMPLATE,
  CROSSBAR_TEMPLATE,
  DEFAULT_TEMPLATE,
  DISPLAY_HEIGHT,
  DISTRICT_TYPES,
  FIXTURE_TYPES,
  FixtureType,
  HORIZONTAL_SPACING,
  PEGBOARD_TEMPLATE,
  PEGBOARD_X_OFFSET,
  PEGBOARD_Y_OFFSET,
  SEGMENT_WIDTH,
  SHELF_TEMPLATE,
  VERTICAL_SPACING
} from '../../components/ViewAndEdit/editorConstants';
import {
  DraggableFixture,
  DraggableFixtureParams,
  ElementsFacingGroups,
  FixtureItemSpace,
  FixtureSpace,
  SplicerPosition,
} from '../../types/editor';
import { VendorPegbordItem } from '../vendorPegboard/vendorPegboard.types';
import { blockingSelector } from '../blocking/blocking.selectors';
import { ContextWorkbenchMenuData } from '../../components/ViewAndEdit/OptimizedAisleView/Workbench';
import { pegboardsSelector } from '../vendorPegboard/vendorPegboard.selectors';
import { getFixtureIdByXCoordinate, getProductNumsFitMirroringHeight } from '../../components/Assortment/helpers';
import {
  facingGroupHeight,
  generateRange,
  generateRangeDGM,
  heightByStep,
  orientationFacing,
  rotateCappedOrientationFacing,
  rotateClockwiseOrientationFacing,
  rotateHorizontallyOrientationFacing,
  rotateVerticallyOrientationFacing,
  updateBlocks,
} from './job.helper';
import { jobsDoneSelector, jobsSelector } from '../jobs/jobs.selectors';
import { comparisonSelector } from '../comparison/comparison.selectors';
import { assortmentApiSelector, assortmentSelector, baseAssortmentSelector } from '../assortment/assortment.selectors';
import { productsListSelector, productsSelector } from '../products/products.selectors';
import { Products } from '../products/products.types';
import { blockingListSelector } from '../blockingList/blockingList.selectors';

export const jobSelector = (state: StoreState) => state.job;
export const metricJobSelector = (state: StoreState) => state.metricJob;
export const solverOutputSelector = (state: StoreState): StoreState['solverOutput'] => state.solverOutput;
export const currentSelectedSelector = (state: StoreState) => state.comparison.currentSelected;
export const packagingSelector = (state: StoreState) => state.assortment.packaging;
export const selectedFacingsSelector = (state: StoreState) => state.reviewAndEdit.selectedFacingEntities;
export const districtTypeSelector = (state: StoreState) => state.reviewAndEdit.selectedDistrictType;
export const selectedFixturesSelector = (state: StoreState) => state.reviewAndEdit.selectedFixtureIds;
export const availableAislesSelector = (state: StoreState) => state.user.availableAisles;
export const removedFacingsSelector = (state: StoreState) => state.reviewAndEdit.removedFacings;
export const incompatiblePegSkusSelector = (state: StoreState) => state.reviewAndEdit.incompatiblePegSkus;
export const setActiveClipboardSkuSelector = (state: StoreState) => state.reviewAndEdit.activeClipboardSku;
export const selectedFixtureSelector = (state: StoreState) => state.reviewAndEdit.selectedFixtureType;
export const workbenchOpenedSelector = (state: StoreState) => state.reviewAndEdit.isWorkbenchOpened;

const visualizerAlertSelector = (state: StoreState) => state.reviewAndEdit.visualizerAlert;

export const saveNewEditedPogProgressSelector = ((state: StoreState) => state?.api?.saveNewEditedPog?.status === 'FETCHING');
export const updateEditedPogProgressSelector = ((state: StoreState) => state?.api?.updateEditedPog?.status === 'FETCHING');

export const historySelector = createSelector(
  reviewAndEditSelector,
  reviewAndEdit => reviewAndEdit.history,
);

export const historyStepSelector = createSelector(
  reviewAndEditSelector,
  reviewAndEdit => reviewAndEdit.historyStep,
);

export const planogramSelector = createSelector<StoreState, SolverOutputState, SolverOutputState['planogram']>(
  solverOutputSelector,
  solverOutput => solverOutput?.planogram,
);

export const labelsSelector = createSelector(
  planogramSelector,
  planogram => planogram?.labels || '',
);

export const jobIdSelector = createSelector<StoreState, SolverOutputState, SolverOutputState['job_id']>(
  solverOutputSelector,
  solverOutput => solverOutput.job_id,
);

export const isMirroredSelector = createSelector(
  solverOutputSelector,
  solverOutput => {
    return solverOutput?.mirrored_parent_id ? true : false;
  }
);

export const activeJobSelector = createSelector(
  jobIdSelector,
  jobsSelector,
  (jobId, jobs) => {
    if (jobId) {
      const jobInfo = jobs.filter((job: Job) => job.job_id === jobId);

      if (jobInfo.length) {
        return { job: jobInfo[0] };
      }
    }

    return { job: null };
  }
);

export const userNotesSelector = createSelector<StoreState, SolverOutputState, SolverOutputState['user_notes']>(
  solverOutputSelector,
  solverOutput => solverOutput.user_notes,
);

export const solverOutputSolverInputSelector = createSelector<
  StoreState,
  SolverOutputState,
  SolverOutputState['solver_input']
>(solverOutputSelector, (solverOutput) => solverOutput.solver_input);

export const solverOutputSolverInputSkuSelector = createSelector(
  solverOutputSolverInputSelector,
  solverOutput => solverOutput?.product_nums || []
);

export const pogAssortmentInitIncludedSelector = createSelector(
  solverOutputSelector,
  solverOutput => solverOutput?.planogram?.pog_assortment?.init_included || []
);

export const solverOutputSolverInputParametersSelector = createSelector<
  StoreState,
  SolverOutputState['solver_input'],
  ApiSolverOutputInputParameters | undefined
>(
  solverOutputSolverInputSelector,
  (solverOutputSolverInput) => solverOutputSolverInput?.parameters
);

export const pogStoreNumberSelector = createSelector(
  solverOutputSolverInputParametersSelector,
  (parameters) => parameters?.store_num
);

export const jobDoneLastSelector = createSelector(jobsDoneSelector, (jobs) => {
  if (jobs.length) {
    return jobs.sort(
      (jobA: Job, jobB: Job) =>
        new Date(jobB.created_at).getTime() -
        new Date(jobA.created_at).getTime()
    )[0];
  }

  return null;
});

export const aisleGeometrySelector = createSelector(
  planogramSelector,
  (planogram) => planogram?.aisle_geometry
);

export const aisleGeometryBlocksSelector = createSelector(
  aisleGeometrySelector,
  (aisleGeometry) => aisleGeometry?.blocks
);

export const aisleGeometryBlocksHistorySelector = createSelector(
  aisleGeometryBlocksSelector,
  historySelector,
  historyStepSelector,
  (blocks, history, historyStep) => {
    let lastBlocks = blocks;

    for (let i = 0; i <= historyStep; i++) {
      if (history[i].blocks.length > 0) {
        lastBlocks = history[i].blocks;
      }
    }

    return lastBlocks;
  }
);

export const aisleGeometryBlocksFixtureSystemSelector = createSelector(
  aisleGeometryBlocksSelector,
  (blocks) => {
    if (Array.isArray(blocks) && blocks.length) {
      return blocks[0].fixture_system;
    }

    return null;
  }
);

export const leadInSelector = createSelector(
  planogramSelector,
  (planogram) => planogram?.lead_ins
);

export const leadInSegmentSelector = createSelector(
  leadInSelector,
  leadIns => {
    if (!leadIns) {
      return [];
    }

    const leadInSegments = leadIns.reduce((segments, leadIn) => {
      const { segment_start, segment_end, text } = leadIn;

      if (segment_start === segment_end && !segments.find(segment => segment.segment === segment_start)) {
        segments.push({ segment: segment_start, text });
      }

      if (segment_start < segment_end) {
        for (let i = segment_start; i <= segment_end; i++) {
          if (!segments.find(segment => segment.segment === i)) {
            segments.push({ segment: i, text });
          }
        }
      }

      return segments;
    }, [] as LeadInInfo[]);

    return leadInSegments;
  }
);

export const popSelector = createSelector(
  planogramSelector,
  (planogram) => planogram?.pops
);

export const merchNotesSelector = createSelector(
  planogramSelector,
  (planogram) => planogram?.merch_notes
);

export const locatedFixturesSelector = createSelector(
  planogramSelector,
  (planogram) => {
    if (planogram && planogram.located_fixtures.length) {
      return planogram.located_fixtures.map(
        ({ y, depth, shelf_slope, ...item }) => ({
          y: yToShelfSlopeOffset(y, shelf_slope, depth),
          depth,
          shelf_slope,
          ...item
        })
      );
    } else {
      return [];
    }
  }
);

export const locatedFixturesHistorySelector = createSelector(
  locatedFixturesSelector,
  historySelector,
  historyStepSelector,
  (fixtures, history, historyStep) => {
    const newFixtures: Array<LocatedFixture> = [];
    const removedId: Array<number> = [];

    for (let i = 0; i <= historyStep; i++) {
      newFixtures.push(...(history[i].newFixtures || []));
      removedId.push(...(history[i].removedFixtureIds || []));
    }

    const pogFixtures = newFixtures
      .concat(fixtures)
      .filter(f => !removedId.includes(f.id));

    return orderBy(
      pogFixtures,
      ['segment', 'y'], ['asc', 'asc']
    );
  }
);

export const clipboardFixturesSelector = createSelector(
  planogramSelector,
  (planogram) => planogram?.clipboardFixtures || []
);

export const locatedFixturesHistorySelector1 = createSelector(
  locatedFixturesSelector,
  historySelector,
  historyStepSelector,
  clipboardFixturesSelector,
  (fixtures, history, historyStep, initialClipboardFixtures) => {
    const newFixtures: Array<LocatedFixture> = [];
    const clipboardFixtures: Array<LocatedFixture> = [];
    const removedId: Array<number> = [];

    for (let i = 0; i <= historyStep; i++) {
      newFixtures.push(...(history[i].newFixtures || []));
      removedId.push(...(history[i].removedFixtureIds || []));
      clipboardFixtures.push(...(history[i].newClipboardFixtures || []));
    }

    const allFixtures = [
      ...newFixtures,
      ...fixtures,
      ...clipboardFixtures,
      ...initialClipboardFixtures
    ];
    const pogFixtures = [
      ...newFixtures,
      ...fixtures
    ].filter(f => !removedId.includes(f.id));

    return {
      allFixtures,
      pogFixtures
    };
  }
);

export const locatedFixturesByIdSelector = createSelector(
  locatedFixturesHistorySelector1,
  locatedFixtures =>
    locatedFixtures.allFixtures.reduce((acc, locatedFixture) => ({
      ...acc,
      [locatedFixture.id]: locatedFixture
    }), {} as Record<string, LocatedFixture>)
);

const getLocatedFixtureType = (locatedFixture: LocatedFixture) => locatedFixture?.type;

export const facingGroupsOriginalSelector = createSelector(
  planogramSelector,
  planogram => planogram?.facing_groups,
);

export const facingGroupsSelector = createSelector(
  planogramSelector,
  locatedFixturesByIdSelector,
  (planogram, locatedFixtures) =>
    planogram?.facing_groups
      ? planogram.facing_groups.map((facing) => ({
        ...facing,
        fixtureType: getLocatedFixtureType(
          locatedFixtures[facing.located_fixture_id]
        ),
        y: locatedFixtures[facing.located_fixture_id] ? yToShelfSlopeOffset(
          facing.y,
          locatedFixtures[facing.located_fixture_id].shelf_slope,
          locatedFixtures[facing.located_fixture_id].depth
        ) : facing.y,
        packaging: {
          ...facing.packaging,
          ...orientationFacing(
            facing.orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        },
        defaultPackaging: facing.packaging,
        cappedPackaging: {
          ...orientationFacing(
            facing.capped_orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        }
      }))
      : []
);

export const clipboardFixturesFacingsSelector = createSelector(
  planogramSelector,
  locatedFixturesByIdSelector,
  (planogram, locatedFixtures) =>
    planogram?.clipboardFixturesFacings
      ? planogram.clipboardFixturesFacings.map((facing) => ({
        ...facing,
        fixtureType: getLocatedFixtureType(
          locatedFixtures[facing.located_fixture_id]
        ),
        y: locatedFixtures[facing.located_fixture_id] ? yToShelfSlopeOffset(
          facing.y,
          locatedFixtures[facing.located_fixture_id].shelf_slope,
          locatedFixtures[facing.located_fixture_id].depth
        ) : facing.y,
        packaging: {
          ...facing.packaging,
          ...orientationFacing(
            facing.orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        },
        defaultPackaging: facing.packaging,
        cappedPackaging: {
          ...orientationFacing(
            facing.capped_orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        }
      }))
      : []
);

export const currentFixtureNumberIdSelector = createSelector(
  locatedFixturesHistorySelector1,
  fixtures => Math.max(...fixtures.allFixtures.map(f => f.id))
);

export const facingGroupsHistorySelector = createSelector(
  facingGroupsSelector,
  historySelector,
  historyStepSelector,
  (facingGroups, history, historyStep) => {
    const newFacings = [];
    const removedId: Array<string> = [];

    for (let i = 0; i <= historyStep; i++) {
      newFacings.push(...(history[i].newFacings || []));
      removedId.push(...(history[i].removedId || []));
    }

    const newFacingsOrientations = newFacings.map((facing) => ({
      ...facing,
      packaging: {
        ...facing.packaging,
        ...orientationFacing(
          facing.orientation_code,
          facing.packaging.width,
          facing.packaging.height,
          facing.packaging.depth
        )
      }
    }));

    return newFacingsOrientations
      .concat(facingGroups)
      .filter((item) => !removedId.includes(item.id));
  }
);

export const searchedFacingGroupSelector = (searchValueForVisualizer: string = '') => createSelector(
  facingGroupsHistorySelector,
  productsSelector,
  (facings, products) => {
    if (searchValueForVisualizer) {
      const regExpPattern = /^(\d{2,3}-)(\d+)?$/;
      const regExp = regExpPattern.test(searchValueForVisualizer) ?
        new RegExp(`^(0+)?${searchValueForVisualizer.replace('-', '')}`) :
        new RegExp(escapeRegExp(searchValueForVisualizer.toString().toLowerCase()));
      const filteredData = facings.filter(item => {
        const product = products[item.product_num];

        if (product) {
          return product.product_num.match(regExp) ||
            product.product_english_desc.toLowerCase().match(regExp) ||
            product.product_english_long_desc.toLowerCase().match(regExp);
        }

        return false;
      });

      return filteredData.map(item => item?.product_num);
    }

    return [];
  }
);

export const editedSolverOutputSelector = createSelector(
  solverOutputSelector,
  locatedFixturesByIdSelector,
  historySelector,
  historyStepSelector,
  (solverOutput, locatedFixturesById, history, historyStep) => {
    const newFacings: Array<FacingGroup> = [];
    const removedId: Array<string> = [];

    const newFixtures: Array<LocatedFixture> = [];
    const removedFixturesId: Array<number> = [];

    const newClipboardFacings: Array<FacingGroup> = [];
    const removedClipboardId: Array<string> = [];

    const newWorkbenchFacings: Array<FacingGroup> = [];
    const removedWorkbenchId: Array<string> = [];
    const excludedIds: string[] = [];

    const newClipboardFixturesFacings: Array<FacingGroup> = [];
    const removedClipboardFixturesFacings: Array<string> = [];

    const newClipboardFixtures: Array<LocatedFixture> = [];
    const removedClipboardFixtures: Array<number> = [];

    const facingGroups = solverOutput?.planogram?.facing_groups || [];
    const located_fixtures = solverOutput?.planogram?.located_fixtures || [];
    const clipboard = solverOutput?.planogram?.clipboard || [];
    const workbench = solverOutput?.planogram?.workbench || [];
    const clipboardFixtures = solverOutput?.planogram?.clipboardFixtures || [];
    const clipboardFixturesFacings = solverOutput?.planogram?.clipboardFixturesFacings || [];
    const newInitIncluded = solverOutput.planogram?.pog_assortment?.init_included ?? [];
    let blocks = solverOutput?.planogram?.aisle_geometry?.blocks || [];

    for (let i = 0; i <= historyStep; i++) {
      newFacings.push(...(history[i].newFacings || []));
      removedId.push(...(history[i].removedId || []));
      newFixtures.push(...(history[i].newFixtures || []));
      removedFixturesId.push(...(history[i].removedFixtureIds || []));
      newClipboardFacings.push(...(history[i].newClipboardFacings || []));
      removedClipboardId.push(...(history[i].removedClipboardId || []));
      newClipboardFixturesFacings.push(...(history[i].newClipboardFixturesFacings || []));
      removedClipboardFixturesFacings.push(...(history[i].removedClipboardFixturesFacings || []));
      newClipboardFixtures.push(...(history[i].newClipboardFixtures || []));
      removedClipboardFixtures.push(...(history[i].removedClipboardFixtures || []));
      removedClipboardFixtures.push(...(history[i].removedClipboardFixtures || []));
      newWorkbenchFacings.push(...(history[i].newWorkbenchFacings || []));
      removedWorkbenchId.push(...(history[i].removedWorkbenchFacingId || []));
      excludedIds.push(...(history[i].excludedIds || []));

      if (history[i]?.blocks?.length) {
        blocks = history[i].blocks;
      }
    }

    const newFacingGroups: Array<FacingGropOrigin> = newFacings
      .map(({ cappedPackaging, fixtureType, defaultPackaging, ...facing }) => ({
        ...facing,
        y: yToOriginShelfSlopeOffset(
          facing.y,
          locatedFixturesById[facing.located_fixture_id]?.shelf_slope,
          locatedFixturesById[facing.located_fixture_id]?.depth
        )
      }))
      .concat(facingGroups)
      .filter((item) => !removedId.includes(item.id));

    const pogFixtures: Array<LocatedFixture> = newFixtures
      .map(locatedFixture => ({
        ...locatedFixture,
        y: yToOriginShelfSlopeOffset(
          locatedFixture.y,
          locatedFixture.shelf_slope,
          locatedFixture.depth
        ),
      }))
      .concat(located_fixtures)
      .filter(f => !removedFixturesId.includes(f.id));

    const locatedFixturesIdsByFacingGroups = Array.from(new Set(newFacingGroups.map(facingGroup => facingGroup.located_fixture_id)));
    const locatedFixtures: Array<LocatedFixture> = pogFixtures.map(locatedFixture => ({
      ...locatedFixture,
      is_merchandisable: locatedFixturesIdsByFacingGroups.includes(locatedFixture.id)
        ? true
        : locatedFixture.is_merchandisable
    }));

    const newLocatedFixtures: Array<LocatedFixture> = orderBy(
      locatedFixtures,
      ['segment', 'y'], ['asc', 'asc']
    );

    const clearClipboard: Array<FacingGropOrigin> = newClipboardFacings
      .map(({ cappedPackaging, fixtureType, defaultPackaging, ...facing }) => facing)
      .concat(clipboard)
      .filter((item) => !removedClipboardId.includes(item.id));

    const clearWorkbenchFacings = newWorkbenchFacings
      .map(({ cappedPackaging, fixtureType, defaultPackaging, ...facing }) => facing)
      .concat(workbench)
      .filter((item) => !removedWorkbenchId.includes(item.id));

    const clearInitInclude = newInitIncluded
      .filter((item) => !excludedIds.includes(item));

    const clearClipboardFixtures = newClipboardFixtures
      .concat(clipboardFixtures)
      .filter((item) => !removedClipboardFixtures.includes(item.id));

    const clearClipboardFixturesFacings: Array<FacingGropOrigin> = newClipboardFixturesFacings
      .map(({ cappedPackaging, fixtureType, defaultPackaging, ...facing }) => facing)
      .concat(clipboardFixturesFacings)
      .filter((item) => !removedClipboardFixturesFacings.includes(item.id));

    const pog_assortment: Partial<PogAssortment> = {
      ...solverOutput.planogram?.pog_assortment,
      init_included: clearInitInclude,
    };

    return {
      job_id: solverOutput.job_id,
      parent_job_id: solverOutput?.parent_job_id,
      facing_groups: newFacingGroups,
      located_fixtures: newLocatedFixtures,
      clipboard: clearClipboard,
      clipboardFixtures: clearClipboardFixtures,
      clipboardFixturesFacings: clearClipboardFixturesFacings,
      workbench: clearWorkbenchFacings,
      blocks,
      type: solverOutput.type,
      pog_assortment,
    };
  }
);

export const itemsCoordinatesSelector = createSelector(
  facingGroupsHistorySelector,
  labelsSelector,
  (facings, labels) =>
    facings?.map((item) => {
      const height = getFacingHeight(
        item.packaging.height,
        item.vertical_facings,
        item.cap_nested as Nesting,
        item.packaging.nested_height,
        item.cappedPackaging.height,
        item.capped_vertical_facings
      );
      const isPeggableFixtureType = isPeggableFixture(item.fixtureType);
      const isSpecialty = item.fixtureType === FIXTURE_TYPES.SPECIALTY;
      const peggable = item.packaging.peggable;
      const isPeg =
        peggable &&
        item.packaging.pegholes.length > 0 &&
        item.packaging.peggable &&
        isPeggableFixtureType;
      const isESL = isPeg && labels === LabelType.ESL;
      const eslHeightOutsideFacing = isESL ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0;

      return {
        id: item.id,
        x1: round(item.x, 2),
        y1: round(item.y, 2),
        x2: round(item.x + item.packaging.width * item.horizontal_facings, 2),
        y2: round(item.y + height + eslHeightOutsideFacing, 2),
        isFacingGroup: item.horizontal_facings > 1,
        isCrossbar: Boolean(item.packaging.pegholes),
        peggable,
        isPeg,
        isSpecialty
      };
    })
);

export const elementsFacingGroupsSelector = createSelector(
  facingGroupsHistorySelector,
  (facings) =>
    facings.reduce<Array<ElementsFacingGroups>>((acc, facing) => {
      const items = range(facing.horizontal_facings).map((horizontal) => {
        const height = getFacingHeight(
          facing.packaging.height,
          facing.vertical_facings,
          facing.cap_nested as Nesting,
          facing.packaging.nested_height,
          facing.cappedPackaging.height,
          facing.capped_vertical_facings
        );

        return {
          horizontal: facing.horizontal_facings,
          id: facing.id,
          x1: round(facing.x + facing.packaging.width * horizontal, 2),
          y1: round(facing.y, 2),
          x2: round(
            facing.x +
              facing.packaging.width +
              facing.packaging.width * horizontal,
            2
          ),
          y2: round(facing.y + height, 2),
          isCrossbar: facing.fixtureType === 'CROSSBAR',
          horizontalElementNumber: horizontal + 1
        };
      });

      return acc.concat(items);
    }, [])
);

export const itemsIdsSelector = createSelector(
  facingGroupsHistorySelector,
  (facings) =>
    facings?.reduce((acc, val) => {
      if (val.horizontal_facings > 1) {
        const extendedIds = generateIdsInRange(
          0,
          val.horizontal_facings,
          1,
          val.id
        );

        acc.push(...extendedIds);
      }

      acc.push(val.id);

      return acc;
    }, [] as string[])
);

export const facingGroupsList = createSelector(
  facingGroupsSelector,
  (facingGroups) =>
    Array.from(new Set(facingGroups.map((facing) => facing.product_num)))
);

export const locatedFixturesIdToTypeSelector = createSelector(
  locatedFixturesHistorySelector,
  (locatedFixtures) =>
    locatedFixtures.reduce((acc, val) => {
      acc[val.id] = val.type as FixtureType;

      return acc;
    }, {} as Record<string, FixtureType>)
);

export const aisleHeightSelector = createSelector(
  aisleGeometrySelector,
  facingGroupsSelector,
  locatedFixturesHistorySelector,
  (aisleGeometry, facingGroups, locatedFixtures) => {
    if (aisleGeometry && facingGroups && locatedFixtures) {
      const maxAisleHeight = Math.max(
        ...aisleGeometry.blocks.map((block) => block.height)
      );
      const maxAisleHeightByStep = heightByStep(maxAisleHeight + 8);
      const maxFacingGroupsHeight = Math.max(
        ...facingGroups.map((facingGroup) => facingGroupHeight(facingGroup))
      );
      const maxFacingGroupsHeightByStep = heightByStep(maxFacingGroupsHeight);
      const maxLocatedFixtures = Math.max(
        ...locatedFixtures.map(
          (fixture) =>
            fixture.y + fixture.height + (fixture.is_display_only ? 8 : 0)
        )
      );
      const maxLocatedFixturesHeightByStep = heightByStep(maxLocatedFixtures);

      return Math.max(
        maxAisleHeightByStep,
        maxFacingGroupsHeightByStep,
        maxLocatedFixturesHeightByStep
      );
    }

    return 0;
  }
);

export const aisleDepthSelector = createSelector(
  aisleGeometrySelector,
  (aisleGeometry) => {
    let maxAisleDepth = 0;

    if (aisleGeometry) {
      maxAisleDepth = Math.max(
        ...aisleGeometry.blocks.map(block => block.depth)
      );
    }

    return maxAisleDepth;
  }
);

export const fixtureShelfSelector = createSelector(
  locatedFixturesHistorySelector,
  (fixtures) => fixtures.filter(
    ({ type }) =>
      type !== FIXTURE_TYPES.PEGBOARD && type !== FIXTURE_TYPES.CROSSBAR && type !== FIXTURE_TYPES.SPECIALTY && type !== FIXTURE_TYPES.FLATBAR
  )
);

export const fixtureSpacesSelector = createSelector(
  fixtureShelfSelector,
  aisleHeightSelector,
  (fixtures, height) =>
    fixtures.reduce((acc, currentFixture, i, array) => {
      const nextFixtures = array[i + 1];

      if (
        nextFixtures &&
        nextFixtures.y < currentFixture.y &&
        nextFixtures.segment === currentFixture.segment
      ) {
        return acc;
      }

      if (
        (nextFixtures && nextFixtures.segment !== currentFixture.segment) ||
        !nextFixtures
      ) {
        const fixtureSpace = {
          id: currentFixture.id,
          fixtureId: currentFixture.fixture_id,
          segment: currentFixture.segment,
          y1: currentFixture.y + currentFixture.height,
          x1: round(currentFixture.x, 2),
          x2: round(currentFixture.x + currentFixture.width, 2),
          y2: height,
          z: currentFixture.z,
          isDisplayOnly: currentFixture.is_display_only
        };

        acc.push(fixtureSpace);

        return acc;
      }

      if (nextFixtures) {
        const fixtureSpace = {
          id: currentFixture.id,
          fixtureId: currentFixture.fixture_id,
          segment: currentFixture.segment,
          y1: currentFixture.y + currentFixture.height,
          x1: round(currentFixture.x, 2),
          x2: round(currentFixture.x + currentFixture.width, 2),
          y2: nextFixtures.y < currentFixture.y ? height : nextFixtures.y,
          z: currentFixture.z,
          isDisplayOnly: currentFixture.is_display_only
        };

        acc.push(fixtureSpace);
      }

      return acc;
    }, [] as FixtureSpace[])
);

export const fixtureHeightSelector = createSelector(
  locatedFixturesHistorySelector,
  (locatedFixtures) =>
    locatedFixtures.map((fixture) => ({
      id: fixture.id,
      fixtureId: fixture.fixture_id,
      segment: fixture.segment,
      x1: fixture.x,
      y1: fixture.y,
      x2: fixture.x + fixture.width,
      y2: fixture.y + fixture.height,
      z: fixture.z,
      isDisplayOnly: fixture.is_display_only,
      isCrossbar: fixture.type === FIXTURE_TYPES.CROSSBAR,
      isPegboard: fixture.type === FIXTURE_TYPES.PEGBOARD,
      isFlatbar: fixture.type === FIXTURE_TYPES.FLATBAR
    }))
);

export const fixtureCrossbarSelector = createSelector(
  fixtureHeightSelector,
  (locatedFixtures) => locatedFixtures.filter((fixture) => fixture.isCrossbar)
);

export const fixturePegboardSelector = createSelector(
  fixtureHeightSelector,
  (locatedFixtures) => locatedFixtures.filter((fixture) => fixture.isPegboard)
);

export const fixtureFlatbarSelector = createSelector(
  fixtureHeightSelector,
  (locatedFixtures) => locatedFixtures.filter((fixture) => fixture.isFlatbar)
);

export const fixtureBasketSelector = createSelector(
  locatedFixturesHistorySelector,
  (locatedFixturesHistory) => locatedFixturesHistory
    .filter(fixture => fixture.type === 'BASKET')
    .map(fixture => ({
      id: fixture.id,
      fixtureId: fixture.fixture_id,
      segment: fixture.segment,
      x1: fixture.x,
      y1: fixture.y,
      x2: fixture.x + fixture.width,
      y2: fixture.y + fixture.height + fixture.grille_height,
      z: fixture.z,
      isDisplayOnly: fixture.is_display_only,
      isCrossbar: false,
      isPegboard: false
    }))
);

export const fixtureDefaultShelfSelector = createSelector(
  locatedFixturesHistorySelector,
  (locatedFixturesHistory) => locatedFixturesHistory
    .filter(fixture => fixture.type === 'SHELF')
    .map(fixture => ({
      id: fixture.id,
      fixtureId: fixture.fixture_id,
      segment: fixture.segment,
      x1: fixture.x,
      y1: fixture.y,
      x2: fixture.x + fixture.width,
      y2: fixture.y + fixture.height + fixture.grille_height,
      z: fixture.z,
      isDisplayOnly: fixture.is_display_only,
      isCrossbar: false,
      isPegboard: false
    }))
);

export const numberOfSegmentsSelector = createSelector(
  aisleGeometryBlocksHistorySelector,
  (blocks) => {
    if (blocks) {
      return blocks.reduce(
        (acc, block) => acc + block.num_segments,
        0
      );
    }

    return 0;
  }
);

export const segmentSpacesSelector = (scale: number) =>
  createSelector(numberOfSegmentsSelector, (numberofSegments) =>
    range(numberofSegments).map((item) => ({
      x1: SEGMENT_WIDTH * scale * item,
      x2: SEGMENT_WIDTH * scale * (item + 1)
    }))
  );

export const productsPlanogramSelector = createSelector(
  planogramSelector,
  (planogram) => {
    if (planogram?.products) {
      const norm = planogram.products.reduce<NormalizeProducts>(
        (acc, product) => ({
          products: {
            ...acc.products,
            [product.product_num]: product
          },
          productsList: acc.productsList.concat(product.product_num)
        }),
        { products: {}, productsList: [] }
      );

      return norm;
    }

    return {
      products: {},
      productsList: []
    };
  }
);

export const clipboardSelector = createSelector(
  planogramSelector,
  locatedFixturesByIdSelector,
  productsPlanogramSelector,
  (planogram, locatedFixtures, { products }) =>
    planogram?.clipboard
      ? planogram.clipboard.map((facing) => ({
        ...facing,
        y: locatedFixtures[facing.located_fixture_id] ? yToShelfSlopeOffset(
          facing.y,
          locatedFixtures[facing.located_fixture_id].shelf_slope,
          locatedFixtures[facing.located_fixture_id].depth,
        ) : facing.y,
        packaging: {
          ...facing.packaging,
          ...orientationFacing(
            facing.orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        },
        defaultPackaging: facing.packaging,
        cappedPackaging: {
          ...orientationFacing(
            facing.capped_orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        },
        fixtureType: facing.fixtureType
          ? facing.fixtureType
          : locatedFixtures[facing.located_fixture_id]?.type || 'SHELF',
      }))
      : []
);

export const workbenchSelector = createSelector(
  planogramSelector,
  locatedFixturesByIdSelector,
  productsPlanogramSelector,
  (planogram, locatedFixtures, { products }) =>
    planogram?.workbench
      ? planogram.workbench.map((facing) => ({
        ...facing,
        y: locatedFixtures[facing.located_fixture_id] ? yToShelfSlopeOffset(
          facing.y,
          locatedFixtures[facing.located_fixture_id].shelf_slope,
          locatedFixtures[facing.located_fixture_id].depth,
        ) : facing.y,
        packaging: {
          ...facing.packaging,
          ...orientationFacing(
            facing.orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        },
        defaultPackaging: facing.packaging,
        cappedPackaging: {
          ...orientationFacing(
            facing.capped_orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        },
        fixtureType: facing.fixtureType
          ? facing.fixtureType
          : locatedFixtures[facing.located_fixture_id]?.type || 'SHELF',
      }))
      : []
);

export const workbenchHistorySelector = createSelector(
  workbenchSelector,
  historySelector,
  historyStepSelector,
  (workbench, history, historyStep) => {
    let newWorkbenchFacings = [];
    const removedWorkbenchId: Array<string> = [];

    for (let i = 0; i <= historyStep; i++) {
      newWorkbenchFacings.push(...(history[i].newWorkbenchFacings || []));
      removedWorkbenchId.push(...(history[i].removedWorkbenchFacingId || []));
    }

    newWorkbenchFacings = newWorkbenchFacings.map((facing) => ({
      ...facing,
      packaging: {
        ...facing.packaging,
        ...orientationFacing(
          facing.orientation_code,
          facing.packaging.width,
          facing.packaging.height,
          facing.packaging.depth
        )
      }
    }));
    const clearWorkbench: Array<FacingGroup> = newWorkbenchFacings
      .concat(workbench)
      .filter((item) => !removedWorkbenchId.includes(item.id));

    return clearWorkbench;
  }
);

export const uniquePlanogramFacingsSelector = createSelector(
  facingGroupsHistorySelector,
  workbenchHistorySelector,
  (facingGroups, facingGroupsWorkbench) => facingGroups.concat(facingGroupsWorkbench)
    .reduce((acc, { product_num: sku, horizontal_facings: h }) => ({
      ...acc,
      [sku]: acc[sku] ? acc[sku] + h : h
    }), {} as Record<string, number>));

export const uniquePlanogramFacingsListSelector = createSelector(
  uniquePlanogramFacingsSelector,
  uniquePlanogramFacings => Object.keys(uniquePlanogramFacings)
);

const protoCSelector = createSelector(
  assortmentSelector,
  solverOutputSolverInputParametersSelector,
  (assortment, parameters) => {
    const protos = assortment?.assortmentApi?.protos as unknown as ApiAssortmentProtos;

    if (protos && parameters) {
      const labels = parameters?.labels as 'esl' | 'regular';

      const protosC = protos?.[labels]?.C;

      return protosC;
    }

    return [];
  }
);

const forecastInfoSelector = createSelector(
  assortmentSelector,
  assortment => assortment.forecastInfo || []
);

export const currentMetricInfoSelector = createSelector(
  assortmentSelector,
  (assortment) => assortment.currentMetricInfo
);

export const totalCommitmentsSelector = createSelector(
  assortmentSelector,
  (assortment) => assortment.totalCommitments
);

export const bincapSelector = createSelector(
  metricJobSelector,
  (metricJob) => metricJob.bincap
);

export const productInfoSelector = createSelector(
  protoCSelector,
  forecastInfoSelector,
  totalCommitmentsSelector,
  bincapSelector,
  packagingSelector,
  (protoC, forecastInfo, totalCommitments, bincaps, packaging) => {
    const infoData = forecastInfo.reduce((acc, val) => {
      const prodNum = val?.product_num;
      const fcst = val?.total_forecast;
      const historicTotalSales = val?.hist_total_sales || null;
      const totalComm = totalCommitments?.[prodNum] || null;
      const bincap = bincaps?.[prodNum] || null;
      const weight = packaging?.[prodNum]?.weight || null;

      acc[prodNum] = {
        isProtoC: protoC?.includes(prodNum),
        fcst,
        totalComm,
        weight,
        bincap,
        historicTotalSales
      };

      return acc;
    }, {} as Record<string, SkuMetrics>);

    return infoData;
  }
);

export const startingAssortmentSelector = createSelector(
  solverOutputSolverInputSelector,
  assortmentApiSelector,
  (solverOutput, assortment) => {
    const startingAssortment = solverOutput?.parameters?.starting_assortment || '';
    let all: string[] = [];

    if (startingAssortment === 'store_default') {
      all = assortment?.store_default || [];
    } else {
      const [orientation, labelType, size] = startingAssortment?.split('.') || [];

      if ((orientation === 'left' || orientation === 'right') && (labelType === 'esl' || labelType === 'regular') && (size === 'A' || size === 'B' || size === 'C' || size === 'D' || size === 'E')) {
        all = assortment?.protos?.[labelType][size] || [];
      }
    }

    return all;
  }
);

export const planogramFactorySelector = createSelector(
  clipboardSelector,
  workbenchSelector,
  clipboardFixturesSelector,
  clipboardFixturesFacingsSelector,
  (
    clipboard,
    workbench,
    clipboardFixtures,
    clipboardFixturesFacings,
  ) => ({
    clipboard,
    workbench,
    clipboardFixtures,
    clipboardFixturesFacings
  })
);

export const clipboardHistorySelector = createSelector(
  planogramFactorySelector,
  historySelector,
  historyStepSelector,
  locatedFixturesByIdSelector,
  productsListSelector,
  productsSelector,
  packagingSelector,
  facingGroupsSelector,
  aisleDepthSelector,
  solverOutputSolverInputSkuSelector,
  pogAssortmentInitIncludedSelector,
  totalCommitmentsSelector,
  (
    {
      clipboard,
      workbench,
      clipboardFixtures,
      clipboardFixturesFacings
    },
    history,
    historyStep,
    locatedFixtures,
    productsList,
    products,
    packaging,
    facingGroups,
    aisleDepth,
    solverInputSku,
    initIncluded,
    totalCommitments,
  ) => {
    const newFacings = [];
    const removedId: Array<string> = [];
    const newClipboardFacings: Array<FacingGroup> = [];
    const newWorkbenchFacings: Array<FacingGroup> = [];
    const removedClipboardId: Array<string> = [];
    const removedWorkbenchFacingId: Array<string> = [];
    const newClipboardFixtures = [];
    const newClipboardFixturesFacings = [];
    const removedClipboardFixtureFacings: Array<string> = [];
    const removedClipboardFixtures: Array<number> = [];
    const excludedIds: string[] = [];

    for (let i = 0; i <= historyStep; i++) {
      newClipboardFacings.push(...(history[i].newClipboardFacings || []));
      removedClipboardId.push(...(history[i].removedClipboardId || []));
      newFacings.push(...(history[i].newFacings || []));
      newWorkbenchFacings.push(...(history[i].newWorkbenchFacings || []));
      removedId.push(...(history[i].removedId || []));
      removedClipboardFixtureFacings.push(...(history[i].removedClipboardFixturesFacings || []));
      newClipboardFixtures.push(...(history[i].newClipboardFixtures || []));
      newClipboardFixturesFacings.push(...(history[i].newClipboardFixturesFacings || []));
      removedClipboardFixtures.push(...(history[i].removedClipboardFixtures || []));
      removedWorkbenchFacingId.push(...(history[i].removedWorkbenchFacingId || []));
      excludedIds.push(...(history[i].excludedIds || []));
    }

    const newFacingsSKU = newFacings
      .concat(facingGroups)
      .filter((item) => !removedId.includes(item.id))
      .map((item) => item.product_num);

    const newWorkbenchSKU = newWorkbenchFacings.concat(workbench)
      .filter((item) => !removedWorkbenchFacingId.includes(item.id))
      .map((item) => item.product_num);

    const newClipboardOrientations = newClipboardFacings.reverse().map((facing) => ({
      ...facing,
      y: yToShelfSlopeOffset(
        facing.y,
        locatedFixtures[facing.located_fixture_id]?.shelf_slope,
        locatedFixtures[facing.located_fixture_id]?.depth
      ),
      packaging: {
        ...facing.packaging,
        ...orientationFacing(
          facing.orientation_code,
          facing.packaging.width,
          facing.packaging.height,
          facing.packaging.depth
        )
      }
    }));

    const newClipboardFixturesFacingsOrientations = newClipboardFixturesFacings
      .map((facing) => ({
        ...facing,
        y: yToShelfSlopeOffset(
          facing.y,
          locatedFixtures[facing.located_fixture_id]?.shelf_slope,
          locatedFixtures[facing.located_fixture_id]?.depth
        ),
        packaging: {
          ...facing.packaging,
          ...orientationFacing(
            facing.orientation_code,
            facing.packaging.width,
            facing.packaging.height,
            facing.packaging.depth
          )
        }
      })).concat(clipboardFixturesFacings);

    const newClipboardFixturesSkus = newClipboardFixturesFacingsOrientations
      .filter(item => !removedClipboardFixtureFacings.includes(item.id));
    const newClipboardFixtureItems = newClipboardFixtures
      .concat(clipboardFixtures)
      .filter(item => !removedClipboardFixtures.includes(item.id));

    const removedByUser = initIncluded
      .filter(item => !solverInputSku.includes(item) && !newFacingsSKU.includes(item));

    const removedByUserFacings = removedByUser.filter(item => packaging[item])
      .map((item) => {
        const defaultPackaging = packaging[item];
        const excludetFixtureType = defaultPackaging.peggable
          ? 'PEGBOARD'
          : 'SHELF';
        let orientation = 0;
        let excludetPackaging = { ...defaultPackaging };

        if (excludetPackaging.depth > aisleDepth) {
          orientation = 2;
          excludetPackaging = {
            ...excludetPackaging,
            ...orientationFacing(
              orientation,
              defaultPackaging.width,
              defaultPackaging.height,
              defaultPackaging.depth
            ),
          };

          if (excludetPackaging.depth > aisleDepth) {
            orientation = 4;
            excludetPackaging = {
              ...excludetPackaging,
              ...orientationFacing(
                orientation,
                defaultPackaging.width,
                defaultPackaging.height,
                defaultPackaging.depth
              ),
            };
          }
        }

        return ({
          located_fixture_id: 0,
          x: 0,
          y: 0,
          horizontal_facings: 1,
          vertical_facings: 1,
          depth_facings: 1,
          orientation_code: orientation,
          segment: 0,
          cap_nested: '',
          capped_horizontal_facings: 0,
          capped_vertical_facings: 0,
          capped_depth_facings: 0,
          capped_orientation_code: 0,
          peg_id: null,
          product_num: item.toString(),
          packaging: excludetPackaging,
          defaultPackaging,
          id: generateUniqueId(),
          cappedPackaging: {
            width: 0,
            height: 0,
            depth: 0
          },
          fixtureType: excludetFixtureType,
          esl_peg_id: null
        });
      });

    const newClipboard = newClipboardOrientations
      .concat(clipboard)
      .concat(workbench)
      .concat(removedByUserFacings)
      .filter((item) => !removedClipboardId.includes(item.id));

    const newClipboardList = newClipboard.map(item => item.product_num);
    const excludedByTetris = initIncluded.filter(item =>
      !newFacingsSKU.includes(item) &&
      !newClipboardList.includes(item) &&
      !removedByUser.includes(item) &&
      !newWorkbenchSKU.includes(item) &&
      !excludedIds.includes(item)
    );
    const excludedList = productsList.filter(item =>
      !newFacingsSKU.includes(item) &&
      !newClipboardList.includes(item) &&
      !newWorkbenchSKU.includes(item) &&
      !excludedByTetris.includes(item)
    );

    const newSkus = productsList.filter(item =>
      !newFacingsSKU.includes(item) &&
      products[item].is_pog_new_sku
    );

    const skusWithCommitments = productsList.filter(item =>
      !newFacingsSKU.includes(item) &&
      (totalCommitments[item] > 0)
    );

    const [excludedFacings, excludedByTetrisFacings, newSkusFacings, skusWithCommitmentsFacings] = [excludedList, excludedByTetris, newSkus, skusWithCommitments].map(skus => {
      const facingGroups = skus.filter(item => packaging[item])
        .map((item) => {
          const defaultPackaging = packaging[item];
          const excludetFixtureType = defaultPackaging.peggable
            ? 'PEGBOARD'
            : 'SHELF';
          let orientation = 0;
          let excludetPackaging = { ...defaultPackaging };

          if (excludetPackaging.depth > aisleDepth) {
            orientation = 2;
            excludetPackaging = {
              ...excludetPackaging,
              ...orientationFacing(
                orientation,
                defaultPackaging.width,
                defaultPackaging.height,
                defaultPackaging.depth
              ),
            };

            if (excludetPackaging.depth > aisleDepth) {
              orientation = 4;
              excludetPackaging = {
                ...excludetPackaging,
                ...orientationFacing(
                  orientation,
                  defaultPackaging.width,
                  defaultPackaging.height,
                  defaultPackaging.depth
                ),
              };
            }
          }

          return ({
            located_fixture_id: 0,
            x: 0,
            y: 0,
            horizontal_facings: 1,
            vertical_facings: 1,
            depth_facings: 1,
            orientation_code: orientation,
            segment: 0,
            cap_nested: '',
            capped_horizontal_facings: 0,
            capped_vertical_facings: 0,
            capped_depth_facings: 0,
            capped_orientation_code: 0,
            peg_id: null,
            product_num: item.toString(),
            packaging: excludetPackaging,
            defaultPackaging,
            id: generateUniqueId(),
            cappedPackaging: {
              width: 0,
              height: 0,
              depth: 0
            },
            fixtureType: excludetFixtureType,
            esl_peg_id: null
          });
        });

      return facingGroups;
    });

    const skusSequence = Array.from(new Set(newClipboard.map(item => item.product_num)));
    const groupClipboard = newClipboard.reduce<Record<string, FacingGroup>>((acc, facing) => {
      return {
        ...acc,
        [facing.product_num]: {
          ...facing,
          'vertical_facings': acc[facing.product_num]
            ? Math.min(acc[facing.product_num].vertical_facings, facing.vertical_facings)
            : facing.vertical_facings,
          'horizontal_facings': acc[facing.product_num]
            ? acc[facing.product_num]?.horizontal_facings + facing.horizontal_facings
            : facing.horizontal_facings,
        }
      };
    }, {});

    const clipboardFcstSort = skusSequence.map(productNum => groupClipboard[productNum]);

    return {
      clipboardSkus: clipboardFcstSort,
      clipboardFixtures: newClipboardFixtureItems,
      newClipboardFixturesFacings: newClipboardFixturesSkus,
      excludedSkus: excludedFacings,
      excludedByTetrisSkus: excludedByTetrisFacings,
      newSkus: newSkusFacings,
      skusWithCommitments: skusWithCommitmentsFacings,
    };
  }
);

export const clipboardItemsIdsSelector = createSelector(
  clipboardHistorySelector,
  ({ clipboardSkus, excludedSkus, excludedByTetrisSkus }) => clipboardSkus
    .concat(excludedSkus)
    .concat(excludedByTetrisSkus).map(c => c.id)
);

export const facingGroupsByIdsSelector = createSelector(
  facingGroupsHistorySelector,
  clipboardHistorySelector,
  workbenchHistorySelector,
  (facingGroups, clipboard, workbench) =>
    [
      ...facingGroups,
      ...clipboard.clipboardSkus,
      ...clipboard.newClipboardFixturesFacings,
      ...clipboard.excludedSkus,
      ...clipboard.excludedByTetrisSkus,
      ...clipboard.newSkus,
      ...clipboard.skusWithCommitments,
      ...workbench
    ].reduce((acc, facingGroup) => {
      acc[facingGroup.id] = facingGroup;

      return acc;
    }, {} as Record<string, FacingGroup>)
);

export const pegboardFixturesSelector = createSelector(
  locatedFixturesHistorySelector,
  (locatedFixtures) => locatedFixtures.reduce((acc, val) => {
    if (val.type === FIXTURE_TYPES.PEGBOARD) {
      acc[val.segment] = val;

      return acc;
    }

    return acc;
  }, {} as Record<string, LocatedFixture>)
);

export const lowShelfFixturesSelector = createSelector(
  locatedFixturesHistorySelector,
  (locatedFixtures) => locatedFixtures.reduce((acc, fixture) => {
    if (
      (fixture.type === FIXTURE_TYPES.PEGBOARD) ||
      (fixture.type === FIXTURE_TYPES.CROSSBAR) ||
      (fixture.type === FIXTURE_TYPES.FLATBAR)
    ) {
      return acc;
    }

    return {
      ...acc,
      [fixture.segment]:
        acc[fixture.segment] && acc[fixture.segment].y < fixture.y
          ? acc[fixture.segment]
          : fixture
    };
  }, {} as Record<string, LocatedFixture>)
);

export const lowFixturesSelector = createSelector(
  lowShelfFixturesSelector,
  pegboardFixturesSelector,
  (shelves, pegboards) => Object.keys(shelves).map(item => {
    return pegboards[item] && shelves[item] && shelves[item].y >= 12
      ? pegboards[item].id
      : shelves[item].id;
  })
);

export const planogramSideSelector = createSelector(
  planogramSelector,
  (planogram) => planogram?.side
);

export const planogramAisleSelector = createSelector(
  planogramSelector,
  (planogram) => planogram?.aisle_code
);

export const planogramFilterSelector = (state: StoreState) =>
  state.planogramFilter;
export const filterSelector = createSelector(
  planogramFilterSelector,
  (planogramFilter) => planogramFilter.filter
);

export const wosSelector = createSelector(
  metricJobSelector,
  (metricJob) => metricJob.wos
);

export const dgmSelector = createSelector(
  assortmentSelector,
  (assortmentJob) => {
    return assortmentJob.baseAssortment?.reduce((dgm, product) => {
      dgm[product.sku] = product.dgm;

      return dgm;
    }, {} as Record<string, number>);
  }
);

export const facingsSelector = createSelector(
  metricJobSelector,
  (metricJob) => metricJob.facings
);

export const totalCommitmentSelector = createSelector(
  metricJobSelector,
  (metricJob) => metricJob.total_commitments
);

export const forecasteNameByParametersSelector = createSelector(
  solverOutputSolverInputParametersSelector,
  (solverOutputSolverInputParameters) => {
    if (solverOutputSolverInputParameters) {
      const {
        demand,
        use_iss: useISS,
        use_prom: useProm
      } = solverOutputSolverInputParameters;
      const isPeak = demand === DemandOptions.Peak;
      const isAverage = demand === DemandOptions.Average;

      if (isPeak && !useISS && !useProm) return 'peakReg';
      if (isPeak && useISS && !useProm) return 'peakRegIss';
      if (isPeak && !useISS && useProm) return 'peakRegPromo';
      if (isPeak && useISS && useProm) return 'peakRegPromoIss';
      if (isAverage && !useISS && !useProm) return 'avgReg';
      if (isAverage && useISS && !useProm) return 'avgRegIss';
      if (isAverage && !useISS && useProm) return 'avgRegPromo';
      if (isAverage && useISS && useProm) return 'avgRegPromoIss';
    }

    return 'avgReg';
  }
);

export const extendedAssortmentProductSelector = createSelector(
  baseAssortmentSelector,
  (baseAssortment) => {
    const products = baseAssortment?.reduce((acc, current) => {
      if (current.isExtendedAssortment === 'YES') {
        acc.push(current.sku);
      }

      return acc;
    }, [] as Array<string>);

    return products || [];
  }
);

export const warningsSelector = createSelector(
  solverOutputSelector,
  locatedFixturesHistorySelector,
  baseAssortmentSelector,
  totalCommitmentSelector,
  bincapSelector,
  availableAislesSelector,
  planogramAisleSelector,
  (solverOutput, locatedFixtures, baseAssortment, totalCommitment, bincap, availableAisles, aisleCode) => {
    const facingGroups = solverOutput?.planogram?.facing_groups;
    const warnings = solverOutput?.warnings;
    const userWarnings = solverOutput?.user_warnings;
    const excludedProductWarnings = solverOutput?.excluded_product_warnings;
    const productsInFacingGroups = facingGroups
      ? facingGroups.reduce((acc: string[], facing) => {
        if (!acc.includes(facing.product_num)) {
          acc.push(facing.product_num);
        }

        return acc;
      }, [])
      : [];
    const fDDwoBincapWarnings = baseAssortment?.reduce((acc, item) => {
      const { sku, corpStatus } = item;
      const isFdDwo = corpStatus === 'FD' || corpStatus === 'DWO';
      const isBincapIssue = bincap[sku] > totalCommitment[sku] || false;

      if (isFdDwo && isBincapIssue) {
        const facing = facingGroups?.filter(facing => facing.product_num === item.sku);

        if (facing) {
          const facingRecord = facing[0];

          if (facingRecord) {
            const warning = {
              product_num: facingRecord.product_num,
              x: facingRecord.x,
              y: facingRecord.y,
              commitment: totalCommitment[sku],
              bincap: bincap[sku]
            } as FdDwoBincapWarnings;

            acc.push(warning);
          }
        }
      }

      return acc;
    }, [] as Array<FdDwoBincapWarnings>);
    const planogramAisle = availableAisles && availableAisles.length && aisleCode ?
      availableAisles.filter(aisle => aisle.code === aisleCode) :
      null;
    const isRegulatoryGoodsPog = planogramAisle && planogramAisle.length ? planogramAisle[0].status === 'PURPLE' : false;

    if (warnings) {
      const warningData: Array<WarningData> = [...(userWarnings || []), ...(excludedProductWarnings || [])];
      const {
        heavy_product_height_warnings = [],
        product_packaging_warnings = [],
        product_missing_blocking_data_warnings = [],
        linear_whitespace_warnings = [],
        too_much_supply_warnings = [],
        excluded_product_warnings = [],
        weight_restriction_warnings = [],
        assortment_sweep_warnings = [],
        no_longer_available_after_refresh_warnings = [],
        removed_from_aisle_assortment_after_refresh_warnings = []
      } = warnings;

      const addWarning = (warnings: any[], warningType: string) => {
        warningData.push({
          warningType,
          selected: false,
          data: warnings
        });
      };

      if (no_longer_available_after_refresh_warnings.length > 0) {
        addWarning(no_longer_available_after_refresh_warnings, 'no_longer_available_after_refresh_warnings');
      }

      if (removed_from_aisle_assortment_after_refresh_warnings.length > 0) {
        addWarning(removed_from_aisle_assortment_after_refresh_warnings, 'removed_from_aisle_assortment_after_refresh_warnings');
      }

      if (fDDwoBincapWarnings && fDDwoBincapWarnings.length) {
        fDDwoBincapWarnings.forEach(item => {
          addWarning([item], 'fd_dwo_bincap_warnings');
        });
      }

      if (assortment_sweep_warnings.length > 0) {
        const warnings = assortment_sweep_warnings.filter((warning) =>
          productsInFacingGroups.includes(warning.product_num)
        );

        if (warnings.length > 0) {
          addWarning(warnings, 'assortment_sweep_warnings');
        }
      }

      if (heavy_product_height_warnings.length > 0) {
        const warnings = heavy_product_height_warnings.filter((warning) =>
          productsInFacingGroups.includes(warning.product_num)
        );

        if (warnings.length > 0) {
          addWarning(warnings, 'heavy_product_height_warnings');
        }
      }

      if (product_packaging_warnings.length > 0) {
        const warnings = product_packaging_warnings.filter((warning) =>
          productsInFacingGroups.includes(warning.product_num)
        );

        if (warnings.length > 0) {
          addWarning(warnings, 'product_packaging_warnings');
        }
      }

      if (product_missing_blocking_data_warnings.length > 0) {
        product_missing_blocking_data_warnings.forEach(
          (warning: ProductMissingBlockingDataWarnings) => {
            const products = warning.product_nums.filter((product) =>
              productsInFacingGroups.includes(product)
            );

            if (products.length > 0) {
              const warningToShow = { ...warning, product_nums: products };

              addWarning(
                [warningToShow],
                'product_missing_blocking_data_warnings'
              );
            }
          }
        );
      }

      if (linear_whitespace_warnings.length > 0) {
        addWarning(linear_whitespace_warnings, 'linear_whitespace_warnings');
      }

      if (isRegulatoryGoodsPog) {
        addWarning([], 'safety_restriction_warnings');
      }

      if (weight_restriction_warnings.length > 0) {
        const groupedAlertByFixtureType = groupAlertsByFixtureType(weight_restriction_warnings, locatedFixtures);

        Object.keys(groupedAlertByFixtureType).forEach(fixtureType => {
          addWarning(groupedAlertByFixtureType[fixtureType], 'weight_restriction_warnings');
        });
      }

      if (too_much_supply_warnings.length > 0) {
        const warnings = too_much_supply_warnings.filter((warning) =>
          productsInFacingGroups.includes(warning.product_num)
        );

        if (warnings.length > 0) {
          addWarning(warnings, 'too_much_supply_warnings');
        }
      }

      if (excluded_product_warnings.length > 0
        && warningData.filter(d => excludedProductAlertTypes.includes(d.warningType)).length === 0) {
        const excludedProductsNoFit: ExcludedProductWarnings[] = [];
        const excludedProductsTooTight: ExcludedProductWarnings[] = [];

        excluded_product_warnings.forEach(
          (warning: ExcludedProductWarnings) => {
            if (!warning?.in_clipboard) {
              warning.in_clipboard = true;
            }

            if (warning.exclusion_reason === 'no_fit') {
              excludedProductsNoFit.push(warning);
            } else {
              excludedProductsTooTight.push(warning);
            }
          }
        );

        if (excludedProductsNoFit.length > 0) {
          addWarning(excludedProductsNoFit, 'excluded_product_warnings_no_fit');
        }

        if (excludedProductsTooTight.length > 0) {
          addWarning(
            excludedProductsTooTight,
            'excluded_product_warnings_too_tight'
          );
        }
      }

      return warningData;
    }

    return [] as WarningsData;
  }
);

export const baseAssortmentForecaste = createSelector(
  forecasteNameByParametersSelector,
  baseAssortmentSelector,
  facingsSelector,
  (forecasteNameByParameters, baseAssortment, facings) =>
    baseAssortment
      ? baseAssortment.reduce<Record<string, number>>(
        (acc, product) => ({
          ...acc,
          [product.sku]:
              product[forecasteNameByParameters] * facings[product.sku] || 0
        }),
        {}
      )
      : {}
);

export const filterBlockingSelector = createSelector(
  blockingSelector,
  blockingListSelector,
  dgmSelector,
  uniquePlanogramFacingsListSelector,
  wosSelector,
  baseAssortmentForecaste,
  (blocking, blockingList, dgm, productsList, wos, baseAssortment) => {
    try {
      if (
        blocking &&
        blockingList &&
        Object.keys(wos).length &&
        Array.isArray(productsList)
      ) {
        const blockingKeys = Object.keys(blocking);
        const filteredProductsList = productsList.filter(pl => blockingKeys.includes(pl));
        const wosGroup: Record<string, AtributeElement> = generateRange(
          wos,
          filteredProductsList,
        );

        const dgmGroup: Record<string, AtributeElement> = generateRangeDGM(
          dgm,
          filteredProductsList,
        );

        const attributesList = UNSUPPORTED_BLOCKING_FILTERS
          .concat(blockingList)
          .filter(
            (blockingAttribute) => blockingAttribute.toLowerCase() !== 'none'
          );
        const productsWithWOSAndDGM: Record<
          string,
          Array<AtributeElement>
        > = filteredProductsList.reduce((acc, productId: string) => {
          const atributesList = blocking[productId].map(
            (bav) => ({
              unique: bav,
              name: Number(bav) ? Math.round(parseFloat(bav)).toString() : bav
            })
          );

          return {
            ...acc,
            [productId]: (wosGroup[productId] ? [wosGroup[productId]] : [{ name: '0', unique: '0' }]).concat((dgmGroup[productId] ? [dgmGroup[productId]] : [{ name: '0', unique: '0' }]), atributesList)
          };
        }, {});

        const attributesProducts = filteredProductsList.reduce<AttributesProducts>(
          (acc: AttributesProducts, productId: string) => ({
            ...acc,
            [productId]: attributesList.reduce(
              (acc: AttributeProduct, attribute: string, index: number) => ({
                ...acc,
                [attribute]: productsWithWOSAndDGM[productId][index]
              }),
              {}
            )
          }),
          {}
        );
        const { attributes, attributesColors } = attributesList.reduce<{
          attributes: Attributes;
          attributesColors: AttributesColors;
        }>(
          (
            acc: {
              attributes: Attributes;
              attributesColors: AttributesColors;
            },
            attribute: string
          ) => {
            const attributeList = Array.from(
              new Set(
                filteredProductsList
                  .map(
                    (productId) => attributesProducts[productId][attribute]?.name
                  )
                  .sort((a, b) => {
                    const isNumber =
                      !Number.isNaN(parseFloat(a)) &&
                      !Number.isNaN(parseFloat(b));

                    if (isNumber) {
                      return parseFloat(a) > parseFloat(b) ? 1 : -1;
                    }

                    return a > b ? 1 : -1;
                  })
              )
            );
            const colorsHSL = intToHSL(attributeList.length);

            return {
              attributes: {
                ...acc.attributes,
                [attribute]:
                  attribute === WEEKS_OF_SUPPLY
                    ? Object.keys(colorsWOS) : attribute === Margin ? Object.keys(colorsDGM)
                      : attributeList
              },
              attributesColors: {
                ...acc.attributesColors,
                [attribute]:
                  attribute === WEEKS_OF_SUPPLY
                    ? colorsWOS : attribute === Margin ? colorsDGM
                      : attributeList.reduce<Record<string, AttributeColor>>(
                        (
                          acc: Record<string, AttributeColor>,
                          value: string,
                          index: number
                        ) => ({
                          ...acc,
                          [value]: colorsHSL[index],
                        }),
                        {}
                      )
              }
            };
          },
          { attributes: {}, attributesColors: {}}
        );

        return {
          attributes,
          attributesList,
          attributesColors,
          attributesProducts
        };
      }
    } catch (e) {
      console.log('filterBlockingSelector error: ', e);

      return null;
    }

    return null;
  }
);

export const postSolverSelector = createSelector(
  comparisonSelector,
  (comparison) => comparison.postSolver
);
export const versionForReviewSelector = createSelector(
  comparisonSelector,
  (comparison) => comparison.versionForReview
);
export const currentMetricSelector = createSelector(
  comparisonSelector,
  (comparison) => comparison.current
);
export const jobVersionForReviewSelector = createSelector(
  versionForReviewSelector,
  jobsDoneSelector,
  (reviewVersion, jobsDone) =>
    jobsDone.find((job: Job) => job.job_id === reviewVersion) || jobsDone[0]
);

export const crossMerchSelector = createSelector(
  assortmentApiSelector,
  (assortmentApi) => assortmentApi?.cross_merch
);

export const alertsSelector = createSelector(
  warningsSelector,
  historySelector,
  historyStepSelector,
  (warnings, history, historyStep) => {
    return historyStep === 0 || !history || !history[historyStep]
      ? warnings
      : history[historyStep].alerts;
  },
);

export const skuFilterSelector = createSelector(
  baseAssortmentSelector,
  assortmentApiSelector,
  filterSelector,
  solverOutputSelector,
  forecasteNameByParametersSelector,
  postSolverSelector,
  facingsSelector,
  alertsSelector,
  facingGroupsHistorySelector,
  (
    baseAssortment,
    assortmentApi,
    filter,
    solverOutput,
    forecasteName: ForecastName,
    postSolver,
    facings,
    alerts,
    facingGroup,
  ) => {
    if (solverOutput && baseAssortment && filter) {
      return filter.reduce((acc, value) => {
        switch (value) {
          case Filters.New:
            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => !assortmentApi?.store_default.includes(i.sku))
                .map((product) => product.sku)
            };

          case Filters.DWOFD:
            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => i.corpStatus === 'FD' || i.corpStatus === 'DWO')
                .map((product) => product.sku)
            };

          case Filters.ACTTDSD:
            return {
              ...acc,
              [value]: baseAssortment
                .filter(
                  (i) =>
                    i.corpStatus === 'ACT' ||
                    i.corpStatus === 'TD' ||
                    i.corpStatus === 'SD'
                )
                .map((product) => product.sku)
            };

          case Filters.CrossMerch:
            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => i.crossMerch === 'YES')
                .map((product) => product.sku)
            };

          case Filters.PrimaryFillIssue:
            const mrs =
              solverOutput?.solver_input?.parameters.minimum_reserved_stock ||
              0;

            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => {
                  const optimizedBincap = Number(i?.optimized?.bincap);
                  const bincap = postSolver ? optimizedBincap : i.bincap;

                  return bincap < i.dmoq + mrs * i[forecasteName];
                })
                .map((product) => product.sku)
            };

          case Filters.MultipleHomes:
            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => i.permanentCount > 1)
                .map((product) => product.sku)
            };

          case Filters.MoreThanOne:
            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => facings[i.sku] && facings[i.sku] > 1)
                .map((product) => product.sku)
            };

          case Filters.AssortmentSweep: {
            const assortmentSweep = alerts.filter(a => a.warningType === 'assortment_sweep_warnings');
            const assortmentSweepAlert = assortmentSweep.length ? assortmentSweep[0].data as Array<AssortmentSweepWarnings> : [];
            const assortmentSweepProducts = assortmentSweepAlert.map(a => a.product_num);

            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => assortmentSweepProducts.includes(i.sku))
                .map((product) => product.sku)
            };
          }

          case Filters.ExtendedAssortment:
            return {
              ...acc,
              [value]: baseAssortment
                .filter((i) => i.isExtendedAssortment === 'YES')
                .map((product) => product.sku)
            };

          case Filters.TopRowDuplicationHeight:
            const allowedProductNums = getProductNumsFitMirroringHeight(solverOutput?.solver_input?.parameters.mirroring_height, facingGroup);

            return {
              ...acc,
              [value]: baseAssortment
                .filter(({ sku }) => allowedProductNums.includes(sku))
                .map(({ sku }) => sku)
            };

          case Filters.ZeroCommitment:

            return {
              ...acc,
              [value]: baseAssortment
                .filter(( item ) => item.totalCommitments === 0)
                .map(({ sku }) => sku)
            };

          default:
            return {};
        }
      }, {} as { [key: string]: string[] });
    }

    return {};
  }
);

export const fixtureFilteredSelector = createSelector(
  skuFilterSelector,
  filterSelector,
  facingGroupsHistorySelector,
  (filteredSkus, filters, facingGroup) => {
    return filters.reduce((acc, filter) => {
      const facingIds = facingGroup.reduce((facingsIds, facing) => {
        if (
          filteredSkus[filter].includes(facing.product_num) &&
          !facingsIds.includes(facing.located_fixture_id)
        ) {
          return [...facingsIds, facing.located_fixture_id] as number[];
        }

        return facingsIds;
      }, [] as number[]);

      return {
        ...acc,
        [filter]: facingIds
      };
    }, {} as { [key: string]: number[] });
  }
);

export const pegboardInitialConfigSelector = createSelector(
  pegboardsSelector,
  aisleGeometrySelector,
  (pegboards, aisleGeometry) => {
    let startHoleX: number = PEGBOARD_X_OFFSET;
    let startHoleY: number = PEGBOARD_Y_OFFSET;
    let obstructed: Array<Array<number>> = [];

    if (aisleGeometry) {
      const [setup] = aisleGeometry.blocks;
      const pegboardGroup = setup.pegboard_config_id
        ? Object.keys(pegboards).reduce((acc, cur) => {
          const [pegbord] = pegboards[cur].filter(p => p.pegboardConfigId === setup.pegboard_config_id);

          if (pegbord) acc = pegbord;

          return acc;
        }, {} as VendorPegbordItem)
        : Object.keys(pegboards).reduce((acc, cur) => {
          const [vendor] = cur.split(',');

          if (vendor.toLocaleLowerCase() === DAFULT_VENDOR_PEGBOARD) {
            acc.push(...pegboards[cur]);

            return acc;
          }

          return acc;
        }, [] as Array<VendorPegbordItem>)[0];

      if (
        has(pegboardGroup, 'startHoleX') &&
        has(pegboardGroup, 'startHoleY') &&
        has(pegboardGroup, 'obstructedRows')
      ) {
        startHoleX = parseFloat(pegboardGroup.startHoleX);
        startHoleY = parseFloat(pegboardGroup.startHoleY);
        obstructed = groupByObstructedRows(pegboardGroup.obstructedRows);
      }

      return {
        startHoleX,
        startHoleY,
        obstructed
      };
    }

    return {
      startHoleX,
      startHoleY,
      obstructed,
    };
  }
);

export const splicerCoordinatesSelector = createSelector(
  pegboardInitialConfigSelector,
  fixturePegboardSelector,
  (pegboardInitialConfig, pegboards) => {
    const { obstructed, startHoleY } = pegboardInitialConfig;
    const splicerCoordinates = obstructed.reduce((acc, val) => {
      if (val.length === 1) return acc;

      const y1 = val[0];
      const y2 = val[val.length - 1];
      const splicerCoordinates = pegboards.map((item) => ({
        id: `splicer-${item.id}-${y1}`,
        x1: item.x1,
        x2: item.x2,
        y1: y1 + startHoleY + item.y1 - 1,
        y2: y2 + startHoleY + item.y1 - 1,
      }));

      return acc.concat(splicerCoordinates);
    }, [] as Array<SplicerPosition>);

    const splicerIds = splicerCoordinates.map((item) => item.id);

    return {
      splicerCoordinates,
      splicerIds,
    };
  }
);

export const canAddOneFacingSelector = (
  contextMenuData: ContextMenuData | null
) =>
  createSelector(
    labelsSelector,
    facingGroupsByIdsSelector,
    locatedFixturesHistorySelector,
    locatedFixturesByIdSelector,
    itemsCoordinatesSelector,
    splicerCoordinatesSelector,
    (
      labels,
      facingGroupsByIds,
      locatedFixtures,
      locatedFixturesById,
      itemsCoordinates,
      splicerCoordinates
    ) => {
      const options: {
        topFacing: FacingGroup | null;
        rightFacing: FacingGroup | null;
        bottomFacing: FacingGroup | null;
        leftFacing: FacingGroup | null;
      } = {
        topFacing: null,
        rightFacing: null,
        bottomFacing: null,
        leftFacing: null
      };

      try {
        if (contextMenuData) {
          const { id } = contextMenuData;
          const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(item => item.id !== id);
          const facing = facingGroupsByIds[id];
          const locatedFixture = locatedFixturesById[facing.located_fixture_id];
          const locatedFixtureSpaces = locatedFixtures.map((locatedFixture) => ({
            x1: locatedFixture.x,
            y1: locatedFixture.y,
            x2: locatedFixture.x + locatedFixture.width,
            y2: locatedFixture.y + locatedFixture.height,
            id: locatedFixture.id
          }));

          const locatedFixtureSpace = {
            x1: locatedFixture.x,
            y1: locatedFixture.y,
            x2: locatedFixture.x + locatedFixture.width,
            y2: locatedFixture.y + locatedFixture.height
          };

          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
          );

          const isPeg =
            facing.packaging.peggable &&
            facing.packaging.pegholes.length > 0 &&
            facing.packaging.peggable &&
            isPeggableFixture(facing.fixtureType);
          const isESL = isPeg && labels === LabelType.ESL;
          const extraEslHeight = isESL ? EXTRA_ESL_HEIGHT_OUTSIDE_FACING : 0;
          const peghole = facing?.packaging?.pegholes[0];

          const facingWidth = facing.packaging.width * facing.horizontal_facings;

          const currentFacing = {
            x1: facing.x,
            y1: facing.y,
            x2: facing.x + facingWidth,
            y2: facing.y + facingHeight + extraEslHeight
          };

          const roundFacingHeight = Math.ceil(facingHeight);
          const roundFacingWidth = Math.ceil(facingWidth);

          // Prepare facing coordinates blocks

          const ghostTopFacing = {
            x1: currentFacing.x1,
            y1: currentFacing.y1 + roundFacingHeight + EPSILON + extraEslHeight,
            x2: currentFacing.x2,
            y2: currentFacing.y2 + roundFacingHeight + EPSILON + extraEslHeight
          };

          const ghostRightFacing = {
            x1: currentFacing.x1 + roundFacingWidth + EPSILON,
            y1: currentFacing.y1,
            x2: currentFacing.x2 + roundFacingWidth + EPSILON,
            y2: currentFacing.y2
          };

          const ghostBottomFacing = {
            x1: currentFacing.x1,
            y1: currentFacing.y1 - roundFacingHeight - EPSILON - extraEslHeight,
            x2: currentFacing.x2,
            y2: currentFacing.y2 - roundFacingHeight - EPSILON - extraEslHeight
          };

          const ghostLeftFacing = {
            x1: currentFacing.x1 - roundFacingWidth - EPSILON,
            y1: currentFacing.y1,
            x2: currentFacing.x2 - roundFacingWidth - EPSILON,
            y2: currentFacing.y2
          };

          // Check occupied facings

          const itemsOccupiedTopFacing = itemsCoordinatesWithoutSelf.some((item) => {
            const left = Math.max(ghostTopFacing.x1, item.x1);
            const bottom = Math.max(ghostTopFacing.y1, item.y1);
            const top = Math.min(ghostTopFacing.y2, item.y2);
            const right = Math.min(ghostTopFacing.x2, item.x2);

            return right > left && top > bottom;
          });

          const itemsOccupiedRightFacing = itemsCoordinatesWithoutSelf.some((item) => {
            const left = Math.max(ghostRightFacing.x1, item.x1);
            const bottom = Math.max(ghostRightFacing.y1, item.y1);
            const top = Math.min(ghostRightFacing.y2, item.y2);
            const right = Math.min(ghostRightFacing.x2, item.x2);

            return right > left && top > bottom;
          });

          const itemsOccupiedBottomFacing = itemsCoordinatesWithoutSelf.some((item) => {
            const left = Math.max(ghostBottomFacing.x1, item.x1);
            const bottom = Math.max(ghostBottomFacing.y1, item.y1);
            const top = Math.min(ghostBottomFacing.y2, item.y2);
            const right = Math.min(ghostBottomFacing.x2, item.x2);

            return right > left && top > bottom;
          });

          const itemsOccupiedLeftFacing = itemsCoordinatesWithoutSelf.some((item) => {
            const left = Math.max(ghostLeftFacing.x1, item.x1);
            const bottom = Math.max(ghostLeftFacing.y1, item.y1);
            const top = Math.min(ghostLeftFacing.y2, item.y2);
            const right = Math.min(ghostLeftFacing.x2, item.x2);

            return right > left && top > bottom;
          });

          // Check occupied fixtures

          const fixturesOccupiedRightFacing = locatedFixtureSpaces.some(
            (locatedFixture) => {
              const left = Math.max(ghostRightFacing.x1, locatedFixture.x1);
              const bottom = Math.max(ghostRightFacing.y1, locatedFixture.y1);
              const top = Math.min(ghostRightFacing.y2, locatedFixture.y2);
              const right = Math.min(ghostRightFacing.x2, locatedFixture.x2);

              return (
                right > left &&
                top > bottom &&
                locatedFixture.id !== facing.located_fixture_id
              );
            }
          );

          const fixturesOccupiedBottomFacing = locatedFixtureSpaces.some(
            (locatedFixture) => {
              const left = Math.max(ghostBottomFacing.x1, locatedFixture.x1);
              const bottom = Math.max(ghostBottomFacing.y1, locatedFixture.y1);
              const top = Math.min(ghostBottomFacing.y2, locatedFixture.y2);
              const right = Math.min(ghostBottomFacing.x2, locatedFixture.x2);

              return (
                right > left &&
                top > bottom &&
                locatedFixture.id !== facing.located_fixture_id
              );
            }
          );

          const fixturesOccupiedLeftFacing = locatedFixtureSpaces.some(
            (locatedFixture) => {
              const left = Math.max(ghostLeftFacing.x1, locatedFixture.x1);
              const bottom = Math.max(ghostLeftFacing.y1, locatedFixture.y1);
              const top = Math.min(ghostLeftFacing.y2, locatedFixture.y2);
              const right = Math.min(ghostLeftFacing.x2, locatedFixture.x2);

              return (
                right > left &&
                top > bottom &&
                locatedFixture.id !== facing.located_fixture_id
              );
            }
          );

          const fixturesOccupiedTopFacing = locatedFixtureSpaces.some(
            (locatedFixture) => {
              const left = Math.max(ghostTopFacing.x1, locatedFixture.x1);
              const bottom = Math.max(ghostTopFacing.y1, locatedFixture.y1);
              const top = Math.min(ghostTopFacing.y2, locatedFixture.y2);
              const right = Math.min(ghostTopFacing.x2, locatedFixture.x2);

              return (
                right > left &&
                top > bottom &&
                locatedFixture.id !== facing.located_fixture_id
              );
            }
          );

          // Check out of current located fixture

          const ghostTopFacingInFixture =
            ghostTopFacing.x1 > locatedFixtureSpace.x1 &&
            ghostTopFacing.y1 > locatedFixtureSpace.y1 &&
            ghostTopFacing.x2 < locatedFixtureSpace.x2 &&
            ghostTopFacing.y2 < locatedFixtureSpace.y2;

          const ghostRightFacingInFixture =
            ghostRightFacing.x1 > locatedFixtureSpace.x1 &&
            ghostRightFacing.y1 > locatedFixtureSpace.y1 &&
            ghostRightFacing.x2 < locatedFixtureSpace.x2 &&
            ghostRightFacing.y2 < locatedFixtureSpace.y2;

          const ghostBottomFacingInFixture =
            ghostBottomFacing.x1 > locatedFixtureSpace.x1 &&
            ghostBottomFacing.y1 > locatedFixtureSpace.y1 &&
            ghostBottomFacing.x2 < locatedFixtureSpace.x2 &&
            ghostBottomFacing.y2 < locatedFixtureSpace.y2;

          const ghostLeftFacingInFixture =
            ghostLeftFacing.x1 > locatedFixtureSpace.x1 &&
            ghostLeftFacing.y1 > locatedFixtureSpace.y1 &&
            ghostLeftFacing.x2 < locatedFixtureSpace.x2 &&
            ghostLeftFacing.y2 < locatedFixtureSpace.y2;

          if (
            !itemsOccupiedTopFacing &&
            !fixturesOccupiedTopFacing &&
            ghostTopFacingInFixture
          ) {
            const x = ghostTopFacing.x1;
            let y = ghostTopFacing.y1 + EPSILON;

            const pegholeArea = transformPegholes(peghole, facingWidth, 1);
            const pegholeY = y + facingHeight - pegholeArea.centerY;
            const splicer = splicerCoordinates.splicerCoordinates.find(item =>
              round(item.y1, 2) <= round(pegholeY, 2) &&
              round(item.y2, 2) >= round(pegholeY, 2)
            );

            if (splicer) {
              const { y1, y2 } = splicer;
              const roundedY1 = Math.round(y1);
              const roundedY2 = Math.round(y2);
              const roundedCurrentPegholeY = Math.round(pegholeY);
              const isShiftUp = roundedY2 === roundedCurrentPegholeY;
              const isShiftDown = roundedY1 === roundedCurrentPegholeY;
              const totalFacingHeight = roundFacingHeight + EPSILON + extraEslHeight;

              y = isShiftUp ? y + 1 : isShiftDown ? y + 2 : y;
              const ghostTopFacing = {
                x1: currentFacing.x1,
                y1: y,
                x2: currentFacing.x2,
                y2: y + totalFacingHeight
              };
              const itemsOccupiedTopFacing = itemsCoordinatesWithoutSelf.some((item) => {
                const left = Math.max(ghostTopFacing.x1, item.x1);
                const bottom = Math.max(ghostTopFacing.y1, item.y1);
                const top = Math.min(ghostTopFacing.y2, item.y2);
                const right = Math.min(ghostTopFacing.x2, item.x2);

                return right > left && top > bottom;
              });

              if (itemsOccupiedTopFacing) {
                return options;
              }
            }

            options.topFacing = {
              ...facing,
              x,
              y
            };
          }

          if (
            !itemsOccupiedRightFacing &&
            !fixturesOccupiedRightFacing &&
            ghostRightFacingInFixture
          ) {
            const x = ghostRightFacing.x1 + EPSILON;
            const y = ghostRightFacing.y1;

            options.rightFacing = {
              ...facing,
              x,
              y
            };
          }

          if (
            !itemsOccupiedBottomFacing &&
            !fixturesOccupiedBottomFacing &&
            ghostBottomFacingInFixture
          ) {
            const x = ghostBottomFacing.x1;
            let y = ghostBottomFacing.y1 - EPSILON;

            const pegholeArea = transformPegholes(peghole, facingWidth, 1);
            const pegholeY = y + facingHeight - pegholeArea.centerY;
            const splicer = splicerCoordinates.splicerCoordinates.find(item =>
              round(item.y1, 2) <= round(pegholeY, 2) &&
              round(item.y2, 2) >= round(pegholeY, 2)
            );

            if (splicer) {
              const { y1, y2 } = splicer;
              const roundedY1 = Math.round(y1);
              const roundedY2 = Math.round(y2);
              const roundedCurrentPegholeY = Math.round(pegholeY);
              const isShiftUp = roundedY2 === roundedCurrentPegholeY;
              const isShiftDown = roundedY1 === roundedCurrentPegholeY;

              y = isShiftUp ? y - 2 : isShiftDown ? y - 1 : y;
              const totalFacingHeight = roundFacingHeight + EPSILON + extraEslHeight;

              const ghostTopFacing = {
                x1: currentFacing.x1,
                y1: y,
                x2: currentFacing.x2,
                y2: y + totalFacingHeight
              };
              const itemsOccupiedTopFacing = itemsCoordinatesWithoutSelf.some((item) => {
                const left = Math.max(ghostTopFacing.x1, item.x1);
                const bottom = Math.max(ghostTopFacing.y1, item.y1);
                const top = Math.min(ghostTopFacing.y2, item.y2);
                const right = Math.min(ghostTopFacing.x2, item.x2);

                return right > left && top > bottom;
              });

              if (itemsOccupiedTopFacing) {
                return options;
              }
            }

            options.bottomFacing = {
              ...facing,
              x,
              y
            };
          }

          if (
            !itemsOccupiedLeftFacing &&
            !fixturesOccupiedLeftFacing &&
            ghostLeftFacingInFixture
          ) {
            const x = ghostLeftFacing.x1 - EPSILON;
            const y = ghostLeftFacing.y1;

            options.leftFacing = {
              ...facing,
              x,
              y
            };
          }
        }
      } catch (e) {
        return options;
      }

      return options;
    }
  );

export const canRemoveCappedVerticalFacingSelector = (
  contextMenuData: ContextMenuData | null
) => createSelector(facingGroupsByIdsSelector, (facingGroupsByIds) => {
  if (contextMenuData) {
    const { id } = contextMenuData;

    return facingGroupsByIds[id]?.capped_vertical_facings > 0;
  }

  return false;
});

export const canAddCappedVerticalFacingSelector = (
  contextMenuData: ContextMenuData | null
) => createSelector(
  facingGroupsByIdsSelector,
  locatedFixturesByIdSelector,
  itemsCoordinatesSelector,
  fixtureSpaceSelectorByTypesSelector(['SHELF', 'BASKET', 'CROSSBAR']),
  aisleHeightSelector,
  (
    facingGroupsByIds,
    locatedFixturesById,
    itemsCoordinates,
    fixtureSpaceSelectorByTypes,
    aisleHeight
  ) => {
    if (contextMenuData) {
      const { id } = contextMenuData;
      const facingGroup = facingGroupsByIds[id];

      if (facingGroup) {
        const {
          x,
          y,
          horizontal_facings: horizontalFacings,
          vertical_facings: verticalFacings,
          cap_nested: nesting,
          capped_vertical_facings: cappedVertical,
          packaging,
          fixtureType,
          located_fixture_id,
          defaultPackaging,
          orientation_code: orientationCode,
          cappedPackaging: { height: cappedHeight }
        } = facingGroup;
        const { width, height, nested_height: nestedHeight } = packaging;
        const locatedFixture = locatedFixturesById[located_fixture_id];
        const isBasketFacing = fixtureType === 'BASKET';
        const isShelfFacing = fixtureType === 'SHELF';

        if (isBasketFacing || isShelfFacing) {
          const cappedOrientationCode = rotateCappedOrientationFacing(orientationCode);
          const cappedPackaging = orientationFacing(
            cappedOrientationCode,
            defaultPackaging.width,
            defaultPackaging.height,
            defaultPackaging.depth
          );

          if (cappedPackaging.depth > locatedFixture.depth) return null;

          const currentItemHeight = getFacingHeight(
            height,
            verticalFacings,
            nesting as Nesting,
            nestedHeight,
            cappedHeight,
            cappedVertical,
          );

          const ghostFacingCapped: AngularCoordinates = {
            x1: x,
            y1: y + currentItemHeight,
            x2: x + width * horizontalFacings,
            y2: y + currentItemHeight + cappedPackaging.height
          };

          if (isBasketFacing) {
            const locatedFixtureHeightWithGrill =
              locatedFixture.y + locatedFixture.grille_height;

            if (ghostFacingCapped.y1 > locatedFixtureHeightWithGrill) return null;
          }

          if (ghostFacingCapped.y2 > aisleHeight) return null;

          const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(
            (item) => item.id !== id
          );

          const placeOccupiedCapped = itemsCoordinatesWithoutSelf.some(
            (item) => {
              const left = Math.max(ghostFacingCapped.x1, item.x1);
              const bottom = Math.max(ghostFacingCapped.y1, item.y1);
              const top = Math.min(ghostFacingCapped.y2, item.y2);
              const right = Math.min(ghostFacingCapped.x2, item.x2);

              return right > left && top > bottom;
            }
          );

          if (placeOccupiedCapped) return null;

          const overlapFixturesRegular = fixtureSpaceSelectorByTypes.some(fixture => {
            const left = Math.max(ghostFacingCapped.x1, fixture.x1);
            const bottom = Math.max(ghostFacingCapped.y1, fixture.y1);
            const top = Math.min(ghostFacingCapped.y2, fixture.y2);
            const right = Math.min(ghostFacingCapped.x2, fixture.x2);

            return right > left && top > bottom;
          });

          if (overlapFixturesRegular) return null;

          const newCappedHorizontalFacongs = horizontalFacings;
          const newCappedVerticalFacings = cappedVertical + 1;
          const newCappedDepthFacings = Math.floor(locatedFixture.depth / cappedPackaging.depth);

          const newFacing = {
            ...facingGroup,
            id: generateUniqueId(),
            cap_nested: Nesting.YCapped,
            capped_orientation_code: cappedOrientationCode,
            capped_horizontal_facings: newCappedHorizontalFacongs,
            capped_vertical_facings: newCappedVerticalFacings,
            capped_depth_facings: newCappedDepthFacings,
          };

          return newFacing;
        }
      }
    }

    return null;
  }
);

export const canAddVerticalFacingSelector = (
  contextMenuData: ContextMenuData | null
) => createSelector(
  facingGroupsByIdsSelector,
  locatedFixturesByIdSelector,
  fixtureSpacesSelector,
  itemsCoordinatesSelector,
  fixtureSpaceSelectorByTypesSelector(['SHELF', 'BASKET', 'CROSSBAR']),
  (
    facingGroupsByIds,
    locatedFixturesById,
    fixturesSpaces,
    itemsCoordinates,
    fixtureSpaceSelectorByTypes
  ) => {
    const options = {
      canAddRegularVerticalFacing: false,
      canRemoveRegularVerticalFacing: false,
    };

    if (contextMenuData) {
      const { id } = contextMenuData;
      const facingGroupsById = facingGroupsByIds[id];

      if (!facingGroupsById) return options;

      const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(
        (item) => item.id !== id
      );
      const facingGroup = facingGroupsByIds[id];
      const {
        x,
        y,
        horizontal_facings: h,
        vertical_facings: v,
        cap_nested: nesting,
        capped_vertical_facings: cappedVertical,
        packaging,
        fixtureType,
        located_fixture_id,
        cappedPackaging: { height: cappedHeight },
      } = facingGroup;
      const { width, height, nested_height: nestedHeight } = packaging;
      const locatedFixture = locatedFixturesById[located_fixture_id];

      const isCrossbarFacing = fixtureType === 'CROSSBAR';
      const isBasketFacing = fixtureType === 'BASKET';

      if (isCrossbarFacing) return options;

      const currentItemHeight = getFacingHeight(
        height,
        v,
        nesting as Nesting,
        nestedHeight,
        cappedHeight,
        cappedVertical
      );
      const occupiedFixtures = fixturesSpaces.filter(
        ({ x1, x2, y1, y2 }) =>
          x <= x2 &&
          x + width * h >= x1 &&
          y + currentItemHeight <= y2 &&
          y >= y1
      );
      const [occupiedFixture] = occupiedFixtures;
      const maxFixtureHeight = occupiedFixture?.y2 - occupiedFixture?.y1;

      options.canRemoveRegularVerticalFacing = v > 1;
      let canAddToBasketShelf = true;

      const ghostFacingRegular = {
        x1: x,
        y1: y + currentItemHeight,
        x2: x + width * h,
        y2: y + currentItemHeight + height
      };

      if (isBasketFacing) {
        const locatedFixtureHeightWithGrill =
          locatedFixture.y + locatedFixture.grille_height;

        if (ghostFacingRegular.y1 > locatedFixtureHeightWithGrill) {
          canAddToBasketShelf = false;
        }
      }

      const placeOccupiedRegular = itemsCoordinatesWithoutSelf.some(
        (item) => {
          const left = Math.max(ghostFacingRegular.x1, item.x1);
          const bottom = Math.max(ghostFacingRegular.y1, item.y1);
          const top = Math.min(ghostFacingRegular.y2, item.y2);
          const right = Math.min(ghostFacingRegular.x2, item.x2);

          return right > left && top > bottom;
        }
      );

      const overlapFixturesRegular = fixtureSpaceSelectorByTypes.some(fixture => {
        const left = Math.max(ghostFacingRegular.x1, fixture.x1);
        const bottom = Math.max(ghostFacingRegular.y1, fixture.y1);
        const top = Math.min(ghostFacingRegular.y2, fixture.y2);
        const right = Math.min(ghostFacingRegular.x2, fixture.x2);

        return right > left && top > bottom;
      });

      options.canAddRegularVerticalFacing =
        currentItemHeight + height < maxFixtureHeight &&
        !placeOccupiedRegular &&
        !overlapFixturesRegular &&
        canAddToBasketShelf;
    }

    return options;
  }
);

export const canAddHorizontalFacingSelector = (
  contextMenuData: ContextMenuData | null
) =>
  createSelector(
    fixtureSpacesSelector,
    fixtureItemSpaceSelector,
    fixtureCrossbarSelector,
    fixtureFlatbarSelector,
    facingGroupsByIdsSelector,
    itemsCoordinatesSelector,
    locatedFixturesByIdSelector,
    (
      fixturesSpaces,
      fixtureItemSpace,
      fixturesCrossbar,
      fixturesFlatbar,
      facingGroupsByIds,
      itemsCoordinates,
      locatedFixturesById
    ) => {
      try {
        if (contextMenuData) {
          const { id } = contextMenuData;
          const selectedFacingGroup = facingGroupsByIds[id];
          const selectedLocatedFixture = locatedFixturesById[selectedFacingGroup.located_fixture_id];
          const selectedTopPosiotionLocatedFixture = selectedLocatedFixture.y + selectedLocatedFixture.height;
          const isCrossbar = selectedFacingGroup.fixtureType === FIXTURE_TYPES.CROSSBAR;
          const isFlatbar = selectedFacingGroup.fixtureType === FIXTURE_TYPES.FLATBAR;
          const isWaterfall = isCrossbar && selectedLocatedFixture.z > 0;
          const isDefaultFacing =
            selectedFacingGroup.fixtureType === 'SHELF' ||
            selectedFacingGroup.fixtureType === 'BASKET';

          const facingHeight = getFacingHeight(
            selectedFacingGroup.packaging.height,
            selectedFacingGroup.vertical_facings,
            selectedFacingGroup.cap_nested,
            selectedFacingGroup.packaging.nested_height,
            selectedFacingGroup.cappedPackaging.height,
            selectedFacingGroup.capped_vertical_facings
          );

          const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(facing => {
            const facingGroup = facingGroupsByIds[facing.id];
            const locatedFixture = locatedFixturesById[facingGroup.located_fixture_id];
            const isSelf = facingGroup.id === selectedFacingGroup.id;

            if (isSelf) {
              return false;
            }

            if (isWaterfall || isFlatbar) {
              const isSameZ = locatedFixture.z === selectedLocatedFixture.z;

              return isSameZ;
            }

            return true;
          });

          if (isDefaultFacing && selectedLocatedFixture) {
            const ghostFacingRight = {
              x1: selectedFacingGroup.x,
              y1: selectedFacingGroup.y,
              x2: selectedFacingGroup.x + selectedFacingGroup.packaging.width * (selectedFacingGroup.horizontal_facings + 1),
              y2: selectedFacingGroup.y + facingHeight
            };

            const occupiedFacingsRight = itemsCoordinatesWithoutSelf.filter(facingGroup => {
              const left = Math.max(ghostFacingRight.x1, facingGroup.x1);
              const bottom = Math.max(ghostFacingRight.y1, facingGroup.y1);
              const top = Math.min(ghostFacingRight.y2, facingGroup.y2);
              const right = Math.min(ghostFacingRight.x2, facingGroup.x2);

              return right > left && top > bottom;
            });

            const occupiedFixturesRight = fixtureItemSpace.filter(fixture => {
              const left = Math.max(ghostFacingRight.x1, fixture.x1);
              const bottom = Math.max(ghostFacingRight.y1, fixture.y1);
              const top = Math.min(ghostFacingRight.y2, fixture.y2);
              const right = Math.min(ghostFacingRight.x2, fixture.x2);

              return right > left && top > bottom;
            });

            const occupiedFixturesCrossbarRight = [...fixturesFlatbar, ...fixturesCrossbar].filter(fixture => {
              const left = Math.max(ghostFacingRight.x1, fixture.x1);
              const bottom = Math.max(ghostFacingRight.y1, fixture.y1);
              const top = Math.min(ghostFacingRight.y2, fixture.y2);
              const right = Math.min(ghostFacingRight.x2, fixture.x2);

              return right > left && top > bottom;
            });

            const occupiedFixturesSpacesRight = fixturesSpaces.filter(fixture => {
              const left = Math.max(ghostFacingRight.x1, fixture.x1);
              const bottom = Math.max(ghostFacingRight.y1, fixture.y1);
              const top = Math.min(ghostFacingRight.y2, fixture.y2);
              const right = Math.min(ghostFacingRight.x2, fixture.x2);

              return right > left && top > bottom;
            });

            const boundaryRight = Math.max(...occupiedFixturesSpacesRight.map(fixture => fixture.x2));

            const isOccupiedFacingsRight = Boolean(occupiedFacingsRight.length);
            const isOccupiedFixturesRight = Boolean(occupiedFixturesRight.length);
            const isOccupiedFixturesCrossbarRight = Boolean(occupiedFixturesCrossbarRight.length);
            const isOutOfBoundaryFixturesSpacesRight =
              ghostFacingRight.x2 > boundaryRight ||
              ghostFacingRight.x1 < 0;
            const differentOccupiedFixturesSpacesRight = occupiedFixturesSpacesRight.filter(
              fixture => {
                const occupiedLocatedFixture = locatedFixturesById[fixture.id];
                const occupiedTopPosiotionLocatedFixture = occupiedLocatedFixture.y + occupiedLocatedFixture.height;

                return selectedTopPosiotionLocatedFixture !== occupiedTopPosiotionLocatedFixture;
              }
            );
            const isDifferentFixturesSpacesRight = Boolean(differentOccupiedFixturesSpacesRight.length);

            const isIdenticalFixturesRight = checkIsIdenticalFixtures(fixturesSpaces, ghostFacingRight, locatedFixturesById);

            const fixtures = [
              ...fixturesSpaces,
              ...fixturesCrossbar,
              ...fixturesFlatbar,
            ];

            if (
              isOccupiedFacingsRight ||
              isOccupiedFixturesRight ||
              isOccupiedFixturesCrossbarRight ||
              isOutOfBoundaryFixturesSpacesRight ||
              isDifferentFixturesSpacesRight ||
              !isIdenticalFixturesRight
            ) {
              const ghostFacingLeft = {
                x1: selectedFacingGroup.x - selectedFacingGroup.packaging.width,
                y1: selectedFacingGroup.y,
                x2: selectedFacingGroup.x + selectedFacingGroup.packaging.width * selectedFacingGroup.horizontal_facings,
                y2: selectedFacingGroup.y + facingHeight
              };

              const occupiedFacingsLeft = itemsCoordinatesWithoutSelf.filter(facingGroup => {
                const left = Math.max(ghostFacingLeft.x1, facingGroup.x1);
                const bottom = Math.max(ghostFacingLeft.y1, facingGroup.y1);
                const top = Math.min(ghostFacingLeft.y2, facingGroup.y2);
                const right = Math.min(ghostFacingLeft.x2, facingGroup.x2);

                return right > left && top > bottom;
              });

              const occupiedFixturesLeft = fixtureItemSpace.filter(fixture => {
                const left = Math.max(ghostFacingLeft.x1, fixture.x1);
                const bottom = Math.max(ghostFacingLeft.y1, fixture.y1);
                const top = Math.min(ghostFacingLeft.y2, fixture.y2);
                const right = Math.min(ghostFacingLeft.x2, fixture.x2);

                return right > left && top > bottom;
              });

              const occupiedFixturesCrossbarLeft = [...fixturesFlatbar, ...fixturesCrossbar].filter(fixture => {
                const left = Math.max(ghostFacingLeft.x1, fixture.x1);
                const bottom = Math.max(ghostFacingLeft.y1, fixture.y1);
                const top = Math.min(ghostFacingLeft.y2, fixture.y2);
                const right = Math.min(ghostFacingLeft.x2, fixture.x2);

                return right > left && top > bottom;
              });

              const occupiedFixturesSpacesLeft = fixturesSpaces.filter(fixture => {
                const left = Math.max(ghostFacingLeft.x1, fixture.x1);
                const bottom = Math.max(ghostFacingLeft.y1, fixture.y1);
                const top = Math.min(ghostFacingLeft.y2, fixture.y2);
                const right = Math.min(ghostFacingLeft.x2, fixture.x2);

                return right > left && top > bottom;
              });

              const boundaryLeft = Math.max(...occupiedFixturesSpacesLeft.map(fixture => fixture.x2));

              const isOccupiedFacingsLeft = Boolean(occupiedFacingsLeft.length);
              const isOccupiedFixturesLeft = Boolean(occupiedFixturesLeft.length);
              const isOccupiedFixturesCrossbarLeft = Boolean(occupiedFixturesCrossbarLeft.length);
              const isOutOfBoundaryFixturesSpacesLeft =
                ghostFacingLeft.x2 > boundaryLeft ||
                ghostFacingLeft.x1 < 0;
              const differentOccupiedFixturesSpacesLeft = occupiedFixturesSpacesLeft.filter(
                fixture => {
                  const occupiedLocatedFixture = locatedFixturesById[fixture.id];
                  const occupiedTopPosiotionLocatedFixture = occupiedLocatedFixture.y + occupiedLocatedFixture.height;

                  return selectedTopPosiotionLocatedFixture !== occupiedTopPosiotionLocatedFixture;
                }
              );
              const isDifferentFixturesSpacesLeft = Boolean(differentOccupiedFixturesSpacesLeft.length);

              if (
                isOccupiedFacingsLeft ||
                isOccupiedFixturesLeft ||
                isOccupiedFixturesCrossbarLeft ||
                isOutOfBoundaryFixturesSpacesLeft ||
                isDifferentFixturesSpacesLeft
              ) {
                let shiftX = 0;

                if (isOutOfBoundaryFixturesSpacesRight) {
                  shiftX = ghostFacingRight.x2 - boundaryRight;
                }

                if (isOccupiedFixturesRight) {
                  const occupiedFixtureRight = occupiedFixturesRight.sort((a, b) => a.x1 - b.x1)[0];

                  shiftX = ghostFacingRight.x2 - occupiedFixtureRight.x1;
                }

                if (isOccupiedFixturesCrossbarRight) {
                  const occupiedFixtureCrossbarRight = occupiedFixturesCrossbarRight.sort((a, b) => a.x1 - b.x1)[0];

                  shiftX = ghostFacingRight.x2 - occupiedFixtureCrossbarRight.x1;
                }

                if (isDifferentFixturesSpacesRight) {
                  const differentOccupiedFixtureCrossbarRight = differentOccupiedFixturesSpacesRight.sort((a, b) => a.x1 - b.x1)[0];

                  shiftX = ghostFacingRight.x2 - differentOccupiedFixtureCrossbarRight.x1;
                }

                if (isOccupiedFacingsRight) {
                  const occupiedFacingRight = occupiedFacingsRight.sort((a, b) => a.x1 - b.x1)[0];

                  shiftX = ghostFacingRight.x2 - occupiedFacingRight.x1;
                }

                const shiftGhostFacing = {
                  ...ghostFacingRight,
                  x1: ghostFacingRight.x1 - shiftX - EPSILON,
                  x2: ghostFacingRight.x2 - shiftX - EPSILON,
                };

                const shiftOccupiedFacings = itemsCoordinatesWithoutSelf.filter(facingGroup => {
                  const left = Math.max(shiftGhostFacing.x1, facingGroup.x1);
                  const bottom = Math.max(shiftGhostFacing.y1, facingGroup.y1);
                  const top = Math.min(shiftGhostFacing.y2, facingGroup.y2);
                  const right = Math.min(shiftGhostFacing.x2, facingGroup.x2);

                  return right > left && top > bottom;
                });

                const shiftOccupiedFixtures = fixtureItemSpace.filter(fixture => {
                  const left = Math.max(shiftGhostFacing.x1, fixture.x1);
                  const bottom = Math.max(shiftGhostFacing.y1, fixture.y1);
                  const top = Math.min(shiftGhostFacing.y2, fixture.y2);
                  const right = Math.min(shiftGhostFacing.x2, fixture.x2);

                  return right > left && top > bottom;
                });

                const shiftOccupiedFixturesCrossbar = [...fixturesFlatbar, ...fixturesCrossbar].filter(fixture => {
                  const left = Math.max(shiftGhostFacing.x1, fixture.x1);
                  const bottom = Math.max(shiftGhostFacing.y1, fixture.y1);
                  const top = Math.min(shiftGhostFacing.y2, fixture.y2);
                  const right = Math.min(shiftGhostFacing.x2, fixture.x2);

                  return right > left && top > bottom;
                });

                const shiftOccupiedFixturesSpaces = fixturesSpaces.filter(fixture => {
                  const left = Math.max(shiftGhostFacing.x1, fixture.x1);
                  const bottom = Math.max(shiftGhostFacing.y1, fixture.y1);
                  const top = Math.min(shiftGhostFacing.y2, fixture.y2);
                  const right = Math.min(shiftGhostFacing.x2, fixture.x2);

                  return right > left && top > bottom;
                });

                const shiftBoundary = Math.max(...shiftOccupiedFixturesSpaces.map(fixture => fixture.x2));

                const isSfiftOccupiedFacings = Boolean(shiftOccupiedFacings.length);
                const isSfiftOccupiedFixtures = Boolean(shiftOccupiedFixtures.length);
                const isSfiftOccupiedFixturesCrossbar = Boolean(shiftOccupiedFixturesCrossbar.length);
                const isSfiftOutOfBoundaryFixturesSpaces =
                  shiftGhostFacing.x2 > shiftBoundary ||
                  shiftGhostFacing.x1 < 0;
                const differentShiftOccupiedFixturesSpaces = shiftOccupiedFixturesSpaces.filter(
                  fixture => {
                    const occupiedLocatedFixture = locatedFixturesById[fixture.id];
                    const occupiedTopPosiotionLocatedFixture = occupiedLocatedFixture.y + occupiedLocatedFixture.height;

                    return selectedTopPosiotionLocatedFixture !== occupiedTopPosiotionLocatedFixture;
                  }
                );
                const isShiftDifferentFixturesSpaces = Boolean(differentShiftOccupiedFixturesSpaces.length);

                if (
                  isSfiftOccupiedFacings ||
                  isSfiftOccupiedFixtures ||
                  isSfiftOccupiedFixturesCrossbar ||
                  isSfiftOutOfBoundaryFixturesSpaces ||
                  isShiftDifferentFixturesSpaces
                ) {
                  return null;
                }

                const isIdenticalFixtures = checkIsIdenticalFixtures(fixturesSpaces, shiftGhostFacing, locatedFixturesById);

                if (!isIdenticalFixtures) {
                  return null;
                }

                return {
                  ...selectedFacingGroup,
                  x: shiftGhostFacing.x1,
                  segment: Math.trunc(shiftGhostFacing.x1 / SEGMENT_WIDTH) + 1,
                  located_fixture_id: getFixtureIdByXCoordinate(shiftGhostFacing.x1, fixtures, selectedLocatedFixture),
                  id: generateUniqueId(),
                  horizontal_facings: selectedFacingGroup.horizontal_facings + 1,
                };
              }

              const isIdenticalFixtures = checkIsIdenticalFixtures(fixturesSpaces, ghostFacingLeft, locatedFixturesById);

              if (!isIdenticalFixtures) {
                return null;
              }

              return {
                ...selectedFacingGroup,
                x: ghostFacingLeft.x1,
                segment: Math.trunc(ghostFacingLeft.x1 / SEGMENT_WIDTH) + 1,
                located_fixture_id: getFixtureIdByXCoordinate(ghostFacingLeft.x1, fixtures, selectedLocatedFixture),
                id: generateUniqueId(),
                horizontal_facings: selectedFacingGroup.horizontal_facings + 1,
              };
            }

            return {
              ...selectedFacingGroup,
              x: ghostFacingRight.x1,
              id: generateUniqueId(),
              horizontal_facings: selectedFacingGroup.horizontal_facings + 1,
            };
          }
        }
      } catch (e) {
        return null;
      }

      return null;
    }
  );

export const canAddCrossbarFacingSelector = (
  contextMenuData: ContextMenuData | null
) =>
  createSelector(
    fixtureSpacesSelector,
    fixtureItemSpaceSelector,
    fixtureCrossbarSelector,
    fixtureFlatbarSelector,
    facingGroupsByIdsSelector,
    itemsCoordinatesSelector,
    locatedFixturesByIdSelector,
    (
      fixturesSpaces,
      fixtureItemSpace,
      fixturesCrossbar,
      fixturesFlatbar,
      facingGroupsByIds,
      itemsCoordinates,
      locatedFixturesById,
    ) => {
      try {
        if (contextMenuData) {
          const { id } = contextMenuData;
          const selectedFacingGroup = facingGroupsByIds[id];
          const isPegFacing =
            selectedFacingGroup.fixtureType === 'CROSSBAR' ||
            selectedFacingGroup.fixtureType === 'FLATBAR';
          const selectedLocatedFixture = locatedFixturesById[selectedFacingGroup.located_fixture_id];

          if (isPegFacing && selectedLocatedFixture) {
            const isCrossbar = selectedFacingGroup.fixtureType === FIXTURE_TYPES.CROSSBAR;
            const isFlatbar = selectedFacingGroup.fixtureType === FIXTURE_TYPES.FLATBAR;
            const isWaterfall = isCrossbar && selectedLocatedFixture.z > 0;
            const facingHeight = getFacingHeight(
              selectedFacingGroup.packaging.height,
              selectedFacingGroup.vertical_facings,
              selectedFacingGroup.cap_nested,
              selectedFacingGroup.packaging.nested_height,
              selectedFacingGroup.cappedPackaging.height,
              selectedFacingGroup.capped_vertical_facings
            );

            const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(facing => {
              const facingGroup = facingGroupsByIds[facing.id];
              const locatedFixture = locatedFixturesById[facingGroup.located_fixture_id];
              const isSelf = facingGroup.id === selectedFacingGroup.id;

              if (isSelf) {
                return false;
              }

              if (isWaterfall || isFlatbar) {
                const isSameZ = locatedFixture.z === selectedLocatedFixture.z;

                return isSameZ;
              }

              return true;
            });

            const selectedFacingBoundary = {
              x1: selectedFacingGroup.x,
              y1: selectedFacingGroup.y,
              x2: selectedFacingGroup.x + selectedFacingGroup.packaging.width,
              y2: selectedFacingGroup.y + facingHeight,
            };

            const ghostFacingRight = {
              x1: selectedFacingBoundary.x2 + HORIZONTAL_SPACING,
              y1: selectedFacingBoundary.y1,
              x2: selectedFacingBoundary.x2 + selectedFacingGroup.packaging.width + HORIZONTAL_SPACING,
              y2: selectedFacingBoundary.y2
            };

            const occupiedFacingsRight = itemsCoordinatesWithoutSelf.filter(facingGroup => {
              const left = Math.max(ghostFacingRight.x1, facingGroup.x1);
              const bottom = Math.max(ghostFacingRight.y1, facingGroup.y1);
              const top = Math.min(ghostFacingRight.y2, facingGroup.y2);
              const right = Math.min(ghostFacingRight.x2, facingGroup.x2);

              return right > left && top > bottom;
            });

            const occupiedFixturesRight = fixtureItemSpace.filter(fixture => {
              const left = Math.max(ghostFacingRight.x1, fixture.x1);
              const bottom = Math.max(ghostFacingRight.y1, fixture.y1);
              const top = Math.min(ghostFacingRight.y2, fixture.y2);
              const right = Math.min(ghostFacingRight.x2, fixture.x2);

              return right > left && top > bottom;
            });

            const occupiedFixturesCrossbarRight = (isFlatbar ? fixturesFlatbar : fixturesCrossbar).filter(fixture => {
              const sameSituated = selectedLocatedFixture.y === fixture.y1 &&
                selectedLocatedFixture.y + selectedLocatedFixture.height === fixture.y2;
              const left = Math.max(ghostFacingRight.x1, fixture.x1);
              const bottom = Math.max(ghostFacingRight.y1, fixture.y1);
              const top = Math.min(ghostFacingRight.y2, fixture.y2);
              const right = Math.min(ghostFacingRight.x2, fixture.x2);

              return right > left && top > bottom && sameSituated;
            });

            const isOccupiedFacingsRight = Boolean(occupiedFacingsRight.length);
            const isOccupiedFixturesRight = Boolean(occupiedFixturesRight.length);
            const boundaryRight = Math.max(...occupiedFixturesCrossbarRight.map(fixture => fixture.x2));
            const isOutOfBoundaryFixturesCrossbarRight =
              ghostFacingRight.x2 > boundaryRight ||
              ghostFacingRight.x1 < 0;

            const fixtures = [
              ...fixturesSpaces,
              ...fixturesCrossbar,
              ...fixturesFlatbar,
            ];

            if (
              isOccupiedFacingsRight ||
              isOccupiedFixturesRight ||
              isOutOfBoundaryFixturesCrossbarRight
            ) {
              const ghostFacingLeft = {
                x1: selectedFacingBoundary.x1 - selectedFacingGroup.packaging.width - HORIZONTAL_SPACING,
                y1: selectedFacingBoundary.y1,
                x2: selectedFacingBoundary.x1 - HORIZONTAL_SPACING,
                y2: selectedFacingBoundary.y2
              };

              const occupiedFacingsLeft = itemsCoordinatesWithoutSelf.filter(facingGroup => {
                const left = Math.max(ghostFacingLeft.x1, facingGroup.x1);
                const bottom = Math.max(ghostFacingLeft.y1, facingGroup.y1);
                const top = Math.min(ghostFacingLeft.y2, facingGroup.y2);
                const right = Math.min(ghostFacingLeft.x2, facingGroup.x2);

                return right > left && top > bottom;
              });

              const occupiedFixturesLeft = fixtureItemSpace.filter(fixture => {
                const left = Math.max(ghostFacingLeft.x1, fixture.x1);
                const bottom = Math.max(ghostFacingLeft.y1, fixture.y1);
                const top = Math.min(ghostFacingLeft.y2, fixture.y2);
                const right = Math.min(ghostFacingLeft.x2, fixture.x2);

                return right > left && top > bottom;
              });

              const occupiedFixturesCrossbarLeft = (isFlatbar ? fixturesFlatbar : fixturesCrossbar).filter(fixture => {
                const sameSituated = selectedLocatedFixture.y === fixture.y1 &&
                  selectedLocatedFixture.y + selectedLocatedFixture.height === fixture.y2;
                const left = Math.max(ghostFacingLeft.x1, fixture.x1);
                const bottom = Math.max(ghostFacingLeft.y1, fixture.y1);
                const top = Math.min(ghostFacingLeft.y2, fixture.y2);
                const right = Math.min(ghostFacingLeft.x2, fixture.x2);

                return right > left && top > bottom && sameSituated;
              });

              const isOccupiedFacingsLeft = Boolean(occupiedFacingsLeft.length);
              const isOccupiedFixturesLeft = Boolean(occupiedFixturesLeft.length);
              const boundaryLeft = Math.min(...occupiedFixturesCrossbarLeft.map(fixture => fixture.x1));
              const isOutOfBoundaryFixturesCrossbarLeft =
                ghostFacingLeft.x1 < boundaryLeft ||
                ghostFacingLeft.x1 < 0;

              if (
                isOccupiedFacingsLeft ||
                isOccupiedFixturesLeft ||
                isOutOfBoundaryFixturesCrossbarLeft
              ) {

                let shiftX = 0;

                if (isOutOfBoundaryFixturesCrossbarRight) {
                  shiftX = ghostFacingRight.x2 - boundaryRight;
                }

                if (isOccupiedFixturesRight) {
                  const occupiedFixtureRight = occupiedFixturesRight.sort((a, b) => a.x1 - b.x1)[0];

                  shiftX = ghostFacingRight.x2 - occupiedFixtureRight.x1;
                }

                if (isOccupiedFacingsRight) {
                  const occupiedFacingRight = occupiedFacingsRight.sort((a, b) => a.x1 - b.x1)[0];

                  shiftX = ghostFacingRight.x2 - occupiedFacingRight.x1;
                }

                const shiftGhostFacing = {
                  ...ghostFacingRight,
                  x1: ghostFacingRight.x1 - shiftX - HORIZONTAL_SPACING,
                  x2: ghostFacingRight.x2 - shiftX - HORIZONTAL_SPACING,
                };

                const shiftSelectedFacing = {
                  ...selectedFacingBoundary,
                  x1: selectedFacingBoundary.x1 - shiftX - HORIZONTAL_SPACING,
                  x2: selectedFacingBoundary.x2 - shiftX - HORIZONTAL_SPACING,
                };

                const shiftOccupiedFacings = itemsCoordinatesWithoutSelf.filter(facingGroup => {
                  const leftGhost = Math.max(shiftGhostFacing.x1, facingGroup.x1);
                  const topGhost = Math.min(shiftGhostFacing.y2, facingGroup.y2);
                  const rightGhost = Math.min(shiftGhostFacing.x2, facingGroup.x2);

                  const leftSelected = Math.max(shiftSelectedFacing.x1, facingGroup.x1);
                  const bottomSelected = Math.max(shiftGhostFacing.y1, facingGroup.y1);
                  const topSelected = Math.min(shiftSelectedFacing.y2, facingGroup.y2);
                  const rightSelected = Math.min(shiftSelectedFacing.x2, facingGroup.x2);

                  return rightGhost > leftGhost && topGhost > bottomSelected || rightSelected > leftSelected && topSelected > bottomSelected;
                });

                const shiftOccupiedFixtures = fixtureItemSpace.filter(fixture => {
                  const leftGhost = Math.max(shiftGhostFacing.x1, fixture.x1);
                  const bottomGhost = Math.max(shiftGhostFacing.y1, fixture.y1);
                  const topGhost = Math.min(shiftGhostFacing.y2, fixture.y2);
                  const rightGhost = Math.min(shiftGhostFacing.x2, fixture.x2);

                  const leftSelected = Math.max(shiftSelectedFacing.x1, fixture.x1);
                  const bottomSelected = Math.max(shiftSelectedFacing.y1, fixture.y1);
                  const topSelected = Math.min(shiftSelectedFacing.y2, fixture.y2);
                  const rightSelected = Math.min(shiftSelectedFacing.x2, fixture.x2);

                  return rightGhost > leftGhost && topGhost > bottomGhost || rightSelected > leftSelected && topSelected > bottomSelected;
                });

                const shiftOccupiedFixturesCrossbar = (isFlatbar ? fixturesFlatbar : fixturesCrossbar).filter(fixture => {
                  const sameSituated = selectedLocatedFixture.y === fixture.y1 &&
                    selectedLocatedFixture.y + selectedLocatedFixture.height === fixture.y2;
                  const leftGhost = Math.max(shiftGhostFacing.x1, fixture.x1);
                  const bottomGhost = Math.max(shiftGhostFacing.y1, fixture.y1);
                  const topGhost = Math.min(shiftGhostFacing.y2, fixture.y2);
                  const rightGhost = Math.min(shiftGhostFacing.x2, fixture.x2);

                  const leftSelected = Math.max(shiftSelectedFacing.x1, fixture.x1);
                  const bottomSelected = Math.max(shiftSelectedFacing.y1, fixture.y1);
                  const topSelected = Math.min(shiftSelectedFacing.y2, fixture.y2);
                  const rightSelected = Math.min(shiftSelectedFacing.x2, fixture.x2);

                  return (rightGhost > leftGhost && topGhost > bottomGhost || rightSelected > leftSelected && topSelected > bottomSelected) && sameSituated;
                });

                const isShiftOccupiedFacings = Boolean(shiftOccupiedFacings.length);
                const isShiftOccupiedFixtures = Boolean(shiftOccupiedFixtures.length);
                const shiftBoundaryRight = Math.max(...shiftOccupiedFixturesCrossbar.map(fixture => fixture.x2));
                const shiftBoundaryLeft = Math.min(...shiftOccupiedFixturesCrossbar.map(fixture => fixture.x1));
                const isOutOfBoundaryShiftFixturesCrossbarRight =
                  shiftGhostFacing.x2 > shiftBoundaryRight ||
                  shiftGhostFacing.x1 < 0 ||
                  shiftSelectedFacing.x2 > shiftBoundaryRight ||
                  shiftSelectedFacing.x1 < 0;
                const isOutOfBoundaryShiftFixturesCrossbarLeft =
                  shiftGhostFacing.x1 < shiftBoundaryLeft ||
                  shiftGhostFacing.x1 < 0 ||
                  shiftSelectedFacing.x1 < shiftBoundaryLeft ||
                  shiftSelectedFacing.x1 < 0;

                if (
                  isShiftOccupiedFacings ||
                  isShiftOccupiedFixtures ||
                  isOutOfBoundaryShiftFixturesCrossbarRight ||
                  isOutOfBoundaryShiftFixturesCrossbarLeft
                ) {
                  return null;
                }

                return [
                  {
                    ...selectedFacingGroup,
                    id: generateUniqueId(),
                    x: shiftSelectedFacing.x1,
                    segment: Math.trunc(shiftSelectedFacing.x1 / SEGMENT_WIDTH) + 1,
                    located_fixture_id: getFixtureIdByXCoordinate(shiftSelectedFacing.x1, fixtures, selectedLocatedFixture),
                  },
                  {
                    ...selectedFacingGroup,
                    id: generateUniqueId(),
                    x: shiftGhostFacing.x1,
                    horizontal_facings: 1,
                    segment: Math.trunc(shiftGhostFacing.x1 / SEGMENT_WIDTH) + 1,
                    located_fixture_id: getFixtureIdByXCoordinate(shiftGhostFacing.x1, fixtures, selectedLocatedFixture),
                  }
                ];
              }

              return [
                {
                  ...selectedFacingGroup,
                  id: generateUniqueId(),
                },
                {
                  ...selectedFacingGroup,
                  x: ghostFacingLeft.x1,
                  id: generateUniqueId(),
                  horizontal_facings: 1,
                  segment: Math.trunc(ghostFacingLeft.x1 / SEGMENT_WIDTH) + 1,
                  located_fixture_id: getFixtureIdByXCoordinate(ghostFacingLeft.x1, fixtures, selectedLocatedFixture),
                }
              ];
            }

            return [
              {
                ...selectedFacingGroup,
                id: generateUniqueId(),
              },
              {
                ...selectedFacingGroup,
                x: ghostFacingRight.x1,
                id: generateUniqueId(),
                horizontal_facings: 1,
                segment: Math.trunc(ghostFacingRight.x1 / SEGMENT_WIDTH) + 1,
              }
            ];
          }
        }
      } catch (e) {
        return null;
      }

      return null;
    }
  );

export const assortmentWithBincapSelector = createSelector(
  baseAssortmentSelector,
  solverOutputSolverInputSelector,
  historySelector,
  historyStepSelector,
  (baseAssortment, solverInput, history, historyStep) => {
    const constraints = solverInput?.constraints ? solverInput?.constraints.enforced_bincaps : [];
    const lockConstraints = solverInput?.constraints?.enforced_bincaps_locked || [];
    const historyConstraints = historyStep === 0 ? [] : history[historyStep].bincapConstraints;

    const addBincap = (constraints: Array<BincapRecord>, assortment: AssortmentItemOptimized) => {
      let record: AssortmentItemOptimized = assortment;
      const sku = record.sku;

      if (constraints.length) {
        const found = constraints.filter(c => c.product_num === sku);

        if (found.length) {
          const rec = found[0];

          if (record.optimized) {
            record = {
              ...record,
              optimized: {
                ...record.optimized,
                newMinTargetBincapOptimized: rec.min,
                newMaxTargetBincapOptimized: rec.max,
              }
            };
          }
        }
      }

      return record;
    };

    const assortments = baseAssortment?.reduce((acc, cur) => {
      let record: AssortmentItemOptimized = addBincap(constraints, cur);

      record = addBincap(lockConstraints, cur);
      record = addBincap(historyConstraints, cur);
      acc.push(record);

      return acc;
    }, [] as Array<AssortmentItemOptimized>);

    return assortments;
  },
);

export const visualizerBincapConstraintsSelector = (
  dirtyBincap?: Array<AssortmentItemOptimized>
) => createSelector(
  historySelector,
  historyStepSelector,
  solverOutputSolverInputSelector,
  (history, historyStep, solverInput) => {
    const lockConstraints = solverInput?.constraints?.enforced_bincaps_locked || [];

    if (dirtyBincap) {
      const editedBincap: Array<BincapRecord> = dirtyBincap.map(
        bincap => {
          return {
            product_num: bincap.sku,
            min: bincap.optimized?.newMinTargetBincapOptimized || null,
            max: bincap.optimized?.newMaxTargetBincapOptimized || null
          };
        }
      );

      return historyStep === 0 ?
        editedBincap.concat(
          lockConstraints.filter(b1 => !editedBincap.some(b2 => b2.product_num === b1.product_num))
        ) : history[historyStep].bincapConstraints;
    }

    return historyStep === 0 ? lockConstraints : history[historyStep].bincapConstraints;
  }
);

export const rotateFacingSelector = (
  contextMenuData: ContextMenuData | null
) =>
  createSelector(
    productsSelector,
    facingGroupsByIdsSelector,
    itemsCoordinatesSelector,
    locatedFixturesHistorySelector,
    locatedFixturesByIdSelector,
    aisleHeightSelector,
    aisleDepthSelector,
    fixtureSpacesSelector,
    (
      products,
      facingGroupsByIds,
      itemsCoordinates,
      locatedFixtures,
      locatedFixturesById,
      aisleHeight,
      aisleDepth,
      fixtureSpaces
    ) => {
      const options: {
        horizontallyFacingGroup: FacingGroup | null;
        verticallyFacingGroup: FacingGroup | null;
        clockwiseFacingGroup: FacingGroup | null;
        defaultFacingGroup: FacingGroup | null;
      } = {
        horizontallyFacingGroup: null,
        verticallyFacingGroup: null,
        clockwiseFacingGroup: null,
        defaultFacingGroup: null
      };

      try {
        if (contextMenuData) {
          const { id } = contextMenuData;
          const facingGroup = facingGroupsByIds[id];

          if (
            facingGroup.fixtureType !== FIXTURE_TYPES.CROSSBAR
            && facingGroup.fixtureType !== FIXTURE_TYPES.PEGBOARD
          ) {
            const gapDepthShelf = facingGroup.fixtureType === FIXTURE_TYPES.SHELF ? 1 : 0;
            const locatedFixtureSpaces: Array<{
              x1: number;
              y1: number;
              x2: number;
              y2: number;
              id: number;
            }> = locatedFixtures
              .filter(locatedFixture => locatedFixture.type !== FIXTURE_TYPES.PEGBOARD)
              .map(locatedFixture => ({
                x1: locatedFixture.x,
                y1: locatedFixture.y,
                x2: locatedFixture.x + locatedFixture.width,
                y2: locatedFixture.y + locatedFixture.height,
                id: locatedFixture.id
              }));

            const currentLocatedFixture = locatedFixturesById[facingGroup.located_fixture_id];
            const currentLocatedFixtureSpace = {
              x1: currentLocatedFixture.x,
              y1: currentLocatedFixture.y,
              x2: currentLocatedFixture.x + currentLocatedFixture.width,
              y2: currentLocatedFixture.y + currentLocatedFixture.height,
            };

            const oneLineLocatedFixtures = locatedFixtureSpaces
              .filter(locatedFixture => locatedFixture.y2 === currentLocatedFixtureSpace.y2);

            const rightContainerBounbary = Math.max(...locatedFixtureSpaces.map(locatedFixture => locatedFixture.x2));

            const itemsCoordinatesWithoutSelf = itemsCoordinates.filter(item => item.id !== id);

            if (facingGroup.cap_nested !== Nesting.YNested) {
              // Clockwise
              let canClockwise = true;
              const clockwiseOrientationCode = rotateClockwiseOrientationFacing(facingGroup.orientation_code);
              const clockwiseCappedOrientationCode = rotateCappedOrientationFacing(clockwiseOrientationCode);
              const clockwisePackaging = {
                ...facingGroup.packaging,
                ...orientationFacing(
                  clockwiseOrientationCode,
                  facingGroup.defaultPackaging.width,
                  facingGroup.defaultPackaging.height,
                  facingGroup.defaultPackaging.depth
                )
              };
              const clockwiseCappedPackaging = orientationFacing(
                clockwiseCappedOrientationCode,
                facingGroup.defaultPackaging.width,
                facingGroup.defaultPackaging.height,
                facingGroup.defaultPackaging.depth
              );
              const clockwiseFacingGroupHeight = getFacingHeight(
                clockwisePackaging.height,
                facingGroup.vertical_facings,
                facingGroup.cap_nested as Nesting,
                facingGroup.packaging.nested_height,
                clockwiseCappedPackaging.height,
                facingGroup.capped_vertical_facings
              );
              const clockwiseFacingGroupWidth = clockwisePackaging.width * facingGroup.horizontal_facings;
              const clockwiseGhostFacingGroup = {
                x1: facingGroup.x,
                y1: facingGroup.y,
                x2: facingGroup.x + clockwiseFacingGroupWidth,
                y2: facingGroup.y + clockwiseFacingGroupHeight
              };

              if (clockwiseGhostFacingGroup.y2 > aisleHeight) {
                canClockwise = false;
              }

              if (canClockwise) {
                const [clockwiseItemsOccupiedFacing] = itemsCoordinatesWithoutSelf.filter((item) => {
                  const left = Math.max(clockwiseGhostFacingGroup.x1, item.x1);
                  const bottom = Math.max(clockwiseGhostFacingGroup.y1, item.y1);
                  const top = Math.min(clockwiseGhostFacingGroup.y2, item.y2);
                  const right = Math.min(clockwiseGhostFacingGroup.x2, item.x2);

                  return right > left && top > bottom;
                }).sort((a, b) => {
                  if (a.x1 > b.x1) return 1;
                  if (a.x1 < b.x1) return -1;

                  return 0;
                });

                if (clockwiseItemsOccupiedFacing) {
                  const delta = clockwiseGhostFacingGroup.x2 - clockwiseItemsOccupiedFacing.x1 + EPSILON;

                  clockwiseGhostFacingGroup.x1 = clockwiseGhostFacingGroup.x1 - delta;
                  clockwiseGhostFacingGroup.x2 = clockwiseGhostFacingGroup.x2 - delta;

                  const clockwiseItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                    const left = Math.max(clockwiseGhostFacingGroup.x1, item.x1);
                    const bottom = Math.max(clockwiseGhostFacingGroup.y1, item.y1);
                    const top = Math.min(clockwiseGhostFacingGroup.y2, item.y2);
                    const right = Math.min(clockwiseGhostFacingGroup.x2, item.x2);

                    return right > left && top > bottom;
                  });

                  const clockwiseHangingFacing = !oneLineLocatedFixtures
                    .some(locatedFixture =>
                      clockwiseGhostFacingGroup.x1 >= locatedFixture.x1 &&
                      clockwiseGhostFacingGroup.x1 <= locatedFixture.x2
                    );

                  if (
                    clockwiseItemsOccupiedFacingDelta ||
                    clockwiseGhostFacingGroup.x1 < 0 ||
                    clockwiseHangingFacing
                  ) {
                    canClockwise = false;
                  }
                }

                if (canClockwise) {
                  const clockwiseFixturesOccupiedFacing = locatedFixtureSpaces.some((locatedFixture) => {
                    const left = Math.max(clockwiseGhostFacingGroup.x1, locatedFixture.x1);
                    const bottom = Math.max(clockwiseGhostFacingGroup.y1, locatedFixture.y1);
                    const top = Math.min(clockwiseGhostFacingGroup.y2, locatedFixture.y2);
                    const right = Math.min(clockwiseGhostFacingGroup.x2, locatedFixture.x2);

                    return right > left && top > bottom;
                  });

                  if (clockwiseFixturesOccupiedFacing) {
                    canClockwise = false;
                  }

                  if (canClockwise && clockwiseGhostFacingGroup.x2 > rightContainerBounbary) {
                    const delta = clockwiseGhostFacingGroup.x2 - rightContainerBounbary + EPSILON;

                    clockwiseGhostFacingGroup.x1 = clockwiseGhostFacingGroup.x1 - delta;
                    clockwiseGhostFacingGroup.x2 = clockwiseGhostFacingGroup.x2 - delta;
                    const clockwiseItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                      const left = Math.max(clockwiseGhostFacingGroup.x1, item.x1);
                      const bottom = Math.max(clockwiseGhostFacingGroup.y1, item.y1);
                      const top = Math.min(clockwiseGhostFacingGroup.y2, item.y2);
                      const right = Math.min(clockwiseGhostFacingGroup.x2, item.x2);

                      return right > left && top > bottom;
                    });

                    if (clockwiseItemsOccupiedFacingDelta || clockwiseGhostFacingGroup.x1 < 0) {
                      canClockwise = false;
                    }
                  }

                  if (canClockwise) {
                    const clockwiseDifferentFixturesOccupiedFacing = fixtureSpaces.filter(fixture => {
                      const left = Math.max(clockwiseGhostFacingGroup.x1, fixture.x1);
                      const bottom = Math.max(clockwiseGhostFacingGroup.y1, fixture.y1);
                      const right = Math.min(clockwiseGhostFacingGroup.x2, fixture.x2);
                      const top = Math.min(clockwiseGhostFacingGroup.y2, fixture.y2);

                      return right > left && top > bottom;
                    });

                    if (clockwiseDifferentFixturesOccupiedFacing.length > 1) {
                      const clockwiseDifferentFixturesOccupiedFacingType = clockwiseDifferentFixturesOccupiedFacing.map(fixture => locatedFixturesById[fixture.id].type);
                      const isSameFixtures = new Set(clockwiseDifferentFixturesOccupiedFacingType).size === 1;

                      if (!isSameFixtures) {
                        const rightFixtureBounbary = Math.trunc(clockwiseGhostFacingGroup.x2 / SEGMENT_WIDTH) * SEGMENT_WIDTH;
                        const delta = clockwiseGhostFacingGroup.x2 - rightFixtureBounbary + EPSILON;

                        clockwiseGhostFacingGroup.x1 = clockwiseGhostFacingGroup.x1 - delta;
                        clockwiseGhostFacingGroup.x2 = clockwiseGhostFacingGroup.x2 - delta;
                        const clockwiseItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                          const left = Math.max(clockwiseGhostFacingGroup.x1, item.x1);
                          const bottom = Math.max(clockwiseGhostFacingGroup.y1, item.y1);
                          const top = Math.min(clockwiseGhostFacingGroup.y2, item.y2);
                          const right = Math.min(clockwiseGhostFacingGroup.x2, item.x2);

                          return right > left && top > bottom;
                        });

                        if (clockwiseItemsOccupiedFacingDelta || clockwiseGhostFacingGroup.x1 < 0) {
                          canClockwise = false;
                        }
                      }
                    }
                  }

                  if (canClockwise) {
                    options.clockwiseFacingGroup = getFacingGroupByAltOrientation({
                      ...facingGroup,
                      id: generateUniqueId(),
                      x: clockwiseGhostFacingGroup.x1,
                      segment: Math.trunc(clockwiseGhostFacingGroup.x1 / SEGMENT_WIDTH) + 1,
                      located_fixture_id: getFixtureIdByXCoordinate(clockwiseGhostFacingGroup.x1, fixtureSpaces, currentLocatedFixture),
                      orientation_code: clockwiseOrientationCode,
                      capped_orientation_code: clockwiseCappedOrientationCode,
                      packaging: clockwisePackaging,
                      cappedPackaging: clockwiseCappedPackaging,
                    }, products[facingGroup.product_num]);
                  }
                }
              }

              // Vertically
              let canVertically = true;

              const verticallyOrientationCode = rotateVerticallyOrientationFacing(facingGroup.orientation_code);
              const verticallyCappedOrientationCode = rotateCappedOrientationFacing(verticallyOrientationCode);
              const verticallyPackaging = {
                ...facingGroup.packaging,
                ...orientationFacing(
                  verticallyOrientationCode,
                  facingGroup.defaultPackaging.width,
                  facingGroup.defaultPackaging.height,
                  facingGroup.defaultPackaging.depth
                )
              };
              const verticallyCappedPackaging = orientationFacing(
                verticallyCappedOrientationCode,
                facingGroup.defaultPackaging.width,
                facingGroup.defaultPackaging.height,
                facingGroup.defaultPackaging.depth
              );
              const verticallyFacingGroupHeight = getFacingHeight(
                verticallyPackaging.height,
                facingGroup.vertical_facings,
                facingGroup.cap_nested as Nesting,
                facingGroup.packaging.nested_height,
                verticallyCappedPackaging.height,
                facingGroup.capped_vertical_facings
              );

              const verticallyFacingGroupWidth = verticallyPackaging.width * facingGroup.horizontal_facings;

              const verticallyDepthFacings = Math.trunc((aisleDepth + gapDepthShelf) / verticallyPackaging.depth);

              if (verticallyDepthFacings === 0) {
                canVertically = false;
              }

              if (canVertically) {
                const verticallyGhostFacingGroup = {
                  x1: facingGroup.x,
                  y1: facingGroup.y,
                  x2: facingGroup.x + verticallyFacingGroupWidth,
                  y2: facingGroup.y + verticallyFacingGroupHeight
                };

                const verticallyFixturesOccupiedFacing = locatedFixtureSpaces.some((locatedFixture) => {
                  const left = Math.max(verticallyGhostFacingGroup.x1, locatedFixture.x1);
                  const bottom = Math.max(verticallyGhostFacingGroup.y1, locatedFixture.y1);
                  const top = Math.min(verticallyGhostFacingGroup.y2, locatedFixture.y2);
                  const right = Math.min(verticallyGhostFacingGroup.x2, locatedFixture.x2);

                  return right > left && top > bottom;
                });

                const verticallyHangingFacing = !oneLineLocatedFixtures
                  .some(locatedFixture =>
                    verticallyGhostFacingGroup.x1 >= locatedFixture.x1 &&
                    verticallyGhostFacingGroup.x1 <= locatedFixture.x2
                  );

                if (
                  verticallyGhostFacingGroup.y2 > aisleHeight ||
                  verticallyFixturesOccupiedFacing ||
                  verticallyHangingFacing
                ) {
                  canVertically = false;
                }
              }

              if (canVertically) {
                options.verticallyFacingGroup = getFacingGroupByAltOrientation({
                  ...facingGroup,
                  id: generateUniqueId(),
                  orientation_code: verticallyOrientationCode,
                  capped_orientation_code: verticallyCappedOrientationCode,
                  packaging: verticallyPackaging,
                  cappedPackaging: verticallyCappedPackaging,
                  depth_facings: verticallyDepthFacings,
                }, products[facingGroup.product_num]);
              }
            }

            // Horizontally
            let canHorizontally = true;
            const horizontallyOrientationCode = rotateHorizontallyOrientationFacing(facingGroup.orientation_code);
            const horizontallyCappedOrientationCode = rotateCappedOrientationFacing(horizontallyOrientationCode);
            const horizontallyPackaging = {
              ...facingGroup.packaging,
              ...orientationFacing(
                horizontallyOrientationCode,
                facingGroup.defaultPackaging.width,
                facingGroup.defaultPackaging.height,
                facingGroup.defaultPackaging.depth
              )
            };
            const horizontallyCappedPackaging = orientationFacing(
              horizontallyCappedOrientationCode,
              facingGroup.defaultPackaging.width,
              facingGroup.defaultPackaging.height,
              facingGroup.defaultPackaging.depth
            );
            const horizontallyFacingGroupHeight = getFacingHeight(
              horizontallyPackaging.height,
              facingGroup.vertical_facings,
              facingGroup.cap_nested as Nesting,
              facingGroup.packaging.nested_height,
              horizontallyCappedPackaging.height,
              facingGroup.capped_vertical_facings
            );

            const horizontallyFacingGroupWidth = horizontallyPackaging.width * facingGroup.horizontal_facings;
            const horizontallyDepthFacings = Math.trunc((aisleDepth + gapDepthShelf) / horizontallyPackaging.depth);
            const horizontallyDepthCappedFacings = Math.trunc((aisleDepth + gapDepthShelf) / horizontallyCappedPackaging.depth);

            if (horizontallyDepthFacings === 0 || horizontallyDepthCappedFacings === 0) {
              canHorizontally = false;
            }

            if (canHorizontally) {
              const horizontallyGhostFacingGroup = {
                x1: facingGroup.x,
                y1: facingGroup.y,
                x2: facingGroup.x + horizontallyFacingGroupWidth,
                y2: facingGroup.y + horizontallyFacingGroupHeight,
              };

              const horizontallyFixturesOccupiedFacing = locatedFixtureSpaces.some((locatedFixture) => {
                const left = Math.max(horizontallyGhostFacingGroup.x1, locatedFixture.x1);
                const bottom = Math.max(horizontallyGhostFacingGroup.y1, locatedFixture.y1);
                const top = Math.min(horizontallyGhostFacingGroup.y2, locatedFixture.y2);
                const right = Math.min(horizontallyGhostFacingGroup.x2, locatedFixture.x2);

                return right > left && top > bottom;
              });

              if (horizontallyFixturesOccupiedFacing) {
                canHorizontally = false;
              }

              if (canHorizontally) {
                const [horizontallyItemOccupiedFacing] = itemsCoordinatesWithoutSelf.filter((item) => {
                  const left = Math.max(horizontallyGhostFacingGroup.x1, item.x1);
                  const bottom = Math.max(horizontallyGhostFacingGroup.y1, item.y1);
                  const top = Math.min(horizontallyGhostFacingGroup.y2, item.y2);
                  const right = Math.min(horizontallyGhostFacingGroup.x2, item.x2);

                  return right > left && top > bottom;
                }).sort((a, b) => {
                  if (a.x1 > b.x1) return 1;
                  if (a.x1 < b.x1) return -1;

                  return 0;
                });

                if (horizontallyItemOccupiedFacing) {
                  const delta = horizontallyGhostFacingGroup.x2 - horizontallyItemOccupiedFacing.x1 + EPSILON;

                  horizontallyGhostFacingGroup.x1 = horizontallyGhostFacingGroup.x1 - delta;
                  horizontallyGhostFacingGroup.x2 = horizontallyGhostFacingGroup.x2 - delta;

                  const horizontallyItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                    const left = Math.max(horizontallyGhostFacingGroup.x1, item.x1);
                    const bottom = Math.max(horizontallyGhostFacingGroup.y1, item.y1);
                    const top = Math.min(horizontallyGhostFacingGroup.y2, item.y2);
                    const right = Math.min(horizontallyGhostFacingGroup.x2, item.x2);

                    return right > left && top > bottom;
                  });

                  const horizontallyHangingFacing = !oneLineLocatedFixtures
                    .some(locatedFixture =>
                      horizontallyGhostFacingGroup.x1 >= locatedFixture.x1 &&
                      horizontallyGhostFacingGroup.x1 <= locatedFixture.x2
                    );

                  if (
                    horizontallyItemsOccupiedFacingDelta ||
                    horizontallyGhostFacingGroup.x1 < 0 ||
                    horizontallyHangingFacing
                  ) {
                    canHorizontally = false;
                  }
                }

                if (canHorizontally && horizontallyGhostFacingGroup.x2 > rightContainerBounbary) {
                  const delta = horizontallyGhostFacingGroup.x2 - rightContainerBounbary + EPSILON;

                  horizontallyGhostFacingGroup.x1 = horizontallyGhostFacingGroup.x1 - delta;
                  horizontallyGhostFacingGroup.x2 = horizontallyGhostFacingGroup.x2 - delta;
                  const horizontallyItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                    const left = Math.max(horizontallyGhostFacingGroup.x1, item.x1);
                    const bottom = Math.max(horizontallyGhostFacingGroup.y1, item.y1);
                    const top = Math.min(horizontallyGhostFacingGroup.y2, item.y2);
                    const right = Math.min(horizontallyGhostFacingGroup.x2, item.x2);

                    return right > left && top > bottom;
                  });

                  if (horizontallyItemsOccupiedFacingDelta || horizontallyGhostFacingGroup.x1 < 0) {
                    canHorizontally = false;
                  }
                }

                if (canHorizontally) {
                  const horizontallyDifferentFixturesOccupiedFacing = fixtureSpaces.filter(fixture => {
                    const left = Math.max(horizontallyGhostFacingGroup.x1, fixture.x1);
                    const bottom = Math.max(horizontallyGhostFacingGroup.y1, fixture.y1);
                    const right = Math.min(horizontallyGhostFacingGroup.x2, fixture.x2);
                    const top = Math.min(horizontallyGhostFacingGroup.y2, fixture.y2);

                    return right > left && top > bottom;
                  });

                  if (horizontallyDifferentFixturesOccupiedFacing.length > 1) {
                    const horizontallyDifferentFixturesOccupiedFacingType = horizontallyDifferentFixturesOccupiedFacing.map(fixture => locatedFixturesById[fixture.id].type);
                    const isSameFixtures = new Set(horizontallyDifferentFixturesOccupiedFacingType).size === 1;

                    if (!isSameFixtures) {
                      const rightFixtureBounbary = Math.trunc(horizontallyGhostFacingGroup.x2 / SEGMENT_WIDTH) * SEGMENT_WIDTH;
                      const delta = horizontallyGhostFacingGroup.x2 - rightFixtureBounbary + EPSILON;

                      horizontallyGhostFacingGroup.x1 = horizontallyGhostFacingGroup.x1 - delta;
                      horizontallyGhostFacingGroup.x2 = horizontallyGhostFacingGroup.x2 - delta;
                      const horizontallyItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                        const left = Math.max(horizontallyGhostFacingGroup.x1, item.x1);
                        const bottom = Math.max(horizontallyGhostFacingGroup.y1, item.y1);
                        const top = Math.min(horizontallyGhostFacingGroup.y2, item.y2);
                        const right = Math.min(horizontallyGhostFacingGroup.x2, item.x2);

                        return right > left && top > bottom;
                      });

                      if (horizontallyItemsOccupiedFacingDelta || horizontallyGhostFacingGroup.x1 < 0) {
                        canHorizontally = false;
                      }
                    }
                  }
                }

                if (canHorizontally) {
                  options.horizontallyFacingGroup = getFacingGroupByAltOrientation({
                    ...facingGroup,
                    id: generateUniqueId(),
                    x: horizontallyGhostFacingGroup.x1,
                    segment: Math.trunc(horizontallyGhostFacingGroup.x1 / SEGMENT_WIDTH) + 1,
                    located_fixture_id: getFixtureIdByXCoordinate(horizontallyGhostFacingGroup.x1, fixtureSpaces, currentLocatedFixture),
                    orientation_code: horizontallyOrientationCode,
                    capped_orientation_code: horizontallyCappedOrientationCode,
                    packaging: horizontallyPackaging,
                    cappedPackaging: horizontallyCappedPackaging,
                    depth_facings: horizontallyDepthFacings,
                  }, products[facingGroup.product_num]);
                }
              }
            }

            // Default Facing Orientation
            if (facingGroup.orientation_code !== 0) {
              let canDefaultFacing = true;
              const defaultOrientationCode = 0;
              const defaultCappedOrientationCode = rotateCappedOrientationFacing(defaultOrientationCode);
              const defaultPackaging = {
                ...facingGroup.packaging,
                ...orientationFacing(
                  defaultOrientationCode,
                  facingGroup.defaultPackaging.width,
                  facingGroup.defaultPackaging.height,
                  facingGroup.defaultPackaging.depth
                )
              };
              const defaultCappedPackaging = orientationFacing(
                defaultCappedOrientationCode,
                facingGroup.defaultPackaging.width,
                facingGroup.defaultPackaging.height,
                facingGroup.defaultPackaging.depth
              );
              const defaultFacingGroupHeight = getFacingHeight(
                defaultPackaging.height,
                facingGroup.vertical_facings,
                facingGroup.cap_nested as Nesting,
                facingGroup.packaging.nested_height,
                defaultCappedPackaging.height,
                facingGroup.capped_vertical_facings
              );
              const defaultFacingGroupWidth = defaultPackaging.width * facingGroup.horizontal_facings;
              const defaultGhostFacingGroup = {
                x1: facingGroup.x,
                y1: facingGroup.y,
                x2: facingGroup.x + defaultFacingGroupWidth,
                y2: facingGroup.y + defaultFacingGroupHeight
              };

              if (defaultGhostFacingGroup.y2 > aisleHeight) {
                canDefaultFacing = false;
              }

              if (canDefaultFacing) {
                const [defaultItemsOccupiedFacing] = itemsCoordinatesWithoutSelf.filter((item) => {
                  const left = Math.max(defaultGhostFacingGroup.x1, item.x1);
                  const bottom = Math.max(defaultGhostFacingGroup.y1, item.y1);
                  const top = Math.min(defaultGhostFacingGroup.y2, item.y2);
                  const right = Math.min(defaultGhostFacingGroup.x2, item.x2);

                  return right > left && top > bottom;
                }).sort((a, b) => {
                  if (a.x1 > b.x1) return 1;
                  if (a.x1 < b.x1) return -1;

                  return 0;
                });

                if (defaultItemsOccupiedFacing) {
                  const delta = defaultGhostFacingGroup.x2 - defaultItemsOccupiedFacing.x1 + EPSILON;

                  defaultGhostFacingGroup.x1 = defaultGhostFacingGroup.x1 - delta;
                  defaultGhostFacingGroup.x2 = defaultGhostFacingGroup.x2 - delta;
                }

                const defaultItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                  const left = Math.max(defaultGhostFacingGroup.x1, item.x1);
                  const bottom = Math.max(defaultGhostFacingGroup.y1, item.y1);
                  const top = Math.min(defaultGhostFacingGroup.y2, item.y2);
                  const right = Math.min(defaultGhostFacingGroup.x2, item.x2);

                  return right > left && top > bottom;
                });

                const defaultHangingFacing = !oneLineLocatedFixtures
                  .some(locatedFixture =>
                    defaultGhostFacingGroup.x1 >= locatedFixture.x1 &&
                    defaultGhostFacingGroup.x1 <= locatedFixture.x2
                  );

                if (
                  defaultItemsOccupiedFacingDelta ||
                  defaultGhostFacingGroup.x1 < 0 ||
                  defaultHangingFacing
                ) {
                  canDefaultFacing = false;
                }

                if (canDefaultFacing) {
                  const defaultFixturesOccupiedFacing = locatedFixtureSpaces.some((locatedFixture) => {
                    const left = Math.max(defaultGhostFacingGroup.x1, locatedFixture.x1);
                    const bottom = Math.max(defaultGhostFacingGroup.y1, locatedFixture.y1);
                    const top = Math.min(defaultGhostFacingGroup.y2, locatedFixture.y2);
                    const right = Math.min(defaultGhostFacingGroup.x2, locatedFixture.x2);

                    return right > left && top > bottom;
                  });

                  if (defaultFixturesOccupiedFacing) {
                    canDefaultFacing = false;
                  }

                  if (canDefaultFacing && defaultGhostFacingGroup.x2 > rightContainerBounbary) {
                    const delta = defaultGhostFacingGroup.x2 - rightContainerBounbary + EPSILON;

                    defaultGhostFacingGroup.x1 = defaultGhostFacingGroup.x1 - delta;
                    defaultGhostFacingGroup.x2 = defaultGhostFacingGroup.x2 - delta;
                    const defaultItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                      const left = Math.max(defaultGhostFacingGroup.x1, item.x1);
                      const bottom = Math.max(defaultGhostFacingGroup.y1, item.y1);
                      const top = Math.min(defaultGhostFacingGroup.y2, item.y2);
                      const right = Math.min(defaultGhostFacingGroup.x2, item.x2);

                      return right > left && top > bottom;
                    });

                    if (defaultItemsOccupiedFacingDelta || defaultGhostFacingGroup.x1 < 0) {
                      canDefaultFacing = false;
                    }
                  }

                  if (canDefaultFacing) {
                    const defaultDifferentFixturesOccupiedFacing = fixtureSpaces.filter(fixture => {
                      const left = Math.max(defaultGhostFacingGroup.x1, fixture.x1);
                      const bottom = Math.max(defaultGhostFacingGroup.y1, fixture.y1);
                      const right = Math.min(defaultGhostFacingGroup.x2, fixture.x2);
                      const top = Math.min(defaultGhostFacingGroup.y2, fixture.y2);

                      return right > left && top > bottom;
                    });

                    if (defaultDifferentFixturesOccupiedFacing.length > 1) {
                      const defaultDifferentFixturesOccupiedFacingType = defaultDifferentFixturesOccupiedFacing.map(fixture => locatedFixturesById[fixture.id].type);
                      const isSameFixtures = new Set(defaultDifferentFixturesOccupiedFacingType).size === 1;

                      if (!isSameFixtures) {
                        const rightFixtureBounbary = Math.trunc(defaultGhostFacingGroup.x2 / SEGMENT_WIDTH) * SEGMENT_WIDTH;
                        const delta = defaultGhostFacingGroup.x2 - rightFixtureBounbary + EPSILON;

                        defaultGhostFacingGroup.x1 = defaultGhostFacingGroup.x1 - delta;
                        defaultGhostFacingGroup.x2 = defaultGhostFacingGroup.x2 - delta;
                        const defaultItemsOccupiedFacingDelta = itemsCoordinatesWithoutSelf.some((item) => {
                          const left = Math.max(defaultGhostFacingGroup.x1, item.x1);
                          const bottom = Math.max(defaultGhostFacingGroup.y1, item.y1);
                          const top = Math.min(defaultGhostFacingGroup.y2, item.y2);
                          const right = Math.min(defaultGhostFacingGroup.x2, item.x2);

                          return right > left && top > bottom;
                        });

                        if (defaultItemsOccupiedFacingDelta || defaultGhostFacingGroup.x1 < 0) {
                          canDefaultFacing = false;
                        }
                      }
                    }
                  }

                  if (canDefaultFacing) {
                    options.defaultFacingGroup = getFacingGroupByAltOrientation({
                      ...facingGroup,
                      id: generateUniqueId(),
                      x: defaultGhostFacingGroup.x1,
                      orientation_code: defaultOrientationCode,
                      capped_orientation_code: defaultCappedOrientationCode,
                      packaging: defaultPackaging,
                      cappedPackaging: defaultCappedPackaging,
                    }, products[facingGroup.product_num]);
                  }
                }
              }
            }
          }
        }
      } catch (e) {
        return options;
      }

      return options;
    }
  );

const locatedFixtureIdsSelector = createSelector(
  locatedFixturesHistorySelector,
  locatedFixtures => locatedFixtures.reduce((acc, val) => {
    acc[val.id] = [];

    return acc;
  }, {} as Record<string, Array<string>>)
);

export const uiAlertsSelector = createSelector(
  warningsSelector,
  historySelector,
  historyStepSelector,
  (warnings, history, historyStep) => {
    return historyStep === 0 || !history || !history[historyStep]
      ? processAlertsToDisplay(warnings)
      : processAlertsToDisplay(history[historyStep].alerts);
  },
);

export const isCriticalAlertExistSelector = createSelector(
  uiAlertsSelector,
  (uiAlertsSelector) => {
    const criticalWarnings = uiAlertsSelector.filter((alert) => criticalAlertTypes.includes(alert.warningType));
    const isCriticalWarningExist = uiAlertsSelector
      .map((alert) => criticalAlertTypes.includes(alert.warningType))
      .some(Boolean);

    return {
      isCriticalWarningExist,
      criticalWarning: criticalWarnings.length ? { ...criticalWarnings[0], selected: true } : null
    };
  },
);

export const isNonCriticalPriorityAlertSelector = createSelector(
  uiAlertsSelector,
  facingGroupsSelector,
  (uiAlertsSelector, facingGroups) => {
    const nonCriticalAlertsIndex = uiAlertsSelector
      .filter((alert) => !criticalAlertTypes.includes(alert.warningType))
      .findIndex(a => a.warningType === 'fd_dwo_bincap_warnings');
    const priorityWarnings = uiAlertsSelector.filter((alert) => alert.warningType === 'fd_dwo_bincap_warnings');
    const isPriorityWarningsExist = uiAlertsSelector
      .map((alert) => alert.warningType === 'fd_dwo_bincap_warnings')
      .some(Boolean);
    const facingGroupHorizonalFacingMapping = facingGroups.reduce((acc, cur) => {
      if (!acc[cur.product_num]) {
        acc[cur.product_num] = cur.horizontal_facings;
      }

      return acc;
    }, {} as Record<string, number>);
    let highlightAlert = false;

    if (priorityWarnings.length) {
      const data = priorityWarnings[0].data as Array<FdDwoBincapWarnings>;

      highlightAlert = data
        .map((d) => facingGroupHorizonalFacingMapping[d.product_num] > 1)
        .some(Boolean);
    }

    return {
      priorityItemIndex: Math.min(nonCriticalAlertsIndex),
      isPriorityWarningsExist,
      priorityWarning: priorityWarnings.length ? { ...priorityWarnings[0], selected: true } : null,
      highlightAlert
    };
  },
);

export const fixtureSpacesToFixtureIdSelector = createSelector(
  fixtureSpacesSelector,
  fixtures => fixtures.reduce((acc, val) => {
    acc[val.id] = {
      x1: val.x1,
      x2: val.x2,
      y1: val.y1,
      y2: val.y2
    };

    return acc;
  }, {} as Record<string, {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
  }>)
);

export const facingIdToFixtureIdsSelector = createSelector(
  facingGroupsHistorySelector,
  fixtureSpacesSelector,
  fixtureHeightSelector,
  locatedFixturesByIdSelector,
  (facingGroups, fixtureSpaces, fixtureHeight, locatedFixturesById) => {
    const shelfFacings = facingGroups
      .filter(facingGroup => facingGroup.fixtureType === FIXTURE_TYPES.SHELF || facingGroup.fixtureType === FIXTURE_TYPES.BASKET)
      .reduce((acc, facingGroup) => {
        const facingWidth = facingGroup.packaging.width * facingGroup.horizontal_facings;
        const facingHeight = getFacingHeight(
          facingGroup.packaging.height,
          facingGroup.vertical_facings,
          facingGroup.cap_nested,
          facingGroup.packaging.nested_height,
          facingGroup.cappedPackaging.height,
          facingGroup.capped_vertical_facings
        );
        const ghostFacingGroup = {
          x1: facingGroup.x,
          y1: facingGroup.y,
          x2: facingGroup.x + facingWidth,
          y2: facingGroup.y + facingHeight
        };
        const occupiedFacing = fixtureSpaces.filter((item) => {
          const left = Math.max(ghostFacingGroup.x1, item.x1);
          const bottom = Math.max(ghostFacingGroup.y1, item.y1);
          const top = Math.min(ghostFacingGroup.y2, item.y2);
          const right = Math.min(ghostFacingGroup.x2, item.x2);

          return right > left && top > bottom;
        }).map(item => item.id);

        return {
          ...acc,
          [facingGroup.id]: occupiedFacing,
        };
      }, {} as Record<string, Array<number>>);

    const pegboardFacings = facingGroups.filter(facingGroup => facingGroup.fixtureType === FIXTURE_TYPES.PEGBOARD)
      .reduce((acc, val) => {
        acc[val.id] = [val.located_fixture_id];

        return acc;
      }, {} as Record<string, Array<number>>);

    const crossbarFacings = facingGroups
      .filter(facingGroup => facingGroup.fixtureType === FIXTURE_TYPES.CROSSBAR)
      .reduce((acc, facingGroup) => {
        const facingWidth = facingGroup.packaging.width * facingGroup.horizontal_facings;
        const facingHeight = getFacingHeight(
          facingGroup.packaging.height,
          facingGroup.vertical_facings,
          facingGroup.cap_nested,
          facingGroup.packaging.nested_height,
          facingGroup.cappedPackaging.height,
          facingGroup.capped_vertical_facings
        );
        const ghostFacingGroup = {
          x1: facingGroup.x,
          y1: facingGroup.y,
          x2: facingGroup.x + facingWidth,
          y2: facingGroup.y + facingHeight
        };
        const occupiedFacing = fixtureHeight.filter((item) => {
          if (!item.isCrossbar) return false;

          const locatedFixtureId = facingGroup.located_fixture_id;
          const { y } = locatedFixturesById[locatedFixtureId];

          if (item.y1 !== y) return false;

          const left = Math.max(ghostFacingGroup.x1, item.x1);
          const bottom = Math.max(ghostFacingGroup.y1, item.y1);
          const top = Math.min(ghostFacingGroup.y2, item.y2);
          const right = Math.min(ghostFacingGroup.x2, item.x2);

          return right > left && top > bottom;
        }).map(item => item.id);

        return {
          ...acc,
          [facingGroup.id]: occupiedFacing,
        };
      }, {} as Record<string, Array<number>>);

    return {
      ...shelfFacings,
      ...pegboardFacings,
      ...crossbarFacings
    };
  });

export const locatedFixtureIdToFacingIdsSelector = createSelector(
  facingGroupsHistorySelector,
  fixtureSpacesSelector,
  fixtureHeightSelector,
  locatedFixturesByIdSelector,
  (facingGroups, fixtureSpaces, fixtureHeight, locatedFixturesById) => {
    const shelfFixtures = fixtureSpaces.reduce((acc, fixture) => {
      const occupiedFacing = facingGroups
        .filter(facingGroup => facingGroup.fixtureType === FIXTURE_TYPES.SHELF || facingGroup.fixtureType === FIXTURE_TYPES.BASKET)
        .filter(facingGroup => {
          const facingWidth = facingGroup.packaging.width * facingGroup.horizontal_facings;
          const facingHeight = getFacingHeight(
            facingGroup.packaging.height,
            facingGroup.vertical_facings,
            facingGroup.cap_nested,
            facingGroup.packaging.nested_height,
            facingGroup.cappedPackaging.height,
            facingGroup.capped_vertical_facings
          );
          const ghostFacingGroup = {
            x1: facingGroup.x,
            y1: facingGroup.y,
            x2: facingGroup.x + facingWidth,
            y2: facingGroup.y + facingHeight
          };
          const left = Math.max(ghostFacingGroup.x1, fixture.x1);
          const bottom = Math.max(ghostFacingGroup.y1, fixture.y1);
          const top = Math.min(ghostFacingGroup.y2, fixture.y2);
          const right = Math.min(ghostFacingGroup.x2, fixture.x2);

          return right > left && top > bottom;
        }).map(item => item.id);

      return {
        ...acc,
        [fixture.id]: occupiedFacing
      };
    }, {} as Record<string, Array<string>>);

    const pegboardFacings = facingGroups.filter(facingGroup => facingGroup.fixtureType === FIXTURE_TYPES.PEGBOARD)
      .reduce((acc, val) => {
        if (acc[val.located_fixture_id]) {
          acc[val.located_fixture_id].push(val.id);
        } else {
          acc[val.located_fixture_id] = [val.id];
        }

        return acc;
      }, {} as Record<string, Array<string>>);

    const crossbarFacings = fixtureHeight.reduce((acc, fixture) => {
      if (!fixture.isCrossbar) {
        return acc;
      }

      const occupiedFacing = facingGroups
        .filter(facingGroup => facingGroup.fixtureType === FIXTURE_TYPES.CROSSBAR)
        .filter(facingGroup => {
          const locatedFixtureId = facingGroup.located_fixture_id;
          const { y } = locatedFixturesById[locatedFixtureId];

          if (fixture.y1 !== y) return false;

          const facingWidth = facingGroup.packaging.width * facingGroup.horizontal_facings;
          const facingHeight = getFacingHeight(
            facingGroup.packaging.height,
            facingGroup.vertical_facings,
            facingGroup.cap_nested,
            facingGroup.packaging.nested_height,
            facingGroup.cappedPackaging.height,
            facingGroup.capped_vertical_facings
          );
          const ghostFacingGroup = {
            x1: facingGroup.x,
            y1: facingGroup.y,
            x2: facingGroup.x + facingWidth,
            y2: facingGroup.y + facingHeight
          };
          const left = Math.max(ghostFacingGroup.x1, fixture.x1);
          const bottom = Math.max(ghostFacingGroup.y1, fixture.y1);
          const top = Math.min(ghostFacingGroup.y2, fixture.y2);
          const right = Math.min(ghostFacingGroup.x2, fixture.x2);

          return right > left && top > bottom;
        }).map(item => item.id);

      return {
        ...acc,
        [fixture.id]: occupiedFacing
      };
    }, {} as Record<string, Array<string>>);

    return {
      ...shelfFixtures,
      ...pegboardFacings,
      ...crossbarFacings
    };
  }
);

export const locatedClipboardFixtureIdToFacingIdsSelector = createSelector(
  clipboardHistorySelector,
  locatedFixtureIdsSelector,
  clipboard => clipboard.newClipboardFixturesFacings.reduce((acc, val) => {
    const {
      id,
      located_fixture_id: locatedFixtureId
    } = val;

    if (acc[locatedFixtureId]) {
      acc[locatedFixtureId].push(id);
    } else {
      acc[locatedFixtureId] = [id];
    }

    return acc;
  }, {} as Record<string, Array<string>>)
);

export const draggableFixtureParamsSelector = createSelector(
  locatedFixtureIdToFacingIdsSelector,
  locatedClipboardFixtureIdToFacingIdsSelector,
  facingGroupsByIdsSelector,
  locatedFixturesByIdSelector,
  (locatedFixtureIdToFacingIds, locatedClipboardFixtureIdToFacingIds, facingGroupsByIds, locatedFixturesById) => {
    return Object.keys({
      ...locatedFixtureIdToFacingIds,
      ...locatedClipboardFixtureIdToFacingIds
    }).reduce((acc, fixtureId) => {
      const facingIds = locatedFixtureIdToFacingIds[fixtureId] || locatedClipboardFixtureIdToFacingIds[fixtureId] || [];

      if (!locatedFixturesById[fixtureId]) return acc;

      const { y, type, height } = locatedFixturesById[fixtureId];
      const fixtureFacingMinPosition = facingIds.length ? Math.min(...facingIds.map(id => facingGroupsByIds[id].y)) : y;
      const offsetDownFromFixture = y - fixtureFacingMinPosition;
      const fixtureFacingMaxPosition = facingIds.length ? Math.max(...facingIds.map(id => {
        const { y, packaging: { height: facingHeight }} = facingGroupsByIds[id];

        return y + facingHeight;
      })) : y + height;

      const offsetUpFromFixture = type === FIXTURE_TYPES.CROSSBAR
        ? fixtureFacingMaxPosition - y
        : 0;

      acc[fixtureId] = {
        offsetDownFromFixture,
        offsetUpFromFixture,
        fixtureFacingMinPosition,
        fixtureFacingMaxPosition
      };

      return acc;
    }, {} as Record<string, DraggableFixtureParams>);
  }
);

export const maxFacingHeightFixtureSelector = createSelector(
  locatedFixtureIdToFacingIdsSelector,
  locatedClipboardFixtureIdToFacingIdsSelector,
  facingGroupsByIdsSelector,
  draggableFixtureParamsSelector,
  locatedFixturesByIdSelector,
  (locatedFixtureToFacingIds, locatedClipboardFixtureToFacingIds, facings, draggableFixtureParams, locatedFixturesById) => {
    return [
      ...Object.keys(locatedFixtureToFacingIds),
      ...Object.keys(locatedClipboardFixtureToFacingIds)
    ].reduce((acc, val) => {
      const mergedFixtures = { ...locatedFixtureToFacingIds, ...locatedClipboardFixtureToFacingIds };
      const heights = Array.isArray(mergedFixtures[val]) ? mergedFixtures[val].reduce((collection, id) => {
        const facing = facings[id];
        const height = getFacingHeight(
          facing?.packaging?.height,
          facing?.vertical_facings,
          facing?.cap_nested,
          facing?.packaging?.nested_height,
          facing?.cappedPackaging?.height,
          facing?.capped_vertical_facings
        );

        collection.push(height);

        return collection;
      }, [] as Array<number>) : [];

      const maxHeight = heights.length ? Math.max(...heights) : 0;
      let offsetY = 0;

      if (locatedFixturesById[val] && locatedFixturesById[val].type === FIXTURE_TYPES.CROSSBAR) {
        if (maxHeight > 0) {
          const { fixtureFacingMaxPosition: ymax, fixtureFacingMinPosition: ymin } = draggableFixtureParams[val];
          const totalHeight = ymin + maxHeight;

          offsetY = ymax - totalHeight < 0 ? 0 : ymax - totalHeight;
        }
      }

      acc[val] = maxHeight + offsetY;

      return acc;
    }, {} as Record<string, number>);
  });

export const fixtureItemSpaceSelector = createSelector(
  fixtureShelfSelector,
  fixtures => fixtures.map(fixture => ({
    id: fixture.id,
    x1: fixture.x,
    x2: fixture.x + fixture.width,
    y1: fixture.y,
    y2: fixture.y + fixture.height
  }) as FixtureItemSpace)
);

export const fixtureSpaceSelectorByTypesSelector = (types: Array<string>) => createSelector(
  locatedFixturesHistorySelector,
  locatedFixtures => {
    const locatedFixturesByTypes = locatedFixtures
      .filter(locatedFixture => types.includes(locatedFixture.type));
    const locatedFixturesSpaces = locatedFixturesByTypes
      .map(locatedFixture => ({
        id: locatedFixture.id,
        x1: locatedFixture.x,
        x2: locatedFixture.x + locatedFixture.width,
        y1: locatedFixture.y,
        y2: locatedFixture.y + locatedFixture.height
      }));

    return locatedFixturesSpaces;
  }
);

export const fixtureSpaceMaxHeightSelectorByTypesSelector = (types: Array<string>) => createSelector(
  locatedFixturesHistorySelector,
  locatedFixtures => {
    const locatedFixturesByTypes = locatedFixtures
      .filter(locatedFixture => types.includes(locatedFixture.type));
    const locatedFixturesSpaces = locatedFixturesByTypes
      .map(locatedFixture => {
        const displayHeight = locatedFixture.is_display_only ? DISPLAY_HEIGHT : 0;
        const basketSlopeHeight = adjacentHeightToShelfSlopeAngle(locatedFixture.shelf_slope, locatedFixture.depth);
        const fixtureHeight = Math.max(basketSlopeHeight, locatedFixture.grille_height, displayHeight);

        return ({
          type: locatedFixture.type,
          id: locatedFixture.id,
          x1: locatedFixture.x,
          x2: locatedFixture.x + locatedFixture.width,
          y1: locatedFixture.y,
          y2: locatedFixture.y + locatedFixture.height + fixtureHeight
        }); }
      );

    return locatedFixturesSpaces;
  }
);

export const clipboardLocatedFixtureIdToFacingSelector = createSelector(
  clipboardHistorySelector,
  productsSelector,
  productInfoSelector,
  (clipboard, products, productsInfo) => {
    const { clipboardFixtureIdToFacings, clipboardFixturesSkus } = clipboard.newClipboardFixturesFacings
      .reduce((acc, facing) => {
        const product = products[facing.product_num];
        const productInfo = productsInfo[facing.product_num];

        const extendedFacingGroup: FacingGroupCipboard = {
          ...facing,
          productName: product?.product_english_desc,
          isProtoC: productInfo?.isProtoC || false,
          fcst: productInfo?.fcst || null,
          totalComm: productInfo?.totalComm || null
        };

        acc.clipboardFixturesSkus.push(extendedFacingGroup);

        if (acc.clipboardFixtureIdToFacings[facing.located_fixture_id]) {
          acc.clipboardFixtureIdToFacings[facing.located_fixture_id].push(extendedFacingGroup);
        } else {
          acc.clipboardFixtureIdToFacings[facing.located_fixture_id] = [extendedFacingGroup];
        }

        return acc;
      }, {
        clipboardFixtureIdToFacings: {} as Record<string, Array<FacingGroupCipboard>>,
        clipboardFixturesSkus: [] as Array<FacingGroupCipboard>
      }
      );

    const clipboardSkus = clipboard.clipboardSkus.map(facingGroup => {
      const product = products[facingGroup.product_num];
      const productInfo = productsInfo[facingGroup.product_num];

      const extendedFacingGroup = {
        ...facingGroup,
        productName: product?.product_english_desc,
        isProtoC: productInfo?.isProtoC || false,
        fcst: productInfo?.fcst || null,
        totalComm: productInfo?.totalComm || null
      };

      return extendedFacingGroup;
    });

    const clipboardExcludedSkus = clipboard.excludedSkus.map(facingGroup => {
      const product = products[facingGroup?.product_num];
      const productInfo = productsInfo[facingGroup?.product_num];

      const extendedFacingGroup = {
        ...facingGroup,
        productName: product?.product_english_desc,
        isProtoC: productInfo?.isProtoC || false,
        fcst: productInfo?.fcst || null,
        totalComm: productInfo?.totalComm || null
      };

      return extendedFacingGroup;
    });

    const clipboardExcludedByTetrisSkus = clipboard.excludedByTetrisSkus.map(facingGroup => {
      const product = products[facingGroup?.product_num];
      const productInfo = productsInfo[facingGroup?.product_num];

      const extendedFacingGroup = {
        ...facingGroup,
        productName: product?.product_english_desc,
        isProtoC: productInfo?.isProtoC || false,
        fcst: productInfo?.fcst || null,
        totalComm: productInfo?.totalComm || null
      };

      return extendedFacingGroup;
    });

    const clipboardNewSkus = clipboard.newSkus.map(facingGroup => {
      const product = products[facingGroup?.product_num];
      const productInfo = productsInfo[facingGroup?.product_num];

      const extendedFacingGroup = {
        ...facingGroup,
        productName: product?.product_english_desc,
        isProtoC: productInfo?.isProtoC || false,
        fcst: productInfo?.fcst || null,
        totalComm: productInfo?.totalComm || null
      };

      return extendedFacingGroup;
    });

    const clipboardSkusWithCommitments = clipboard.skusWithCommitments.map(facingGroup => {
      const product = products[facingGroup?.product_num];
      const productInfo = productsInfo[facingGroup?.product_num];

      const extendedFacingGroup = {
        ...facingGroup,
        productName: product?.product_english_desc,
        isProtoC: productInfo?.isProtoC || false,
        fcst: productInfo?.fcst || null,
        totalComm: productInfo?.totalComm || null
      };

      return extendedFacingGroup;
    });

    const clipboardExcludedSkusSort = orderBy(
      clipboardExcludedSkus,
      [
        item => item.totalComm === null ? '' : item.totalComm,
        item => item.fcst === null ? '' : item.fcst,
      ],
      ['desc', 'desc']
    );

    const clipboardExcludedByTetrisSkusSort = orderBy(
      clipboardExcludedByTetrisSkus,
      [
        item => item.totalComm === null ? '' : item.totalComm,
        item => item.fcst === null ? '' : item.fcst,
      ],
      ['desc', 'desc']
    );

    return {
      clipboardSkus,
      clipboardFixturesSkus,
      clipboardFixtureIdToFacings,
      clipboardExcludedSkus: clipboardExcludedSkusSort,
      clipboardExcludedByTetrisSkus: clipboardExcludedByTetrisSkusSort,
      clipboardNewSkus,
      clipboardSkusWithCommitments,
    };
  }
);

export const clipboardLocatedFixtureIdSelector = (fixtureId: number) => createSelector(
  clipboardLocatedFixtureIdToFacingSelector,
  clipboardLocatedFixtureIdToFacing => {
    const facings = clipboardLocatedFixtureIdToFacing.clipboardFixtureIdToFacings[fixtureId] || [];

    return orderBy(
      facings,
      [
        item => item.totalComm === null ? '' : item.totalComm,
        item => item.fcst === null ? '' : item.fcst,
      ],
      ['desc', 'desc']
    );
  }
);

export const facingGroupsByFixtureIdSelector = (id: number) => createSelector(
  facingGroupsSelector,
  (facingGroups) => facingGroups.filter(facingGroup => facingGroup.located_fixture_id === id)
);

export const canAddFixtureSelector = (
  contextMenuSegmentData: ContextMenuSegmentData | null,
  zoom: number,
) =>
  createSelector(
    fixtureSpaceMaxHeightSelectorByTypesSelector(['SHELF', 'BASKET', 'CROSSBAR']),
    itemsCoordinatesSelector,
    currentFixtureNumberIdSelector,
    aisleDepthSelector,
    aisleHeightSelector,
    locatedFixturesByIdSelector,
    (fixtureItemSpace, itemsCoordinates, currentFixtureNumber, aisleDepth, aisleHeight, locatedFixturesById) => {
      let addFixture = null;

      if (contextMenuSegmentData) {
        const { x, y, container } = contextMenuSegmentData;
        const { left, top, height } = container.getBoundingClientRect();
        const xPoint = (x - left) / zoom;
        let yPoint = parseFloat(((height - (y - top)) / zoom).toFixed(2));
        const y2 = yPoint + SHELF_TEMPLATE.height;

        if (y2 > aisleHeight) {
          yPoint = aisleHeight - SHELF_TEMPLATE.height;
        }

        const segment = Math.trunc(xPoint / SEGMENT_WIDTH);
        const xShelf = segment * SEGMENT_WIDTH;
        const fixtureSegment = segment + 1;

        const fixture_id = generateFixtureId(
          SHELF_TEMPLATE.width,
          SHELF_TEMPLATE.height,
          aisleDepth,
          FIXTURE_TYPES.SHELF
        );

        let fixture: LocatedFixture = {
          ...SHELF_TEMPLATE,
          fixture_id,
          id: currentFixtureNumber + 1,
          x: xShelf,
          y: yPoint,
          depth: aisleDepth,
          type: 'SHELF',
          segment: fixtureSegment,
        };

        let cloneFixtureSpace = null;

        const fixtureItemSpaceShelf = fixtureItemSpace
          .filter(fixture => {
            const locatedFixture = locatedFixturesById[fixture.id];

            if (locatedFixture) {
              return locatedFixture.type === FIXTURE_TYPES.SHELF && !locatedFixture.is_display_only;
            }

            return false;
          });

        cloneFixtureSpace = fixtureItemSpaceShelf
          .filter(fixture => {
            const locatedFixture = locatedFixturesById[fixture.id];

            if (locatedFixture) {
              return locatedFixture.segment === fixtureSegment;
            }

            return false;
          })
          .sort((a, b) => Math.abs(a.y1 - yPoint) - Math.abs(b.y1 - yPoint))[0];

        if (!cloneFixtureSpace) {
          cloneFixtureSpace = fixtureItemSpaceShelf
            .sort((a, b) => {
              const distanceA = Math.sqrt(Math.pow(a.x1 - xShelf, 2) + Math.pow(a.y1 - yPoint, 2));
              const distanceB = Math.sqrt(Math.pow(b.x1 - xShelf, 2) + Math.pow(b.y1 - yPoint, 2));

              return distanceA - distanceB;
            })[0];
        }

        if (cloneFixtureSpace) {
          const cloneFixture = locatedFixturesById[cloneFixtureSpace.id];

          if (cloneFixture) {
            fixture = {
              ...cloneFixture,
              ...DEFAULT_TEMPLATE,
              id: currentFixtureNumber + 1,
              x: xShelf,
              y: yPoint,
              segment: fixtureSegment,
            };
          }
        }

        // height alignment
        let overlapLocatedFixtures = fixtureItemSpaceShelf
          .filter(locatedFixture => locatedFixturesById[locatedFixture.id].segment === fixture.segment - 1)
          .filter(locatedFixture => {
            const bottom = Math.max(fixture.y, locatedFixture.y1);
            const top = Math.min(fixture.y + fixture.height, locatedFixture.y2);

            return top >= bottom;
          })[0];

        if (!overlapLocatedFixtures) {
          overlapLocatedFixtures = fixtureItemSpaceShelf
            .filter(locatedFixture => locatedFixturesById[locatedFixture.id].segment === fixture.segment + 1)
            .filter(locatedFixture => {
              const bottom = Math.max(fixture.y, locatedFixture.y1);
              const top = Math.min(fixture.y + fixture.height, locatedFixture.y2);

              return top >= bottom;
            })[0];
        }

        if (overlapLocatedFixtures) {
          fixture = {
            ...fixture,
            y: overlapLocatedFixtures.y1,
          };
        }
        // end height alignment

        const ghostFixture = {
          id: fixture.id,
          x1: fixture.x,
          x2: fixture.x + fixture.width,
          y1: fixture.y,
          y2: fixture.y + fixture.height,
        };

        const overlapFixture = fixtureItemSpace.some(item => {
          const left = Math.max(ghostFixture.x1, item.x1);
          const bottom = Math.max(ghostFixture.y1, item.y1);
          const top = Math.min(ghostFixture.y2, item.y2);
          const right = Math.min(ghostFixture.x2, item.x2);

          return right > left && top > bottom;
        });

        if (!overlapFixture) {
          const overlapFacing = itemsCoordinates.some(item => {
            const left = Math.max(ghostFixture.x1, item.x1);
            const bottom = Math.max(ghostFixture.y1, item.y1);
            const top = Math.min(ghostFixture.y2, item.y2);
            const right = Math.min(ghostFixture.x2, item.x2);

            return right > left && top > bottom;
          });

          if (!overlapFacing) {
            addFixture = fixture;
          }
        }

      }

      return {
        addFixture,
      };
    }
  );

export const canAddCrossbarSelector = (
  contextMenuSegmentData: ContextMenuSegmentData | null,
  zoom: number,
) => createSelector(
  locatedFixturesHistorySelector,
  fixtureSpaceMaxHeightSelectorByTypesSelector(['SHELF', 'BASKET', 'CROSSBAR']),
  itemsCoordinatesSelector,
  currentFixtureNumberIdSelector,
  aisleDepthSelector,
  aisleHeightSelector,
  (locatedFixturesHistory, locatedFixtures, itemsCoordinates, currentFixtureNumberId, aisleDepth, aisleHeight) => {
    let addCrossbar = null;

    if (contextMenuSegmentData) {
      const { x, y, container } = contextMenuSegmentData;
      const { left, top, height } = container.getBoundingClientRect();
      const xCrossbar = (x - left) / zoom;
      let yPoint = parseFloat(((height - (y - top)) / zoom).toFixed(2));
      const segment = Math.trunc(xCrossbar / SEGMENT_WIDTH);
      const xPoint = segment * SEGMENT_WIDTH;
      const fixtureSegment = segment + 1;
      const fixture_id = generateFixtureId(
        CROSSBAR_TEMPLATE.width,
        CROSSBAR_TEMPLATE.height,
        aisleDepth,
        FIXTURE_TYPES.CROSSBAR
      );
      const y2 = yPoint + CROSSBAR_TEMPLATE.height;

      if (y2 > aisleHeight) {
        yPoint = aisleHeight - CROSSBAR_TEMPLATE.height;
      }

      let fixture: LocatedFixture = {
        ...CROSSBAR_TEMPLATE,
        fixture_id,
        id: currentFixtureNumberId + 1,
        x: xPoint,
        y: yPoint,
        depth: aisleDepth,
        segment: fixtureSegment,
      };

      let cloneCrossbar = null;

      const locatedFixturesCrossbar = locatedFixturesHistory
        .filter(locatedFixture => locatedFixture.type === 'CROSSBAR');

      cloneCrossbar = locatedFixturesCrossbar
        .filter(locatedFixture => locatedFixture.segment === fixtureSegment)
        .sort((a, b) => Math.abs(a.y - yPoint) - Math.abs(b.y - yPoint))[0];

      if (!cloneCrossbar) {
        cloneCrossbar = locatedFixturesCrossbar
          .sort((a, b) => {
            const distanceA = Math.sqrt(Math.pow(a.x - xPoint, 2) + Math.pow(a.y - yPoint, 2));
            const distanceB = Math.sqrt(Math.pow(b.x - xPoint, 2) + Math.pow(b.y - yPoint, 2));

            return distanceA - distanceB;
          })[0];
      }

      if (cloneCrossbar) {
        fixture = {
          ...cloneCrossbar,
          ...DEFAULT_TEMPLATE,
          id: currentFixtureNumberId + 1,
          x: xPoint,
          y: yPoint,
          segment: fixtureSegment,
        };
      }

      // height alignment
      let overlapLocatedFixturesCrossbar = locatedFixturesCrossbar
        .filter(locatedFixture => locatedFixture.segment === fixture.segment - 1)
        .filter(locatedFixture => {
          const bottom = Math.max(fixture.y, locatedFixture.y);
          const top = Math.min(fixture.y + fixture.height, locatedFixture.y + locatedFixture.height);

          return top >= bottom;
        })[0];

      if (!overlapLocatedFixturesCrossbar) {
        overlapLocatedFixturesCrossbar = locatedFixturesCrossbar
          .filter(locatedFixture => locatedFixture.segment === fixture.segment + 1)
          .filter(locatedFixture => {
            const bottom = Math.max(fixture.y, locatedFixture.y);
            const top = Math.min(fixture.y + fixture.height, locatedFixture.y + locatedFixture.height);

            return top >= bottom;
          })[0];
      }

      if (overlapLocatedFixturesCrossbar) {
        fixture = {
          ...fixture,
          y: overlapLocatedFixturesCrossbar.y,
        };
      }
      // end height alignment

      const ghostFixture = {
        id: fixture.id,
        x1: fixture.x,
        x2: fixture.x + fixture.width,
        y1: fixture.y,
        y2: fixture.y + fixture.height,
      };

      const overlapFixture = locatedFixtures.some(item => {
        const left = Math.max(ghostFixture.x1, item.x1);
        const bottom = Math.max(ghostFixture.y1, item.y1);
        const top = Math.min(ghostFixture.y2, item.y2);
        const right = Math.min(ghostFixture.x2, item.x2);

        return right > left && top > bottom;
      });

      if (!overlapFixture) {
        const overlapFacing = itemsCoordinates.some(item => {
          const left = Math.max(ghostFixture.x1, item.x1);
          const bottom = Math.max(ghostFixture.y1, item.y1);
          const top = Math.min(ghostFixture.y2, item.y2);
          const right = Math.min(ghostFixture.x2, item.x2);

          return right > left && top > bottom;
        });

        if (!overlapFacing) {
          addCrossbar = fixture;
        }
      }
    }

    return addCrossbar;
  }
);

export const canAddPegboardSelector = (
  contextMenuSegmentData: ContextMenuSegmentData | null,
  zoom: number,
) => createSelector(
  locatedFixturesHistorySelector,
  aisleHeightSelector,
  currentFixtureNumberIdSelector,
  (locatedFixtures, aisleHeight, currentFixtureNumberId) => {
    let addFixture: LocatedFixture | null = null;

    if (contextMenuSegmentData) {
      const { x, y, container } = contextMenuSegmentData;
      const { left, top, height } = container.getBoundingClientRect();
      const xPoint = (x - left) / zoom;
      const yPoint = parseFloat(((height - (y - top)) / zoom).toFixed(2));
      const segment = Math.trunc(xPoint / SEGMENT_WIDTH);
      const fixtureSegment = segment + 1;

      const locatedFixturesPegboard = locatedFixtures
        .filter(locatedFixture => locatedFixture.type === 'PEGBOARD');

      const isPogboardSameSegment = locatedFixturesPegboard.some(
        locatedFixture => locatedFixture.segment === fixtureSegment
      );

      if (isPogboardSameSegment) return addFixture;

      const [nearPegboard] = locatedFixturesPegboard.sort((a, b) => {
        const distanceA = Math.sqrt(Math.pow(a.x - xPoint, 2) + Math.pow(a.y - yPoint, 2));
        const distanceB = Math.sqrt(Math.pow(b.x - xPoint, 2) + Math.pow(b.y - yPoint, 2));

        return distanceA - distanceB;
      });

      if (nearPegboard) {
        const nearShiftX = nearPegboard.x - (nearPegboard.segment - 1) * SEGMENT_WIDTH;
        const pegboardX = segment * SEGMENT_WIDTH + nearShiftX;

        addFixture = {
          ...nearPegboard,
          ...DEFAULT_TEMPLATE,
          id: currentFixtureNumberId + 1,
          segment: fixtureSegment,
          x: pegboardX
        };
      }

      if (!addFixture) {
        const [minLocatedFixture] = locatedFixtures
          .filter(locatedFixture => locatedFixture.segment === fixtureSegment)
          .sort((a, b) => a.y - b.y);
        const pegboardDepth = PEGBOARD_TEMPLATE.depth;

        const pegboardId = generateFixtureId(
          SHELF_TEMPLATE.width,
          SHELF_TEMPLATE.height,
          pegboardDepth,
          FIXTURE_TYPES.SHELF
        );
        const pegboardX = segment * SEGMENT_WIDTH + PEGBOARD_X_OFFSET;
        const pegboardY = minLocatedFixture?.y || 7;
        const pegboardHeight = aisleHeight - pegboardY;

        addFixture = {
          ...PEGBOARD_TEMPLATE,
          id: currentFixtureNumberId + 1,
          segment: fixtureSegment,
          fixture_id: pegboardId,
          y: pegboardY,
          x: pegboardX,
          height: pegboardHeight,
        };
      }
    }

    return addFixture;
  }
);

export const canAddBasketSelector = (
  contextMenuSegmentData: ContextMenuSegmentData | null,
  zoom: number,
) => createSelector(
  fixtureSpaceMaxHeightSelectorByTypesSelector(['SHELF', 'BASKET', 'CROSSBAR']),
  itemsCoordinatesSelector,
  currentFixtureNumberIdSelector,
  aisleDepthSelector,
  locatedFixturesByIdSelector,
  aisleHeightSelector,
  fixtureShelfSelector,
  (
    fixtureItemSpace,
    itemsCoordinates,
    currentFixtureNumber,
    aisleDepth,
    locatedFixturesById,
    aisleHeight,
    fixtureShelf
  ) => {
    let addBasket: Record<number, LocatedFixture> = {};

    if (contextMenuSegmentData) {
      const { x, y, container } = contextMenuSegmentData;
      const { left, top, height } = container.getBoundingClientRect();
      const xPoint = (x - left) / zoom;
      const yBasket = parseFloat(((height - (y - top)) / zoom).toFixed(2));
      const segment = Math.trunc(xPoint / SEGMENT_WIDTH);
      const xBasket = segment * SEGMENT_WIDTH;
      const fixtureSegment = segment + 1;

      addBasket = BASKET_PARAMS.reduce((acc, param) => {
        const fixture_id = generateFixtureId(
          BASKET_TEMPLATE.width,
          BASKET_TEMPLATE.height,
          aisleDepth,
          FIXTURE_TYPES.BASKET
        );
        let basket: LocatedFixture = {
          ...BASKET_TEMPLATE,
          id: currentFixtureNumber + 1,
          fixture_id,
          x: xBasket,
          y: yBasket,
          depth: aisleDepth,
          shelf_slope: param.shelfSlope,
          grille_height: param.grille,
          segment: fixtureSegment,
        };

        let cloneBasketSpace = null;

        const fixtureItemSpaceShelf = fixtureItemSpace
          .filter(fixture => {
            const locatedFixture = locatedFixturesById[fixture.id];

            if (locatedFixture) {
              return (
                locatedFixture.type === FIXTURE_TYPES.BASKET &&
                locatedFixture.grille_height === param.grille &&
                locatedFixture.shelf_slope === param.shelfSlope
              );
            }

            return false;
          });

        cloneBasketSpace = fixtureItemSpaceShelf
          .filter(fixture => {
            const locatedFixture = locatedFixturesById[fixture.id];

            if (locatedFixture) {
              return locatedFixture.segment === fixtureSegment;
            }

            return false;
          })
          .sort((a, b) => Math.abs(a.y1 - yBasket) - Math.abs(b.y1 - yBasket))[0];

        if (!cloneBasketSpace) {
          cloneBasketSpace = fixtureItemSpaceShelf
            .sort((a, b) => {
              const distanceA = Math.sqrt(Math.pow(a.x1 - xBasket, 2) + Math.pow(a.y1 - yBasket, 2));
              const distanceB = Math.sqrt(Math.pow(b.x1 - xBasket, 2) + Math.pow(b.y1 - yBasket, 2));

              return distanceA - distanceB;
            })[0];
        }

        if (cloneBasketSpace) {
          const cloneFixture = locatedFixturesById[cloneBasketSpace.id];

          if (cloneFixture) {
            basket = {
              ...cloneFixture,
              ...DEFAULT_TEMPLATE,
              id: currentFixtureNumber + 1,
              x: xBasket,
              y: yToShelfSlopeOffset(yBasket, param.shelfSlope, cloneFixture.depth),
              segment: fixtureSegment,
            };
          }
        }

        const basketSlopeHeight = adjacentHeightToShelfSlopeAngle(basket.shelf_slope, basket.depth);
        const basketHeight = Math.max(basketSlopeHeight, basket.height, basket.grille_height);

        let ghostBasket = {
          id: basket.id,
          x1: basket.x,
          x2: basket.x + basket.width,
          y1: basket.y,
          y2: basket.y + basketHeight,
        };

        // height alignment
        let correctionFixtureY = basket.y;
        const [crossoverlocatedFixture] = fixtureShelf.filter(locatedFixture => {
          if (
            locatedFixture.type === FIXTURE_TYPES.CROSSBAR ||
            locatedFixture.type === FIXTURE_TYPES.PEGBOARD ||
            locatedFixture.is_display_only ||
            locatedFixture.is_base_shelf
          ) {
            return false;
          }

          const isNear = Math.abs(locatedFixture.segment - basket.segment) === 1;

          if (!isNear) return false;

          const fixtureRect = {
            y1: locatedFixture.y,
            y2: locatedFixture.y + locatedFixture.height + locatedFixture.grille_height,
          };
          const bottom = Math.max(ghostBasket.y1, fixtureRect.y1);
          const top = Math.min(ghostBasket.y2, fixtureRect.y2);

          return top >= bottom;
        }).sort((fixtureA, fixtureB) => {
          const diffA = fixtureA.y - basket.y;
          const diffB = fixtureB.y - basket.y;

          return diffA - diffB;
        });

        if (crossoverlocatedFixture) {
          const top = crossoverlocatedFixture.y + crossoverlocatedFixture.height;

          correctionFixtureY = top - basket.height;
          basket.y = correctionFixtureY;
          ghostBasket = {
            ...ghostBasket,
            y1: correctionFixtureY,
            y2: correctionFixtureY + basketHeight,
          };
        }

        const overlapFixtures = fixtureItemSpace.filter(item => {
          const left = Math.max(ghostBasket.x1, item.x1);
          const bottom = Math.max(ghostBasket.y1, item.y1);
          const top = Math.min(ghostBasket.y2, item.y2);
          const right = Math.min(ghostBasket.x2, item.x2);

          return right > left && top > bottom;
        });

        const overlapFacing = itemsCoordinates.filter(item => {
          const left = Math.max(ghostBasket.x1, item.x1);
          const bottom = Math.max(ghostBasket.y1, item.y1);
          const top = Math.min(ghostBasket.y2, item.y2);
          const right = Math.min(ghostBasket.x2, item.x2);

          return right > left && top > bottom;
        });

        const [topOverlapFixture] = overlapFixtures.sort((a, b) => b.y2 - a.y2);
        const [topOverlapFacing] = overlapFacing.sort((a, b) => b.y2 - a.y2);

        const maxOverlapY = Math.max(topOverlapFixture?.y2 || 0, topOverlapFacing?.y2 || 0);

        if (maxOverlapY) {
          basket = {
            ...basket,
            y: maxOverlapY + 1,
          };
          ghostBasket = {
            id: basket.id,
            x1: basket.x,
            x2: basket.x + basket.width,
            y1: basket.y,
            y2: basket.y + basketHeight,
          };
        }

        if (ghostBasket.y2 > aisleHeight) {
          const shiftY = ghostBasket.y2 - aisleHeight;

          basket = {
            ...basket,
            y: basket.y - shiftY,
          };
          ghostBasket = {
            id: basket.id,
            x1: basket.x,
            x2: basket.x + basket.width,
            y1: basket.y,
            y2: basket.y + basketHeight,
          };
        }

        const overlapFixturesNext = fixtureItemSpace.some(item => {
          const left = Math.max(ghostBasket.x1, item.x1);
          const bottom = Math.max(ghostBasket.y1, item.y1);
          const top = Math.min(ghostBasket.y2, item.y2);
          const right = Math.min(ghostBasket.x2, item.x2);

          return right > left && top > bottom;
        });

        if (!overlapFixturesNext) {
          const overlapFacingNext = itemsCoordinates.some(item => {
            const left = Math.max(ghostBasket.x1, item.x1);
            const bottom = Math.max(ghostBasket.y1, item.y1);
            const top = Math.min(ghostBasket.y2, item.y2);
            const right = Math.min(ghostBasket.x2, item.x2);

            return right > left && top > bottom;
          });

          if (!overlapFacingNext) {
            return {
              ...acc,
              [param.id]: basket,
            };
          }
        }

        return acc;
      }, {} as Record<number, LocatedFixture>);
    }

    return addBasket;
  }
);

export const addSegmentSelector = (
  contextMenuSegmentData: ContextMenuSegmentData | null,
  zoom: number,
) => createSelector(
  locatedFixturesHistorySelector,
  facingGroupsHistorySelector,
  facingGroupsByIdsSelector,
  currentFixtureNumberIdSelector,
  itemsCoordinatesSelector,
  aisleGeometryBlocksHistorySelector,
  numberOfSegmentsSelector,
  (
    locatedFixturesHistory,
    facingGroupsHistory,
    facingGroupsByIds,
    currentFixtureNumberId,
    itemsCoordinates,
    aisleGeometryBlocksHistory,
    numberOfSegments,
  ) => {
    if (contextMenuSegmentData) {
      const { x, container } = contextMenuSegmentData;
      const { left } = container.getBoundingClientRect();
      const xPoint = (x - left) / zoom;

      const segment = Math.trunc(xPoint / SEGMENT_WIDTH);
      const fixtureSegment = segment + 1;
      const segmentRightX = fixtureSegment * SEGMENT_WIDTH;

      const removedFixtureIds = locatedFixturesHistory
        .filter(lf => lf.segment > fixtureSegment)
        .map((lf, index) => lf.id);

      const removedId = itemsCoordinates.filter(item => item.x1 >= segmentRightX).map(f => f.id);

      const newFixturesObj: Record<number, LocatedFixture> = locatedFixturesHistory
        .filter(lf => lf.segment > segment)
        .reduce((acc, lf, index) => ({
          ...acc,
          [lf.id]: {
            ...lf,
            segment: lf.segment + 1,
            id: currentFixtureNumberId + index + 1,
            x: lf.x + SEGMENT_WIDTH,
          }
        }), {});

      const newFixtures: Array<LocatedFixture> = Object.values(newFixturesObj);

      const newFacings = facingGroupsHistory
        .filter(facingGroup => facingGroup.x >= segmentRightX)
        .reduce((acc, facing) => {
          const lf = newFixturesObj[facing.located_fixture_id];

          if (lf) {
            const x = facing.x + SEGMENT_WIDTH;
            const segment = Math.trunc(x / SEGMENT_WIDTH) + 1;

            acc.push({
              ...facing,
              located_fixture_id: lf.id,
              x,
              segment,
              id: generateUniqueId(),
            });
          }

          return acc;
        }, [] as Array<FacingGroup>);

      const blocks = aisleGeometryBlocksHistory ? updateBlocks(aisleGeometryBlocksHistory, fixtureSegment, 1) : [];

      return {
        newFixtures,
        newFacings,
        removedFixtureIds,
        removedId,
        blocks,
      };
    }

    return null;
  }
);

export const deleteSegmentSelector = (
  contextMenuSegmentData: ContextMenuSegmentData | null,
  zoom: number,
) => createSelector(
  locatedFixturesHistorySelector,
  facingGroupsHistorySelector,
  facingGroupsByIdsSelector,
  currentFixtureNumberIdSelector,
  itemsCoordinatesSelector,
  aisleGeometryBlocksHistorySelector,
  numberOfSegmentsSelector,
  uniquePlanogramFacingsSelector,
  (
    locatedFixturesHistory,
    facingGroupsHistory,
    facingGroupsByIds,
    currentFixtureNumberId,
    itemsCoordinates,
    aisleGeometryBlocksHistory,
    numberOfSegments,
    uniquePlanogramFacings
  ) => {
    if (contextMenuSegmentData && numberOfSegments > 1) {
      const { x, container } = contextMenuSegmentData;
      const { left } = container.getBoundingClientRect();
      const xPoint = (x - left) / zoom;

      const segment = Math.trunc(xPoint / SEGMENT_WIDTH);
      const fixtureSegment = segment + 1;
      const segmentLeftX = segment * SEGMENT_WIDTH;
      const segmentRightX = fixtureSegment * SEGMENT_WIDTH;
      const segmentRightTotal = numberOfSegments * SEGMENT_WIDTH;

      const removedFixtureIds = locatedFixturesHistory
        .filter(lf => lf.segment === fixtureSegment)
        .map((lf, index) => lf.id);

      const removedId = itemsCoordinates.filter(item => {
        const left = Math.max(segmentLeftX, item.x1);
        const right = Math.min(segmentRightTotal, item.x2);

        return right > left;
      }).map(f => f.id);

      const removedIdBySelectedSegment = itemsCoordinates.filter(item => {
        const left = Math.max(segmentLeftX, item.x1);
        const right = Math.min(segmentRightX, item.x2);

        return right > left;
      }).map(f => f.id);

      const removedSku = removedIdBySelectedSegment.map(id => facingGroupsByIds[id]);

      const newFixturesObj: Record<number, LocatedFixture> = locatedFixturesHistory
        .filter(lf => lf.segment > fixtureSegment)
        .reduce((acc, lf, index) => ({
          ...acc,
          [lf.id]: {
            ...lf,
            segment: lf.segment - 1,
            id: currentFixtureNumberId + index + 1,
            x: lf.x - SEGMENT_WIDTH,
          }
        }), {});

      const newFixtures: Array<LocatedFixture> = Object.values(newFixturesObj);
      const removedFixtureIdsRight: Array<number> = Object.keys(newFixturesObj).map(Number);

      const newFacings = facingGroupsHistory
        .filter(facingGroup => facingGroup.x >= segmentRightX)
        .reduce((acc, facing) => {
          const lf = newFixturesObj[facing.located_fixture_id];

          if (lf) {
            const x = facing.x - SEGMENT_WIDTH;
            const segment = Math.trunc(x / SEGMENT_WIDTH) + 1;

            acc.push({
              ...facing,
              x,
              segment,
              located_fixture_id: lf.id,
              id: generateUniqueId(),
            });
          }

          return acc;
        }, [] as Array<FacingGroup>);

      const blocks = aisleGeometryBlocksHistory
        ? updateBlocks(aisleGeometryBlocksHistory, fixtureSegment, -1).filter(block => block.num_segments > 0)
        : [];

      const newClipboardFacings = filterByUniqueSku(removedSku, uniquePlanogramFacings);

      return {
        removedFixtureIds: removedFixtureIds.concat(removedFixtureIdsRight),
        newClipboardFacings,
        newFixtures,
        removedId,
        newFacings,
        blocks,
      };
    }

    return null;
  }
);

export const clipboardSkusSelector = createSelector(
  clipboardLocatedFixtureIdToFacingSelector,
  ({ clipboardFixturesSkus, clipboardSkus }) => clipboardFixturesSkus
    .concat(clipboardSkus)
    .map(f => ({ [f.id]: f.product_num }))
);

export const highlightedSkusSelector = createSelector(
  facingGroupsHistorySelector,
  clipboardHistorySelector,
  facingGroupsByIdsSelector,
  selectedFacingsSelector,
  districtTypeSelector,
  (facingGroups, { clipboardSkus, newClipboardFixturesFacings, excludedSkus }, facingGroupsByIds, selectedFacings, districtType) => {
    const selectedFacingSkus = Object.keys(selectedFacings).map(id => facingGroupsByIds[id]?.product_num);

    switch (districtType) {
      case DISTRICT_TYPES.CLIPBOARD: {
        return selectedFacingSkus.length !== 0
          ? facingGroups.reduce((acc, val) => {
            if (selectedFacingSkus.includes(val.product_num)) {
              acc.push(val.id);
            }

            return acc;

          }, [] as Array<string>
          )
          : [];
      }

      case DISTRICT_TYPES.PLANOGRAM: {
        return selectedFacingSkus.length !== 0
          ? [
            ...clipboardSkus,
            ...excludedSkus,
            ...newClipboardFixturesFacings
          ].reduce((acc, val) => {
            if (selectedFacingSkus.includes(val.product_num)) {
              acc.push(val.id);
            }

            return acc;
          }, [] as Array<string>
          )
          : [];
      }

      default: {
        return [];
      }
    }
  }
);

export const clipboardSkuToFacingGroupsSelector = createSelector(
  clipboardLocatedFixtureIdToFacingSelector,
  ({ clipboardSkus }) => clipboardSkus.reduce((acc, val) => {

    return {
      ...acc,
      [val.product_num]: val,
    };
  }, {} as Record<string, FacingGroup>)
);

export const clipboardFixtureIdsSelector = createSelector(
  clipboardFixturesSelector,
  fixtures => fixtures.map(f => f.id)
);

export const arrangeFixturesByIdSelector = (id: number) => createSelector(
  clipboardFixtureIdsSelector,
  selectedFixturesSelector,
  (fixtureIds, selectedFixtureIds) => {
    const fixtures = selectedFixtureIds
      .filter(fixtureId => fixtureId !== id)
      .sort((a, b) => fixtureIds.indexOf(b) - fixtureIds.indexOf(a));

    return [id, ...fixtures];
  }
);

export const draggableFixturesSelector = (id: number, isClipboard: boolean = true) => createSelector(
  maxFacingHeightFixtureSelector,
  locatedFixturesByIdSelector,
  draggableFixtureParamsSelector,
  arrangeFixturesByIdSelector(id),
  (maxProductHeightOnFixtures, locatedFixturesById, draggableFixtureParams, arrangeFixturesById) => arrangeFixturesById
    .reduce((acc, fixtureId, index) => {
      const draggableFixture = locatedFixturesById[id];
      const fixture = locatedFixturesById[fixtureId];
      const isBasket = fixture.type === FIXTURE_TYPES.BASKET;
      let fixtureYOffset = 0;

      switch (fixture.type) {
        case FIXTURE_TYPES.PEGBOARD: {
          const x = fixture.x;
          const y = fixture.y;
          const width = fixture.width;
          const height = fixture.height;
          let left = x - draggableFixture.x;
          let bottom = y - draggableFixture.y - draggableFixture.grille_height - draggableFixture.height;

          if (isClipboard) {
            left = 0;
            bottom = index === 0
              ? y - draggableFixture.y
              : acc[index - 1].bottom + acc[index - 1].height + VERTICAL_SPACING;
          }

          acc.push({ id: fixture.id, width, height, left, bottom, fixtureYOffset });

          return acc;
        }

        case FIXTURE_TYPES.CROSSBAR: {
          const maxFacingHeight = maxProductHeightOnFixtures[fixtureId] || 0;
          const draggableParams = draggableFixtureParams[fixtureId] || { offsetDownFromFixture: 0 };
          const x = fixture.x;
          const y = fixture.y;
          const width = fixture.width;
          const height = maxFacingHeight > 0 ? maxFacingHeight : fixture.height;
          let left = x - draggableFixture.x;
          let bottom = y - draggableFixture.y - draggableParams.offsetDownFromFixture - fixture.height;

          if (isClipboard) {
            left = 0;
            bottom = index === 0
              ? y - draggableFixture.y
              : acc[index - 1].bottom + acc[index - 1].height + VERTICAL_SPACING;
          }

          fixtureYOffset = maxFacingHeight > 0
            ? Math.abs(draggableParams.offsetUpFromFixture) - fixture.height / 2
            : 0;

          acc.push({ id: fixture.id, width, height, left, bottom, fixtureYOffset });

          return acc;
        }

        default: {
          const isDisplayOnly = fixture.is_display_only;
          const x = fixture.x;
          const y = fixture.y;
          const width = fixture.width;
          let left = x - draggableFixture.x;
          let bottom = y - draggableFixture.y - draggableFixture.grille_height - draggableFixture.height;
          let height = 0;

          if (isDisplayOnly) {
            height = DISPLAY_HEIGHT + fixture.height;
            acc.push({ id: fixture.id, width, height, left, bottom, fixtureYOffset });

            return acc;
          }

          const maxFacingHeight = maxProductHeightOnFixtures[fixtureId] || 0;
          const basketSlopeHeight = adjacentHeightToShelfSlopeAngle(fixture.shelf_slope, fixture.depth);
          const basketHeight = Math.max(basketSlopeHeight, maxFacingHeight, fixture.grille_height);

          height = (isBasket ? basketHeight : maxFacingHeight + fixture.height);

          if (isClipboard) {
            left = 0;
            bottom = index === 0
              ? y - draggableFixture.y
              : acc[index - 1].bottom + acc[index - 1].height + VERTICAL_SPACING;
          }

          acc.push({ id: fixture.id, width, height, left, bottom, fixtureYOffset });

          return acc;
        }
      }
    }, [] as Array<DraggableFixture>)
);

export const searchedSkusSelector = (searchValue: string = '', fixtureId = -1) => createSelector(
  clipboardHistorySelector,
  clipboardLocatedFixtureIdToFacingSelector,
  locatedFixturesByIdSelector,
  productsSelector,
  ({ clipboardFixtures }, { clipboardSkus, clipboardExcludedSkus, clipboardExcludedByTetrisSkus, clipboardFixtureIdToFacings, clipboardNewSkus, clipboardSkusWithCommitments }, locatedFixturesById, products) => {
    let data = null;

    if (searchValue) {
      const clipboardSearchFn = clipbordSearchHandler(products, searchValue);

      let clipboardExcluded: Array<FacingGroupCipboard> = [];
      let clipboardRemoved: Array<FacingGroupCipboard> = [];
      let clipboardExcludedByTetris: Array<FacingGroupCipboard> = [];
      let clipboardFixtureFacings: Array<ClipboardFixtureFacings> = [];
      let clipboardNewSkusFacings: Array<FacingGroupCipboard> = [];
      let clipboardSkusWithCommitmentsFacings: Array<FacingGroupCipboard> = [];

      if (fixtureId === -1) {
        clipboardExcluded = clipboardSearchFn(clipboardExcludedSkus);
        clipboardRemoved = clipboardSearchFn(clipboardSkus);
        clipboardExcludedByTetris = clipboardSearchFn(clipboardExcludedByTetrisSkus);
        const clipboardFixtureFacings1 = clipboardFixtures.reduce((acc, fixture) => {
          const fixtureFacings = clipboardFixtureIdToFacings[fixture.id] || [];
          const facings = clipboardSearchFn(fixtureFacings);

          if (facings.length === 0) {
            return acc;
          }

          const { y, segment, type } = locatedFixturesById[fixture.id];

          return {
            ...acc,
            [fixture.id]: { type, y, segment, facings }
          };
        }, {});

        clipboardFixtureFacings = Object.values(clipboardFixtureFacings1);
        clipboardNewSkusFacings = clipboardSearchFn(clipboardNewSkus);
        clipboardSkusWithCommitmentsFacings = clipboardSearchFn(clipboardSkusWithCommitments);
      }

      if (fixtureId !== -1) {
        const facings = clipboardSearchFn(clipboardFixtureIdToFacings[fixtureId]);
        const { y, segment, type } = locatedFixturesById[fixtureId];

        clipboardFixtureFacings = [{ type, y, segment, facings }];
      }

      const isResult = Boolean(clipboardExcluded.length)
        || Boolean(clipboardRemoved.length)
        || Boolean(clipboardFixtureFacings.length)
        || Boolean(clipboardExcludedByTetris.length)
        || Boolean(clipboardNewSkusFacings.length)
        || Boolean(clipboardSkusWithCommitmentsFacings.length);

      if (isResult) {
        data = {
          clipboardExcluded,
          clipboardRemoved,
          clipboardFixtureFacings,
          clipboardExcludedByTetris,
          clipboardNewSkusFacings,
          clipboardSkusWithCommitmentsFacings,
        };
      }
    }

    return data;
  }
);

const clipbordSearchHandler = (products: Products, searchValue: string) => (data: Array<FacingGroupCipboard>) => data.filter(item => {
  const regExp = getRegExpForClipboardSearching(searchValue);
  const product = products[item.product_num];

  if (product) {
    return product.product_num.match(regExp) ||
      product.product_english_desc.match(regExp) ||
      product.product_english_long_desc.match(regExp);
  }

  return false;
});

export const alertPropsSelector = (pixelsPerInch: number) =>
  createSelector(
    visualizerAlertSelector,
    fixtureSpacesSelector,
    maxFacingHeightFixtureSelector,
    locatedFixturesHistorySelector,
    locatedFixtureIdToFacingIdsSelector,
    facingGroupsByIdsSelector,
    (visualizerAlert, fixtureSpaces, maxFacingHeightFixture, locatedFixturesHistory, locatedFixtureIdToFacingIds, facingGroupsByIds) => {
      if (!visualizerAlert) {
        return {
          productAlerts: null,
          userAlerts: null,
          whiteSpaceAlerts: null,
          weightRestrictionAlerts: null,
          assortmentSweepAlerts: null,
          heavyProductHeightAlerts: null
        };
      }

      const isAssortmentSweepAlert = visualizerAlert.warningType === 'assortment_sweep_warnings';

      return {
        productAlerts: getLinkedProductsAlert(visualizerAlert, false, false),
        userAlerts: getUserAlert(visualizerAlert),
        whiteSpaceAlerts: getWhiteSpaceAlert(
          visualizerAlert,
          pixelsPerInch,
          fixtureSpaces,
          maxFacingHeightFixture,
          locatedFixturesHistory,
          locatedFixtureIdToFacingIds,
          facingGroupsByIds
        ),
        weightRestrictionAlerts: getWeightRestrictionAlert(visualizerAlert),
        assortmentSweepAlerts: isAssortmentSweepAlert ? getLinkedProductsAlert(visualizerAlert, true, false) : null,
        heavyProductHeightAlerts: getHeavyProductAlerts(visualizerAlert)
      };
    }
  );

export const addDisplaySelector = createSelector(
  locatedFixtureIdToFacingIdsSelector,
  locatedFixturesByIdSelector,
  itemsCoordinatesSelector,
  currentFixtureNumberIdSelector,
  locatedFixturesHistorySelector1,
  aisleHeightSelector,
  selectedFixturesSelector,
  (
    locatedFixtureIdToFacingIds,
    locatedFixturesById,
    itemsCoordinates,
    currentFixtureNumberId,
    { pogFixtures },
    aisleHeight,
    selectedFixtures
  ) => {
    const defaultValue = {
      canAddDisplay: false,
      newDisplayFixtures: []
    };

    const canShelfBeConverted = selectedFixtures.every(id =>
      locatedFixtureIdToFacingIds[id] &&
      locatedFixtureIdToFacingIds[id].length === 0 &&
      locatedFixturesById[id].type === FIXTURE_TYPES.SHELF &&
      !locatedFixturesById[id].is_display_only
    );

    if (!canShelfBeConverted) {
      return defaultValue;
    }

    const newDisplayFixtures = selectedFixtures.reduce((acc, id, index) => {
      const locatedFixture = locatedFixturesById[id];
      const newLocatedFixture: LocatedFixture = {
        ...locatedFixture,
        id: currentFixtureNumberId + index + 1,
        is_display_only: true,
        is_merchandisable: false
      };

      return acc.concat(newLocatedFixture);
    }, [] as Array<LocatedFixture>);

    const isWithinBoundary = newDisplayFixtures
      .every(({ y, height }) => y + height + DISPLAY_HEIGHT < aisleHeight);

    if (!isWithinBoundary) {
      return defaultValue;
    }

    const isDisplayOverlapWithFacings = itemsCoordinates.filter(facing =>
      newDisplayFixtures.filter(fixture => {
        const ghostFixture = {
          x1: fixture.x,
          y1: fixture.y,
          x2: fixture.x + fixture.width,
          y2: fixture.y + fixture.height + DISPLAY_HEIGHT
        };

        const left = Math.max(ghostFixture.x1, facing.x1);
        const bottom = Math.max(ghostFixture.y1, facing.y1);
        const right = Math.min(ghostFixture.x2, facing.x2);
        const top = Math.min(ghostFixture.y2, facing.y2);

        return right > left && top > bottom;
      }).length > 0);

    if (isDisplayOverlapWithFacings.length) {
      return defaultValue;
    }

    const isFixtureOverlappings = isFixturesOverlap(newDisplayFixtures, pogFixtures, selectedFixtures);

    if (isFixtureOverlappings.length) {
      return defaultValue;
    }

    return {
      canAddDisplay: true,
      newDisplayFixtures
    };
  }
);

export const canSwitchFixtureSelector = createSelector(
  reviewAndEditSelector,
  locatedFixturesByIdSelector,
  aisleDepthSelector,
  fixtureSpaceMaxHeightSelectorByTypesSelector([
    FIXTURE_TYPES.SHELF,
    FIXTURE_TYPES.BASKET,
    FIXTURE_TYPES.CROSSBAR,
    FIXTURE_TYPES.SPECIALTY,
    FIXTURE_TYPES.FLATBAR
  ]),
  (
    selectedSegment,
    locatedFixturesById,
    aisleDepth,
    fixtureSpaces,
  ) => {
    let isSwitchToBasket: boolean = false;
    let isSwitchToShelf: boolean = false;
    let canSwitchBasketParams: Record<number, boolean> = {};
    const { selectedFixtureIds } = selectedSegment;

    if (selectedFixtureIds.length) {
      const selectedFixtures = selectedFixtureIds.map(id => locatedFixturesById[id]);

      isSwitchToBasket = selectedFixtures.every(
        fixture => fixture.type === FIXTURE_TYPES.SHELF
      );
      isSwitchToShelf = selectedFixtures.every(
        fixture => fixture.type === FIXTURE_TYPES.BASKET
      );

      if (isSwitchToBasket) {
        canSwitchBasketParams = BASKET_PARAMS.reduce((acc, param) => {
          const fixtureSlopeHeight = adjacentHeightToShelfSlopeAngle(param.shelfSlope, aisleDepth);
          const fixtureHeight = Math.max(fixtureSlopeHeight, param.grille);
          const ghostBaskets = selectedFixtures.map(fixture => ({
            id: fixture.id,
            x1: fixture.x,
            x2: fixture.x + fixture.width,
            y1: fixture.y,
            y2: fixture.y + fixture.height + fixtureHeight,
          }));

          const isOverlapFixture = fixtureSpaces.some(fixtureSpace => ghostBaskets.some(ghostBasket => {
            if (fixtureSpace.id === ghostBasket.id) {
              return false;
            }

            const left = Math.max(ghostBasket.x1, fixtureSpace.x1);
            const bottom = Math.max(ghostBasket.y1, fixtureSpace.y1);
            const right = Math.min(ghostBasket.x2, fixtureSpace.x2);
            const top = Math.min(ghostBasket.y2, fixtureSpace.y2);

            return right > left && top > bottom;
          }));

          acc[param.id] = isOverlapFixture;

          return acc;
        }, {} as Record<number, boolean>);
      }
    }

    return {
      isSwitchToBasket,
      isSwitchToShelf,
      canSwitchBasketParams,
    };
  }
);

export const canAlignSelector =
  createSelector(
    facingGroupsByIdsSelector,
    fixtureHeightSelector,
    locatedFixturesByIdSelector,
    itemsCoordinatesSelector,
    selectedFacingEntitiesSelector,
    districtTypeSelector,
    pegboardInitialConfigSelector,
    (
      facingGroupsByIds,
      fixtureHeight,
      locatedFixturesById,
      itemsCoordinates,
      selectedFacingEntities,
      distinctType,
      pegboardInitialConfig
    ) => {
      const options: {
        leftAlign: Array<FacingGroup> | null;
        rightAlign: Array<FacingGroup> | null;
        topAlign: Array<FacingGroup> | null;
        bottomAlign: Array<FacingGroup> | null;
      } = {
        leftAlign: null,
        rightAlign: null,
        topAlign: null,
        bottomAlign: null,
      };

      if (distinctType === DISTRICT_TYPES.CLIPBOARD || distinctType === DISTRICT_TYPES.WORKBENCH) {
        return options;
      }

      const selectedFacingIds = Object.keys(selectedFacingEntities);
      const isMultiple = selectedFacingIds.length > 1;
      const isPegboard = selectedFacingIds.every(id => facingGroupsByIds[id].fixtureType === FIXTURE_TYPES.PEGBOARD);

      if (isMultiple && isPegboard) {

        // generate facing spaces
        const facingSpacesById = selectedFacingIds.reduce((acc, facingId) => {
          const facing = facingGroupsByIds[facingId];
          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
          );
          const facingWidth = facing.packaging.width * facing.horizontal_facings;
          const facingSpace = {
            id: facingId,
            x1: facing.x,
            y1: facing.y,
            x2: facing.x + facingWidth,
            y2: facing.y + facingHeight,
          };

          return {
            ...acc,
            [facingId]: facingSpace,
          };
        }, {} as Record<string, {
          id: string;
          x1: number;
          y1: number;
          x2: number;
          y2: number;
        }>);

        // detect align facings id
        const {
          leftFacingId,
          rightFacingId,
          topFacingId,
          bottomFacingId,
        } = selectedFacingIds.reduce((acc, facingId) => {
          let {
            leftFacingId,
            rightFacingId,
            topFacingId,
            bottomFacingId,
          } = acc;

          if (!leftFacingId) {
            leftFacingId = facingId;
          }

          if (!rightFacingId) {
            rightFacingId = facingId;
          }

          if (!topFacingId) {
            topFacingId = facingId;
          }

          if (!bottomFacingId) {
            bottomFacingId = facingId;
          }

          const facingSpace = facingSpacesById[facingId];
          const leftFacingSpace = facingSpacesById[leftFacingId];
          const rightFacingSpace = facingSpacesById[rightFacingId];
          const topFacingSpace = facingSpacesById[topFacingId];
          const bottomFacingSpace = facingSpacesById[bottomFacingId];

          if (facingSpace.x1 <= leftFacingSpace.x1) {
            leftFacingId = facingId;
          }

          if (facingSpace.x2 >= rightFacingSpace.x2) {
            rightFacingId = facingId;
          }

          if (facingSpace.y2 >= topFacingSpace.y2) {
            topFacingId = facingId;
          }

          if (facingSpace.y1 <= bottomFacingSpace.y1) {
            bottomFacingId = facingId;
          }

          return {
            leftFacingId,
            rightFacingId,
            topFacingId,
            bottomFacingId,
          };
        }, {} as {
          leftFacingId: string;
          rightFacingId: string;
          topFacingId: string;
          bottomFacingId: string;
        });
        const itemsCoordinatesWithoutSelected = itemsCoordinates.filter(item => !selectedFacingIds.some(id => item.id === id));

        // left
        const leftGhostAlign = facingSpacesById[leftFacingId];
        const ghostLeftAlignSpaces = selectedFacingIds.map(facingId => {
          const ghostFacing = facingSpacesById[facingId];
          const facing = facingGroupsByIds[facingId];
          const diffX = Math.abs(ghostFacing.x1 - leftGhostAlign.x1);
          const ghostFacingDiff = {
            ...ghostFacing,
            x1: ghostFacing.x1 - diffX,
            x2: ghostFacing.x2 - diffX,
          };
          const [peghole] = facing.packaging.pegholes;
          const { x: newFacingX, y: newFacingY, occupiedFixture } = correctionPositionPegholes(ghostFacingDiff, fixtureHeight, peghole, pegboardInitialConfig);
          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
          );
          const facingWidth = facing.packaging.width * facing.horizontal_facings;

          return {
            id: facing.id,
            locatedFixtureId: occupiedFixture?.id,
            segment: occupiedFixture.segment,
            x1: newFacingX,
            y1: newFacingY,
            x2: newFacingX + facingWidth,
            y2: newFacingY + facingHeight,
          };
        });

        // Check cross segments
        const isCrossSegmentLeft = ghostLeftAlignSpaces.some(ghostLeft => {
          const segmentA = Math.trunc(ghostLeft.x1 / SEGMENT_WIDTH);
          const segmentB = Math.trunc(ghostLeft.x2 / SEGMENT_WIDTH);

          return segmentA !== segmentB;
        });

        if (!isCrossSegmentLeft) {
          // Check occupied facings left self
          const itemsOccupiedLeftAlignFacingSelf = ghostLeftAlignSpaces.some((ghostLeftOne, indexOne) =>
            ghostLeftAlignSpaces.some((ghostLeftTwo, indexTwo) => {
              if (indexOne === indexTwo) {
                return false;
              }

              const left = Math.max(ghostLeftOne.x1, ghostLeftTwo.x1);
              const bottom = Math.max(ghostLeftOne.y1, ghostLeftTwo.y1);
              const top = Math.min(ghostLeftOne.y2, ghostLeftTwo.y2);
              const right = Math.min(ghostLeftOne.x2, ghostLeftTwo.x2);

              return right > left && top > bottom;
            })
          );

          if (!itemsOccupiedLeftAlignFacingSelf) {
            // Check occupied facings left
            const itemsOccupiedLeftAlignFacing = itemsCoordinatesWithoutSelected.some((item) =>
              ghostLeftAlignSpaces.some(ghostLeft => {
                const left = Math.max(ghostLeft.x1, item.x1);
                const bottom = Math.max(ghostLeft.y1, item.y1);
                const top = Math.min(ghostLeft.y2, item.y2);
                const right = Math.min(ghostLeft.x2, item.x2);

                return right > left && top > bottom;
              })
            );

            if (!itemsOccupiedLeftAlignFacing) {
              // Check occupied fixtures
              const fixturesOccupiedLeftFacing = fixtureHeight.some(locatedFixture =>
                ghostLeftAlignSpaces.some(ghostLeft => {
                  const left = Math.max(ghostLeft.x1, locatedFixture.x1);
                  const bottom = Math.max(ghostLeft.y1, locatedFixture.y1);
                  const top = Math.min(ghostLeft.y2, locatedFixture.y2);
                  const right = Math.min(ghostLeft.x2, locatedFixture.x2);

                  return right > left && top > bottom && !locatedFixture.isPegboard;
                })
              );

              if (!fixturesOccupiedLeftFacing) {
                options.leftAlign = ghostLeftAlignSpaces.map(ghostLeft => {
                  const facing = facingGroupsByIds[ghostLeft.id];
                  const x = ghostLeft.x1;
                  const y = ghostLeft.y1;

                  return {
                    ...facing,
                    id: generateUniqueId(),
                    located_fixture_id: ghostLeft.locatedFixtureId,
                    segment: ghostLeft.segment,
                    x,
                    y
                  };
                });
              }
            }
          }
        }

        // right
        const rightGhostAlign = facingSpacesById[rightFacingId];
        const ghostRightAlignSpaces = selectedFacingIds.map(facingId => {
          const ghostFacing = facingSpacesById[facingId];
          const facing = facingGroupsByIds[facingId];
          const diffX = Math.abs(ghostFacing.x2 - rightGhostAlign.x2);
          const ghostFacingDiff = {
            ...ghostFacing,
            x1: ghostFacing.x1 + diffX,
            x2: ghostFacing.x2 + diffX,
          };
          const [peghole] = facing.packaging.pegholes;
          const { x: newFacingX, y: newFacingY, occupiedFixture } = correctionPositionPegholes(ghostFacingDiff, fixtureHeight, peghole, pegboardInitialConfig);
          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
          );
          const facingWidth = facing.packaging.width * facing.horizontal_facings;

          return {
            id: facing.id,
            locatedFixtureId: occupiedFixture?.id,
            segment: occupiedFixture.segment,
            x1: newFacingX,
            y1: newFacingY,
            x2: newFacingX + facingWidth,
            y2: newFacingY + facingHeight,
          };
        });

        // Check cross segments
        const isCrossSegmentRight = ghostLeftAlignSpaces.some(ghostRight => {
          const segmentA = Math.trunc(ghostRight.x1 / SEGMENT_WIDTH);
          const segmentB = Math.trunc(ghostRight.x2 / SEGMENT_WIDTH);

          return segmentA !== segmentB;
        });

        if (!isCrossSegmentRight) {
          // Check occupied facings right self
          const itemsOccupiedRightAlignFacingSelf = ghostRightAlignSpaces.some((ghostRightOne, indexOne) =>
            ghostRightAlignSpaces.some((ghostRightTwo, indexTwo) => {
              if (indexOne === indexTwo) {
                return false;
              }

              const left = Math.max(ghostRightOne.x1, ghostRightTwo.x1);
              const bottom = Math.max(ghostRightOne.y1, ghostRightTwo.y1);
              const top = Math.min(ghostRightOne.y2, ghostRightTwo.y2);
              const right = Math.min(ghostRightOne.x2, ghostRightTwo.x2);

              return right > left && top > bottom;
            })
          );

          if (!itemsOccupiedRightAlignFacingSelf) {
            // Check occupied facings left
            const itemsOccupiedRightAlignFacing = itemsCoordinatesWithoutSelected.some((item) =>
              ghostRightAlignSpaces.some(ghostRight => {
                const left = Math.max(ghostRight.x1, item.x1);
                const bottom = Math.max(ghostRight.y1, item.y1);
                const top = Math.min(ghostRight.y2, item.y2);
                const right = Math.min(ghostRight.x2, item.x2);

                return right > left && top > bottom;
              })
            );

            if (!itemsOccupiedRightAlignFacing) {
              // Check occupied fixtures
              const fixturesOccupiedRightFacing = fixtureHeight.some(locatedFixture =>
                ghostRightAlignSpaces.some(ghostRight => {
                  const left = Math.max(ghostRight.x1, locatedFixture.x1);
                  const bottom = Math.max(ghostRight.y1, locatedFixture.y1);
                  const top = Math.min(ghostRight.y2, locatedFixture.y2);
                  const right = Math.min(ghostRight.x2, locatedFixture.x2);

                  return right > left && top > bottom && !locatedFixture.isPegboard;
                })
              );

              if (!fixturesOccupiedRightFacing) {
                options.rightAlign = ghostRightAlignSpaces.map(ghostRight => {
                  const facing = facingGroupsByIds[ghostRight.id];
                  const x = ghostRight.x1;
                  const y = ghostRight.y1;

                  return {
                    ...facing,
                    id: generateUniqueId(),
                    located_fixture_id: ghostRight.locatedFixtureId,
                    segment: ghostRight.segment,
                    x,
                    y
                  };
                });
              }
            }
          }
        }

        // top
        const topGhostAlign = facingSpacesById[topFacingId];
        const ghostTopAlignSpaces = selectedFacingIds.map(facingId => {
          const ghostFacing = facingSpacesById[facingId];
          const facing = facingGroupsByIds[facingId];
          const diffX = Math.abs(ghostFacing.y2 - topGhostAlign.y2);
          const ghostFacingDiff = {
            ...ghostFacing,
            y1: ghostFacing.y1 + diffX,
            y2: ghostFacing.y2 + diffX,
          };
          const [peghole] = facing.packaging.pegholes;
          const { x: newFacingX, y: newFacingY, occupiedFixture } = correctionPositionPegholes(ghostFacingDiff, fixtureHeight, peghole, pegboardInitialConfig);
          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
          );
          const facingWidth = facing.packaging.width * facing.horizontal_facings;

          return {
            id: facing.id,
            locatedFixtureId: occupiedFixture?.id,
            segment: occupiedFixture.segment,
            x1: newFacingX,
            y1: newFacingY,
            x2: newFacingX + facingWidth,
            y2: newFacingY + facingHeight,
          };
        });

        // Check occupied facings top self
        const itemsOccupiedTopAlignFacingSelf = ghostTopAlignSpaces.some((ghostTopOne, indexOne) =>
          ghostTopAlignSpaces.some((ghostTopTwo, indexTwo) => {
            if (indexOne === indexTwo) {
              return false;
            }

            const left = Math.max(ghostTopOne.x1, ghostTopTwo.x1);
            const bottom = Math.max(ghostTopOne.y1, ghostTopTwo.y1);
            const top = Math.min(ghostTopOne.y2, ghostTopTwo.y2);
            const right = Math.min(ghostTopOne.x2, ghostTopTwo.x2);

            return right > left && top > bottom;
          })
        );

        if (!itemsOccupiedTopAlignFacingSelf) {
          // Check occupied facings top
          const itemsOccupiedTopAlignFacing = itemsCoordinatesWithoutSelected.some((item) =>
            ghostTopAlignSpaces.some(ghostTop => {
              const left = Math.max(ghostTop.x1, item.x1);
              const bottom = Math.max(ghostTop.y1, item.y1);
              const top = Math.min(ghostTop.y2, item.y2);
              const right = Math.min(ghostTop.x2, item.x2);

              return right > left && top > bottom;
            })
          );

          if (!itemsOccupiedTopAlignFacing) {
            // Check occupied fixtures
            const fixturesOccupiedTopFacing = fixtureHeight.some(locatedFixture =>
              ghostTopAlignSpaces.some(ghostTop => {
                const left = Math.max(ghostTop.x1, locatedFixture.x1);
                const bottom = Math.max(ghostTop.y1, locatedFixture.y1);
                const top = Math.min(ghostTop.y2, locatedFixture.y2);
                const right = Math.min(ghostTop.x2, locatedFixture.x2);

                return right > left && top > bottom && !locatedFixture.isPegboard;
              })
            );

            if (!fixturesOccupiedTopFacing) {
              options.topAlign = ghostTopAlignSpaces.map(ghostTop => {
                const facing = facingGroupsByIds[ghostTop.id];
                const x = ghostTop.x1;
                const y = ghostTop.y1;

                return {
                  ...facing,
                  id: generateUniqueId(),
                  located_fixture_id: ghostTop.locatedFixtureId,
                  segment: ghostTop.segment,
                  x,
                  y
                };
              });
            }
          }
        }

        // bottom
        const bottomGhostAlign = facingSpacesById[bottomFacingId];
        const ghostBottomAlignSpaces = selectedFacingIds.map(facingId => {
          const ghostFacing = facingSpacesById[facingId];
          const facing = facingGroupsByIds[facingId];
          const diffX = Math.abs(ghostFacing.y1 - bottomGhostAlign.y1);
          const ghostFacingDiff = {
            ...ghostFacing,
            y1: ghostFacing.y1 - diffX,
            y2: ghostFacing.y2 - diffX,
          };
          const [peghole] = facing.packaging.pegholes;
          const { x: newFacingX, y: newFacingY, occupiedFixture } = correctionPositionPegholes(ghostFacingDiff, fixtureHeight, peghole, pegboardInitialConfig);
          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
          );
          const facingWidth = facing.packaging.width * facing.horizontal_facings;

          return {
            id: facing.id,
            locatedFixtureId: occupiedFixture?.id,
            segment: occupiedFixture.segment,
            x1: newFacingX,
            y1: newFacingY,
            x2: newFacingX + facingWidth,
            y2: newFacingY + facingHeight,
          };
        });

        // Check occupied facings bottom self
        const itemsOccupiedBottomAlignFacingSelf = ghostBottomAlignSpaces.some((ghostBottomOne, indexOne) =>
          ghostBottomAlignSpaces.some((ghostBottomTwo, indexTwo) => {
            if (indexOne === indexTwo) {
              return false;
            }

            const left = Math.max(ghostBottomOne.x1, ghostBottomTwo.x1);
            const bottom = Math.max(ghostBottomOne.y1, ghostBottomTwo.y1);
            const top = Math.min(ghostBottomOne.y2, ghostBottomTwo.y2);
            const right = Math.min(ghostBottomOne.x2, ghostBottomTwo.x2);

            return right > left && top > bottom;
          })
        );

        if (!itemsOccupiedBottomAlignFacingSelf) {
          // Check occupied facings bottom
          const itemsOccupiedBottomAlignFacing = itemsCoordinatesWithoutSelected.some((item) =>
            ghostBottomAlignSpaces.some(ghostBottom => {
              const left = Math.max(ghostBottom.x1, item.x1);
              const bottom = Math.max(ghostBottom.y1, item.y1);
              const top = Math.min(ghostBottom.y2, item.y2);
              const right = Math.min(ghostBottom.x2, item.x2);

              return right > left && top > bottom;
            })
          );

          if (!itemsOccupiedBottomAlignFacing) {
            // Check occupied fixtures
            const fixturesOccupiedBottomFacing = fixtureHeight.some(locatedFixture =>
              ghostBottomAlignSpaces.some(ghostBottom => {
                const left = Math.max(ghostBottom.x1, locatedFixture.x1);
                const bottom = Math.max(ghostBottom.y1, locatedFixture.y1);
                const top = Math.min(ghostBottom.y2, locatedFixture.y2);
                const right = Math.min(ghostBottom.x2, locatedFixture.x2);

                return right > left && top > bottom && !locatedFixture.isPegboard;
              })
            );

            if (!fixturesOccupiedBottomFacing) {
              options.bottomAlign = ghostBottomAlignSpaces.map(ghostBottom => {
                const facing = facingGroupsByIds[ghostBottom.id];
                const x = ghostBottom.x1;
                const y = ghostBottom.y1;

                return {
                  ...facing,
                  id: generateUniqueId(),
                  located_fixture_id: ghostBottom.locatedFixtureId,
                  segment: ghostBottom.segment,
                  x,
                  y
                };
              });
            }
          }
        }
      }

      return options;
    }
  );

export const canDistributePegboardSelector =
  createSelector(
    facingGroupsByIdsSelector,
    fixtureHeightSelector,
    locatedFixturesByIdSelector,
    itemsCoordinatesSelector,
    selectedFacingEntitiesSelector,
    pegboardInitialConfigSelector,
    districtTypeSelector,
    (
      facingGroupsByIds,
      fixtureHeight,
      locatedFixturesById,
      itemsCoordinates,
      selectedFacingEntities,
      pegboardInitialConfig,
      districtType
    ) => {
      const options: {
      horizontallyPegboardDistribute: Array<FacingGroup> | null;
      verticallyPegboardDistribute: Array<FacingGroup> | null;
    } = {
      horizontallyPegboardDistribute: null,
      verticallyPegboardDistribute: null,
    };

      const selectedFacingIds = Object.keys(selectedFacingEntities);
      const isMultiple = selectedFacingIds.length > 2;
      const isPegboard = selectedFacingIds.every(id => facingGroupsByIds[id].fixtureType === FIXTURE_TYPES.PEGBOARD);
      const isSameSegment = new Set(selectedFacingIds.map(id => facingGroupsByIds[id].segment)).size === 1;
      const isPlanogram = districtType === 'PLANOGRAM';

      if (isMultiple && isPegboard && isSameSegment && isPlanogram) {
      // generate facing spaces
        const facingSpacesById = selectedFacingIds.reduce((acc, facingId) => {
          const facing = facingGroupsByIds[facingId];
          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
          facing.cap_nested as Nesting,
          facing.packaging.nested_height,
          facing.cappedPackaging.height,
          facing.capped_vertical_facings
          );
          const facingWidth = facing.packaging.width * facing.horizontal_facings;
          const facingSpace = {
            id: facingId,
            x1: facing.x,
            y1: facing.y,
            x2: facing.x + facingWidth,
            y2: facing.y + facingHeight,
          };

          return {
            ...acc,
            [facingId]: facingSpace,
          };
        }, {} as Record<string, {
        id: string;
        x1: number;
        y1: number;
        x2: number;
        y2: number;
      }>);

        const itemsCoordinatesWithoutSelected = itemsCoordinates.filter(item => !selectedFacingIds.some(id => item.id === id));

        // detect distribute facings id
        const {
          leftFacingId,
          rightFacingId,
          topFacingId,
          bottomFacingId,
        } = selectedFacingIds.reduce((acc, facingId) => {
          let {
            leftFacingId,
            rightFacingId,
            topFacingId,
            bottomFacingId,
          } = acc;

          if (!leftFacingId) {
            leftFacingId = facingId;
          }

          if (!rightFacingId) {
            rightFacingId = facingId;
          }

          if (!topFacingId) {
            topFacingId = facingId;
          }

          if (!bottomFacingId) {
            bottomFacingId = facingId;
          }

          const facingSpace = facingSpacesById[facingId];
          const leftFacingSpace = facingSpacesById[leftFacingId];
          const rightFacingSpace = facingSpacesById[rightFacingId];
          const topFacingSpace = facingSpacesById[topFacingId];
          const bottomFacingSpace = facingSpacesById[bottomFacingId];

          if (facingSpace.x1 <= leftFacingSpace.x1) {
            leftFacingId = facingId;
          }

          if (facingSpace.x2 >= rightFacingSpace.x2) {
            rightFacingId = facingId;
          }

          if (facingSpace.y2 >= topFacingSpace.y2) {
            topFacingId = facingId;
          }

          if (facingSpace.y1 <= bottomFacingSpace.y1) {
            bottomFacingId = facingId;
          }

          return {
            leftFacingId,
            rightFacingId,
            topFacingId,
            bottomFacingId,
          };
        }, {} as {
        leftFacingId: string;
        rightFacingId: string;
        topFacingId: string;
        bottomFacingId: string;
      });

        const leftFacingSpace = facingSpacesById[leftFacingId];
        const rightFacingSpace = facingSpacesById[rightFacingId];
        const topFacingSpace = facingSpacesById[topFacingId];
        const bottomFacingSpace = facingSpacesById[bottomFacingId];

        // check destination for horizontallyPegboardDistribute, verticallyPegboardDistribute
        const selectedFacingIdsWithoutHorizontally = selectedFacingIds.filter(facingId => facingId !== leftFacingId && facingId !== rightFacingId);
        const selectedFacingIdsWithoutVertically = selectedFacingIds.filter(facingId => facingId !== topFacingId && facingId !== bottomFacingId);

        const horizontallyDistance = rightFacingSpace.x1 - leftFacingSpace.x2;
        const verticallyDistance = topFacingSpace.y1 - bottomFacingSpace.y2;

        const horizontallySumSpace = selectedFacingIdsWithoutHorizontally.reduce((acc, facingId) => acc + facingGroupsByIds[facingId].packaging.width, 0);
        const verticallySumSpace = selectedFacingIdsWithoutVertically.reduce((acc, facingId) => acc + facingGroupsByIds[facingId].packaging.height, 0);

        const horizontallyDiff = horizontallyDistance - horizontallySumSpace;
        const verticallyDiff = verticallyDistance - verticallySumSpace;

        if (horizontallyDiff > 0) {
          let horizontallyIteratorSpace = facingSpacesById[leftFacingId].x2;
          const horizontallySpaceBetween = horizontallyDiff / (selectedFacingIdsWithoutHorizontally.length + 1);

          selectedFacingIdsWithoutHorizontally
            .sort((a, b) => facingGroupsByIds[a].x - facingGroupsByIds[b].x)
            .forEach((facingId, index) => {
              const nextX = horizontallyIteratorSpace + horizontallySpaceBetween;
              const diff = nextX - facingSpacesById[facingId].x1;

              facingSpacesById[facingId].x1 += diff;
              facingSpacesById[facingId].x2 += diff;
              horizontallyIteratorSpace = facingSpacesById[facingId].x2;
            });

          selectedFacingIds.forEach(facingId => {
            const facing = facingGroupsByIds[facingId];
            const facingSpace = facingSpacesById[facingId];
            const [peghole] = facing.packaging.pegholes;
            const { x: newFacingX, y: newFacingY } = correctionPositionPegholes(facingSpace, fixtureHeight, peghole, pegboardInitialConfig);
            const facingHeight = getFacingHeight(
              facing.packaging.height,
              facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
            );
            const facingWidth = facing.packaging.width * facing.horizontal_facings;

            facingSpacesById[facingId].x1 = newFacingX;
            facingSpacesById[facingId].y1 = newFacingY;
            facingSpacesById[facingId].x2 = newFacingX + facingWidth;
            facingSpacesById[facingId].y2 = newFacingY + facingHeight;
          });

          // Check occupied facings
          const itemsOccupiedHorizontallyFacing = itemsCoordinatesWithoutSelected.some((item) =>
            selectedFacingIds.some(facingId => {
              const facingSpace = facingSpacesById[facingId];
              const left = Math.max(facingSpace.x1, item.x1);
              const bottom = Math.max(facingSpace.y1, item.y1);
              const top = Math.min(facingSpace.y2, item.y2);
              const right = Math.min(facingSpace.x2, item.x2);

              return right > left && top > bottom;
            })
          );

          if (!itemsOccupiedHorizontallyFacing) {
            options.horizontallyPegboardDistribute = selectedFacingIds.map(facingId => {
              const facing = facingGroupsByIds[facingId];
              const facingSpace = facingSpacesById[facingId];
              const x = facingSpace.x1;
              const y = facingSpace.y1;

              return {
                ...facing,
                id: generateUniqueId(),
                x,
                y
              };
            });
          }
        }

        if (verticallyDiff > 0) {
          let verticallyIteratorSpace = facingSpacesById[bottomFacingId].y2;
          const verticallySpaceBetween = verticallyDiff / (selectedFacingIdsWithoutVertically.length + 1);

          selectedFacingIdsWithoutVertically
            .sort((a, b) => facingGroupsByIds[a].y - facingGroupsByIds[b].y)
            .forEach((facingId, index) => {
              const nextY = verticallyIteratorSpace + verticallySpaceBetween;
              const diff = nextY - facingSpacesById[facingId].y1;

              facingSpacesById[facingId].y1 += diff;
              facingSpacesById[facingId].y2 += diff;
              verticallyIteratorSpace = facingSpacesById[facingId].y2;
            });

          selectedFacingIds.forEach(facingId => {
            const facing = facingGroupsByIds[facingId];
            const facingSpace = facingSpacesById[facingId];
            const [peghole] = facing.packaging.pegholes;
            const { x: newFacingX, y: newFacingY } = correctionPositionPegholes(facingSpace, fixtureHeight, peghole, pegboardInitialConfig);
            const facingHeight = getFacingHeight(
              facing.packaging.height,
              facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
            );
            const facingWidth = facing.packaging.width * facing.horizontal_facings;

            facingSpacesById[facingId].x1 = newFacingX;
            facingSpacesById[facingId].y1 = newFacingY;
            facingSpacesById[facingId].x2 = newFacingX + facingWidth;
            facingSpacesById[facingId].y2 = newFacingY + facingHeight;
          });

          // Check occupied facings
          const itemsOccupiedVerticallyFacing = itemsCoordinatesWithoutSelected.some((item) =>
            selectedFacingIds.some(facingId => {
              const facingSpace = facingSpacesById[facingId];
              const left = Math.max(facingSpace.x1, item.x1);
              const bottom = Math.max(facingSpace.y1, item.y1);
              const top = Math.min(facingSpace.y2, item.y2);
              const right = Math.min(facingSpace.x2, item.x2);

              return right > left && top > bottom;
            })
          );

          if (!itemsOccupiedVerticallyFacing) {
            options.verticallyPegboardDistribute = selectedFacingIds.map(facingId => {
              const facing = facingGroupsByIds[facingId];
              const facingSpace = facingSpacesById[facingId];
              const x = facingSpace.x1;
              const y = facingSpace.y1;

              return {
                ...facing,
                id: generateUniqueId(),
                x,
                y
              };
            });
          }
        }

      }

      return options;
    });

export const canDistributeSelector =
  createSelector(
    facingGroupsByIdsSelector,
    fixtureHeightSelector,
    locatedFixturesByIdSelector,
    itemsCoordinatesSelector,
    selectedFacingEntitiesSelector,
    districtTypeSelector,
    fixtureSpaceSelectorByTypesSelector(['SHELF', 'BASKET', 'CROSSBAR']),
    (
      facingGroupsByIds,
      fixtureHeight,
      locatedFixturesById,
      itemsCoordinates,
      selectedFacingEntities,
      districtType,
      locatedFixturesSpace
    ) => {
      let horizontallyDistribute = null;

      if (districtType === 'CLIPBOARD' || districtType === DISTRICT_TYPES.WORKBENCH) {
        return horizontallyDistribute;
      }

      const selectedFacingIds = Object.keys(selectedFacingEntities);
      const selectedFixturesIds = selectedFacingIds.map(facingId => facingGroupsByIds[facingId].located_fixture_id);
      const isMultiple = selectedFacingIds.length > 2;
      const isDefaultFixture = selectedFacingIds.every(id => {
        const fixtureType = facingGroupsByIds[id].fixtureType;

        return fixtureType === FIXTURE_TYPES.SHELF ||
        fixtureType === FIXTURE_TYPES.BASKET ||
        fixtureType === FIXTURE_TYPES.CROSSBAR;
      });
      const isSameFixtureType = new Set(selectedFacingIds.map(id => facingGroupsByIds[id].fixtureType)).size === 1;
      const isSameFixtureTop = new Set(selectedFixturesIds.map(id => locatedFixturesById[id].y + locatedFixturesById[id].height)).size === 1;

      if (
        isMultiple &&
        isDefaultFixture &&
        isSameFixtureType &&
        isSameFixtureTop
      ) {
      // generate facing spaces
        const facingSpacesById = selectedFacingIds.reduce((acc, facingId) => {
          const facing = facingGroupsByIds[facingId];
          const facingHeight = getFacingHeight(
            facing.packaging.height,
            facing.vertical_facings,
            facing.cap_nested as Nesting,
            facing.packaging.nested_height,
            facing.cappedPackaging.height,
            facing.capped_vertical_facings
          );
          const facingWidth = facing.packaging.width * facing.horizontal_facings;
          const facingSpace = {
            id: facingId,
            x1: facing.x,
            y1: facing.y,
            x2: facing.x + facingWidth,
            y2: facing.y + facingHeight,
          };

          return {
            ...acc,
            [facingId]: facingSpace,
          };
        }, {} as Record<string, {
        id: string;
        x1: number;
        y1: number;
        x2: number;
        y2: number;
      }>);
        const itemsCoordinatesWithoutSelected = itemsCoordinates.filter(item => !selectedFacingIds.some(id => item.id === id));
        // detect distribute facings id
        const {
          leftFacingId,
          rightFacingId,
        } = selectedFacingIds.reduce((acc, facingId) => {
          let {
            leftFacingId,
            rightFacingId,
          } = acc;

          if (!leftFacingId) {
            leftFacingId = facingId;
          }

          if (!rightFacingId) {
            rightFacingId = facingId;
          }

          const facingSpace = facingSpacesById[facingId];
          const leftFacingSpace = facingSpacesById[leftFacingId];
          const rightFacingSpace = facingSpacesById[rightFacingId];

          if (facingSpace.x1 <= leftFacingSpace.x1) {
            leftFacingId = facingId;
          }

          if (facingSpace.x2 >= rightFacingSpace.x2) {
            rightFacingId = facingId;
          }

          return {
            leftFacingId,
            rightFacingId,
          };
        }, {} as {
        leftFacingId: string;
        rightFacingId: string;
      });

        const leftFacingSpace = facingSpacesById[leftFacingId];
        const rightFacingSpace = facingSpacesById[rightFacingId];

        const selectedFacingIdsWithoutHorizontally = selectedFacingIds.filter(facingId => facingId !== leftFacingId && facingId !== rightFacingId);
        const horizontallyDistance = rightFacingSpace.x1 - leftFacingSpace.x2;
        const horizontallySumSpace = selectedFacingIdsWithoutHorizontally.reduce((acc, facingId) => {
          const facing = facingGroupsByIds[facingId];
          const facingWidth = facing.packaging.width * facing.horizontal_facings;

          return acc + facingWidth;
        }, 0);
        const horizontallyDiff = horizontallyDistance - horizontallySumSpace;

        if (horizontallyDiff > 0) {
          let horizontallyIteratorSpace = facingSpacesById[leftFacingId].x2;
          const horizontallySpaceBetween = horizontallyDiff / (selectedFacingIdsWithoutHorizontally.length + 1);

          selectedFacingIdsWithoutHorizontally
            .sort((a, b) => facingGroupsByIds[a].x - facingGroupsByIds[b].x)
            .forEach((facingId, index) => {
              const nextX = horizontallyIteratorSpace + horizontallySpaceBetween;
              const diff = nextX - facingSpacesById[facingId].x1;

              facingSpacesById[facingId].x1 += diff;
              facingSpacesById[facingId].x2 += diff;
              horizontallyIteratorSpace = facingSpacesById[facingId].x2;
            });

          // Check occupied facings
          const itemsOccupiedHorizontallyFacing = itemsCoordinatesWithoutSelected.some((item) =>
            selectedFacingIds.some(facingId => {
              const facingSpace = facingSpacesById[facingId];
              const left = Math.max(facingSpace.x1, item.x1);
              const bottom = Math.max(facingSpace.y1, item.y1);
              const top = Math.min(facingSpace.y2, item.y2);
              const right = Math.min(facingSpace.x2, item.x2);

              return right > left && top > bottom;
            })
          );

          if (!itemsOccupiedHorizontallyFacing) {
          // check occupied fixtures
            const locatedFixturesWithoutSelected = locatedFixturesSpace.filter(locatedFixture => !selectedFixturesIds.includes(locatedFixture.id));
            const occupiedLocatedFixturesWithoutSelected = locatedFixturesWithoutSelected.some((locatedFixture) =>
              selectedFacingIds.some(facingId => {
                const facingSpace = facingSpacesById[facingId];
                const left = Math.max(facingSpace.x1, locatedFixture.x1);
                const bottom = Math.max(facingSpace.y1, locatedFixture.y1);
                const top = Math.min(facingSpace.y2, locatedFixture.y2);
                const right = Math.min(facingSpace.x2, locatedFixture.x2);

                return right > left && top > bottom;
              })
            );

            if (!occupiedLocatedFixturesWithoutSelected) {
              horizontallyDistribute = selectedFacingIds.map(facingId => {
                const facing = facingGroupsByIds[facingId];
                const facingSpace = facingSpacesById[facingId];
                const x = facingSpace.x1;
                const y = facingSpace.y1;

                return {
                  ...facing,
                  id: generateUniqueId(),
                  x,
                  y
                };
              });
            }
          }
        }
      }

      return horizontallyDistribute;
    });

export const assortmentHasBincapErrorSelector = (postSolver: boolean) =>
  createSelector(baseAssortmentSelector, (baseAssortment) => {
    const errorAssortment = baseAssortment
      ? baseAssortment.reduce((acc, cur) => {
        if (postSolver) {
          if (
            cur.optimized?.newMinTargetBincapOptimized &&
              cur.optimized?.newMaxTargetBincapOptimized &&
              cur.optimized?.newMinTargetBincapOptimized > cur.optimized?.newMaxTargetBincapOptimized
          ) {
            acc.push(cur.sku);
          }
        } else {
          if (cur.minTargetBincap && cur.maxTargetBincap && cur.minTargetBincap > cur.maxTargetBincap) {
            acc.push(cur.sku);
          }
        }

        return acc;
      }, [] as Array<string>)
      : [];

    return errorAssortment;
  });

export const moveToWorkbenchSelector = (
  contextMenuData: ContextMenuData | null,
  zoom: number
) =>
  createSelector(
    selectedFacingEntitiesSelector,
    facingGroupsByIdsSelector,
    workbenchHistorySelector,
    locatedFixturesIdToTypeSelector,
    selectedFixtureSelector,
    workbenchOpenedSelector,
    fixtureCrossbarSelector,
    fixturePegboardSelector,
    fixtureFlatbarSelector,
    fixtureSpacesSelector,
    aisleHeightSelector,
    (
      selectedFacingEntities,
      facingGroupsById,
      workbenchFacings,
      locatedFixturesIdToType,
      selectedFixtureType,
      workbenchOpened,
      fixturesCrossbar,
      fixturesPegboard,
      fixturesFlatbar,
      fixtureSpaces,
      aisleHeight
    ) => {
      try {
        if (contextMenuData) {
          const WORKBENCH_EXTRA_HEIGHT = 111;
          const scale = workbenchOpened
            ? zoom
            : (zoom * aisleHeight - WORKBENCH_EXTRA_HEIGHT) / aisleHeight;
          const selectedFacings = Object.keys(selectedFacingEntities).length
            ? selectedFacingEntities
            : { [contextMenuData.id]: [] };
          const removedId = Object.keys(selectedFacings);
          const { newFacings, newWorkbenchFacings } =
            processMovingFacingsToWorkbench(
              selectedFacings,
              facingGroupsById,
              fixturesCrossbar,
              fixturesFlatbar,
              fixturesPegboard,
              fixtureSpaces,
              locatedFixturesIdToType,
              selectedFixtureType,
              scale,
              contextMenuData.scrollHeight,
              contextMenuData.isScroll,
              contextMenuData.workbenchScrollLeft,
              contextMenuData.pogScrollLeft
            );

          const correctedNewWorkbenchFacings = correctionWorkbenchItems(
            workbenchFacings,
            newWorkbenchFacings,
            scale
          );

          return {
            removedId,
            newFacings,
            newWorkbenchFacings: correctedNewWorkbenchFacings,
          };
        }
      } catch (e) {
        return {
          removedId: [],
          newFacings: [],
          newWorkbenchFacings: [],
        };
      }

      return {
        removedId: [],
        newFacings: [],
        newWorkbenchFacings: [],
      };
    }
  );

export const rotateWorkbenchFacingSelector = (
  contextMenuData: ContextWorkbenchMenuData | null
) =>
  createSelector(
    facingGroupsByIdsSelector,
    aisleDepthSelector,
    (
      facingGroupsByIds,
      aisleDepth,
    ) => {
      const options: {
        horizontallyFacingGroup: FacingGroup | null;
        verticallyFacingGroup: FacingGroup | null;
        clockwiseFacingGroup: FacingGroup | null;
      } = {
        horizontallyFacingGroup: null,
        verticallyFacingGroup: null,
        clockwiseFacingGroup: null,
      };

      if (contextMenuData) {
        const { id } = contextMenuData;
        const facingGroup = facingGroupsByIds[id];

        if (
          facingGroup.fixtureType !== FIXTURE_TYPES.CROSSBAR &&
          facingGroup.fixtureType !== FIXTURE_TYPES.PEGBOARD
        ) {
          if (facingGroup.cap_nested !== Nesting.YNested) {
            // Clockwise
            const clockwiseOrientationCode = rotateClockwiseOrientationFacing(
              facingGroup.orientation_code
            );
            const clockwiseCappedOrientationCode =
              rotateCappedOrientationFacing(clockwiseOrientationCode);
            const clockwisePackaging = {
              ...facingGroup.packaging,
              ...orientationFacing(
                clockwiseOrientationCode,
                facingGroup.defaultPackaging.width,
                facingGroup.defaultPackaging.height,
                facingGroup.defaultPackaging.depth
              ),
            };
            const clockwiseCappedPackaging = orientationFacing(
              clockwiseCappedOrientationCode,
              facingGroup.defaultPackaging.width,
              facingGroup.defaultPackaging.height,
              facingGroup.defaultPackaging.depth
            );
            const clockwiseFacingGroupHeight = getFacingHeight(
              clockwisePackaging.height,
              facingGroup.vertical_facings,
              facingGroup.cap_nested as Nesting,
              facingGroup.packaging.nested_height,
              clockwiseCappedPackaging.height,
              facingGroup.capped_vertical_facings
            );
            const clockwiseFacingGroupWidth =
              clockwisePackaging.width * facingGroup.horizontal_facings;
            const clockwiseGhostFacingGroup = {
              x1: facingGroup.x,
              y1: facingGroup.y,
              x2: facingGroup.x + clockwiseFacingGroupWidth,
              y2: facingGroup.y + clockwiseFacingGroupHeight,
            };

            options.clockwiseFacingGroup = {
              ...facingGroup,
              id: generateUniqueId(),
              x: clockwiseGhostFacingGroup.x1,
              orientation_code: clockwiseOrientationCode,
              capped_orientation_code: clockwiseCappedOrientationCode,
              packaging: clockwisePackaging,
              cappedPackaging: clockwiseCappedPackaging,
            };

            const verticallyOrientationCode = rotateVerticallyOrientationFacing(
              facingGroup.orientation_code
            );
            const verticallyCappedOrientationCode =
              rotateCappedOrientationFacing(verticallyOrientationCode);
            const verticallyPackaging = {
              ...facingGroup.packaging,
              ...orientationFacing(
                verticallyOrientationCode,
                facingGroup.defaultPackaging.width,
                facingGroup.defaultPackaging.height,
                facingGroup.defaultPackaging.depth
              ),
            };
            const verticallyCappedPackaging = orientationFacing(
              verticallyCappedOrientationCode,
              facingGroup.defaultPackaging.width,
              facingGroup.defaultPackaging.height,
              facingGroup.defaultPackaging.depth
            );
            const verticallyDepthFacings = Math.trunc(
              aisleDepth / verticallyPackaging.depth
            );

            options.verticallyFacingGroup = {
              ...facingGroup,
              id: generateUniqueId(),
              orientation_code: verticallyOrientationCode,
              capped_orientation_code: verticallyCappedOrientationCode,
              packaging: verticallyPackaging,
              cappedPackaging: verticallyCappedPackaging,
              depth_facings: verticallyDepthFacings,
            };

            // Horizontally
            const horizontallyOrientationCode =
              rotateHorizontallyOrientationFacing(facingGroup.orientation_code);
            const horizontallyCappedOrientationCode =
              rotateCappedOrientationFacing(horizontallyOrientationCode);
            const horizontallyPackaging = {
              ...facingGroup.packaging,
              ...orientationFacing(
                horizontallyOrientationCode,
                facingGroup.defaultPackaging.width,
                facingGroup.defaultPackaging.height,
                facingGroup.defaultPackaging.depth
              ),
            };
            const horizontallyCappedPackaging = orientationFacing(
              horizontallyCappedOrientationCode,
              facingGroup.defaultPackaging.width,
              facingGroup.defaultPackaging.height,
              facingGroup.defaultPackaging.depth
            );
            const horizontallyDepthFacings = Math.trunc(
              aisleDepth / horizontallyPackaging.depth
            );

            options.horizontallyFacingGroup = {
              ...facingGroup,
              id: generateUniqueId(),
              orientation_code: horizontallyOrientationCode,
              capped_orientation_code: horizontallyCappedOrientationCode,
              packaging: horizontallyPackaging,
              cappedPackaging: horizontallyCappedPackaging,
              depth_facings: horizontallyDepthFacings,
            };
          }
        }
      }

      return options;
    }
  );

export const workbenchMinItemPositionSelector = createSelector(
  workbenchHistorySelector,
  facings => facings.length
    ? Math.min(...(facings?.map(item => item.y) || {}))
    : 0
);

export const workbenchUniqueSkusSelector = createSelector(
  workbenchHistorySelector,
  facings => Array.from(new Set(facings.map(item => item.product_num))).length
);
